import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import AchievementImage from "@/components/AchievementImage";
import { getTagColor } from "@/utils/tagColors";
import { buildSystemLookup, getSystemName } from "@/utils/systemNames";
import { getMinDateString, getTodayString, isValidUnlockDate } from "@/utils/dateUtils";
import {
  ArrowLeft,
  Plus,
  Trophy,
  Filter,
  Edit2,
  Trash2,
  Tag,
  Upload,
  Link as LinkIcon,
  ArrowUpDown,
  Package,
  Search,
  X,
  RefreshCw,
  Database,
  Loader2,
  Image,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  AlertTriangle,
  Download,
  Gamepad2,
  Calendar,
  StickyNote,
  ExternalLink,
  Star,
  Layers,
  Gem,
  Archive,
  CheckCircle2,
  Tags,
  MoreVertical,
  Settings,
  SortAsc,
  SortDesc,
  FolderPlus,
  Move,
  Target,
  Timer,
  Clock,
  Check,
  Square,
  CheckSquare,
  Share2,
  Users,
  LayoutGrid,
  List,
  Wrench,
  Lightbulb,
  ClipboardList,
  MapPin,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { CalendarWithYear } from "@/components/ui/calendar-with-year";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import PlatformBadge, { SystemBadge } from "@/components/PlatformBadge";
import TagBadge from "@/components/TagBadge";
import RarityBadge from "@/components/RarityBadge";
import GameHeaderNew from "@/components/GameDetail/GameHeaderNew";
import GameContentSection from "@/components/GameDetail/GameContentSection";
import { MapDisplay, MapEditor } from "@/components/GameDetail/MapNote";
import { CharacterDisplay, CharacterEditor } from "@/components/GameDetail/CharacterNote";
import ImageUpload from "@/components/ImageUpload";
import SystemBarOverlay from "@/components/SystemBarOverlay";
import { SubmitFactDialog, getCategoryInfo, FACT_CATEGORIES } from "@/components/AchievementFacts";
import { FormattedText } from "@/components/ui/formatted-text";
import PlannerPanel from "@/components/GameDetail/PlannerPanel";
import AchievementCelebration from "@/components/AchievementCelebration";
import ProgressiveList from "@/components/ProgressiveList";
import PaginatedList from "@/components/PaginatedList";
import PackedGroupedList from "@/components/PackedGroupedList";
import { format } from "date-fns";
import { 
  AchievementCard, 
  GameHeader, 
  TROPHY_TYPES, 
  formatTime12Hour,
  EditAchievementDialog,
  TimestampDialog,
  GameInfoDialog,
  AddNoteDialog,
  MilestoneBadge,
  RefreshDialog,
  ShareToCommunityDialog,
  BrowseCommunityDialog,
  ManualSyncDialog,
  ScoreMismatchDialog,
  XboxTitleSearchDialog,
  PsnTitleSearchDialog,
  ExophaseVerifyDialog,
  CoverArtDialog,
  SectionDialog,
  ChallengeDialog,
  BulkMoveDialog,
  AddDlcDialog,
  EditDlcDialog,
  ChecklistDisplay,
  ShareChecklistDialog,
  SubgroupManagerDialog,
  OrganizerSubgroupSorterDialog,
  getSubgroupIconComp,
} from "@/components/GameDetail";
import { ChatlogImportDialog } from "@/components/GameDetail/ChatlogImportDialog";

const API = `${getApiUrl()}/api`;

// Auto-format date input: adds "/" between MM/DD/YYYY as user types
const formatDateInput = (value) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Build formatted string with slashes
  let formatted = '';
  if (digits.length > 0) {
    formatted = digits.slice(0, 2); // MM
  }
  if (digits.length > 2) {
    formatted += '/' + digits.slice(2, 4); // DD
  }
  if (digits.length > 4) {
    formatted += '/' + digits.slice(4, 8); // YYYY
  }
  
  return formatted;
};

// Auto-format time input: adds ":" between HH:MM as user types
// More intelligently handles various input formats
const formatTimeInput = (value) => {
  // Remove all non-digits and non-colons first
  let cleaned = value.replace(/[^\d:]/g, '');
  
  // If there's already a colon, use it as a separator
  if (cleaned.includes(':')) {
    const parts = cleaned.split(':');
    const hours = parts[0].slice(0, 2);
    const minutes = (parts[1] || '').slice(0, 2);
    if (minutes) {
      return hours + ':' + minutes;
    }
    return hours;
  }
  
  // No colon - extract digits only
  const digits = cleaned.replace(/\D/g, '');
  
  // Handle different digit lengths intelligently
  if (digits.length === 0) return '';
  
  // 1-2 digits: just hours (e.g., "2" -> "2", "14" -> "14")
  if (digits.length <= 2) {
    return digits;
  }
  
  // 3 digits: could be H:MM (e.g., "230" -> "2:30") or HH:M (e.g., "143" -> "14:3")
  // Prefer H:MM format for single-digit hours
  if (digits.length === 3) {
    const firstDigit = parseInt(digits[0]);
    // If first digit is 0-9 and second digit makes valid minutes when combined with third
    // E.g., "230" -> "2:30", "930" -> "9:30"
    if (firstDigit <= 9) {
      return digits[0] + ':' + digits.slice(1, 3);
    }
  }
  
  // 4+ digits: HH:MM format
  return digits.slice(0, 2) + ':' + digits.slice(2, 4);
};

// Natural sort comparison - handles embedded numbers so "Level 10" comes after "Level 9"
// .trim() handles inconsistent leading/trailing whitespace in achievement names
const naturalCompare = (a, b) => {
  return (a || "").trim().localeCompare((b || "").trim(), undefined, { numeric: true, sensitivity: "base" });
};

export default function GameDetail() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [achievements, setAchievements] = useState([]);
  // DLC pagination: `groupPageMap` receives {groupId -> pageNumber} from
  // PackedGroupedList; `jumpToGroupId` requests a jump when the user clicks
  // a DLC in the Base Game & DLC nav list.
  const [groupPageMap, setGroupPageMap] = useState({});
  const [jumpToGroupId, setJumpToGroupId] = useState(null);
  // Organizer pagination — one organizer per page. `activeOrganizerId` is
  // the id of the organizer currently shown; null means "first available".
  const [activeOrganizerId, setActiveOrganizerId] = useState(null);
  const [groupedAchievements, setGroupedAchievements] = useState(null); // New: grouped by DLC
  const [tags, setTags] = useState([]);
  const [dlcs, setDlcs] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [systems, setSystems] = useState({}); // Systems grouped by platform
  const [loading, setLoading] = useState(true);
  
  // Build system name lookup from systems data
  const systemsLookup = useMemo(() => buildSystemLookup(systems), [systems]);
  
  const [statusFilter, setStatusFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [characterFilter, setCharacterFilter] = useState("all");
  const [rarityFilter, setRarityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name"); // Will be updated from preferences
  // User preferences relevant to per-achievement rendering. Defaults match
  // the backend's defaults (everything hidden) so rendering is safe before
  // the prefs fetch finishes.
  const [userPrefs, setUserPrefs] = useState({
    hideSecretAchievements: true,
    hideSecretImage: true,
    hideSecretName: true,
  });
  const [sortOrder, setSortOrder] = useState("asc"); // Will be updated from preferences
  const [achievementViewMode, setAchievementViewMode] = useState("grid"); // grid or list
  const [defaultSortLoaded, setDefaultSortLoaded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [orphanDialogOpen, setOrphanDialogOpen] = useState(false);
  const [orphanInfo, setOrphanInfo] = useState(null);
  const [orphanRelinking, setOrphanRelinking] = useState(false);
  // Smart repair (option C): when a DLC record exists but has user_id=null
  // and its parentGameId points to a game the current user already owns,
  // we surface a one-click "Claim DLC under <parent>" repair with an escape
  // hatch to open the full Orphan Relink dialog.
  const [repairChoice, setRepairChoice] = useState(null); // { game, suggested_parent, is_dlc }
  const [repairBusy, setRepairBusy] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [timestampDialogOpen, setTimestampDialogOpen] = useState(false);
  const [dlcDialogOpen, setDlcDialogOpen] = useState(false);
  const [gameInfoDialogOpen, setGameInfoDialogOpen] = useState(false);
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  const [editTagsDialogOpen, setEditTagsDialogOpen] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [imageMode, setImageMode] = useState("url");
  const [selectedDlcForAdd, setSelectedDlcForAdd] = useState(null); // DLC to add achievement to
  const [genreInput, setGenreInput] = useState("");
  const [newDlc, setNewDlc] = useState({ name: "", boxArtUrl: "", releaseDate: "" });
  const [editingDlcId, setEditingDlcId] = useState(null);  // For editing DLC release date
  const [editingDlc, setEditingDlc] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ base: true }); // Track expanded/collapsed sections
  const [expandedContentGroups, setExpandedContentGroups] = useState({ baseGame: true, dlcs: true, challenges: true, planner: true }); // Track collapsed content panel groups
  const [fetchingIgdbDlcs, setFetchingIgdbDlcs] = useState(false);
  
  // Master genres for display
  const [masterGenres, setMasterGenres] = useState([]);
  
  // Edit tags state
  const [editPlatforms, setEditPlatforms] = useState([]);
  const [editSystems, setEditSystems] = useState([]);
  
  // Refresh from database states
  const [refreshSearch, setRefreshSearch] = useState("");
  const [refreshResults, setRefreshResults] = useState([]);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [selectedRefreshGame, setSelectedRefreshGame] = useState(null);
  const [refreshSource, setRefreshSource] = useState("master"); // "master" or "igdb"
  const [refreshFields, setRefreshFields] = useState(["boxArtUrl", "releaseDate", "publisher", "developer", "genres", "completionEstimate", "achievements"]);
  const [masterDbSearched, setMasterDbSearched] = useState(false); // Track if we've searched Master DB
  
  // Cover Art Dialog state (IGDB + SteamGridDB)
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const [coverSearchQuery, setCoverSearchQuery] = useState("");
  const [coverResults, setCoverResults] = useState({ igdb: [], steamgriddb: [], combined: [] });
  const [coverLoading, setCoverLoading] = useState(false);
  const [selectedCover, setSelectedCover] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverSourceFilter, setCoverSourceFilter] = useState("all");
  
  // Sync achievements state
  const [syncing, setSyncing] = useState(false);
  const [xboxOAuthStatus, setXboxOAuthStatus] = useState({ authenticated: false });
  const [togglingPriority, setTogglingPriority] = useState(false);
  const [deduplicating, setDeduplicating] = useState(false);
  
  // Xbox title search/link state (for when auto-sync fails)
  const [xboxTitleSearchOpen, setXboxTitleSearchOpen] = useState(false);
  const [xboxTitleSearch, setXboxTitleSearch] = useState("");
  const [xboxTitleResults, setXboxTitleResults] = useState([]);
  const [xboxTitleSearching, setXboxTitleSearching] = useState(false);
  const [xboxTitleLinking, setXboxTitleLinking] = useState(false);
  
  // PlayStation title search/link state (for when game lacks npCommunicationId)
  const [psnTitleSearchOpen, setPsnTitleSearchOpen] = useState(false);
  const [psnTitleSearch, setPsnTitleSearch] = useState("");
  const [psnTitleResults, setPsnTitleResults] = useState([]);
  const [psnTitleSearching, setPsnTitleSearching] = useState(false);
  const [psnTitleLinking, setPsnTitleLinking] = useState(false);
  
  // Score mismatch dialog state (for incomplete Xbox 360 syncs)
  const [scoreMismatchDialogOpen, setScoreMismatchDialogOpen] = useState(false);
  const [scoreMismatchData, setScoreMismatchData] = useState(null);
  const [manualUnlockSelections, setManualUnlockSelections] = useState({});
  const [savingManualUnlocks, setSavingManualUnlocks] = useState(false);
  
  // Manual bulk sync dialog state
  const [manualSyncDialogOpen, setManualSyncDialogOpen] = useState(false);
  const [exophaseVerifyOpen, setExophaseVerifyOpen] = useState(false);
  const [manualSyncAchievements, setManualSyncAchievements] = useState([]);
  const [manualSyncSelections, setManualSyncSelections] = useState({});
  const [savingManualSync, setSavingManualSync] = useState(false);
  const [syncOptionsOpen, setSyncOptionsOpen] = useState(false);
  
  // Notes state
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  
  // Checklist state
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  const [shareChecklistDialogOpen, setShareChecklistDialogOpen] = useState(false);
  const [checklistToShare, setChecklistToShare] = useState(null);
  
  // Game tags state
  const [availableGameTags, setAvailableGameTags] = useState([]);
  const [gameTagsPopoverOpen, setGameTagsPopoverOpen] = useState(false);
  
  // Session time preference for completion estimates
  const [sessionTimeHours, setSessionTimeHours] = useState(2);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Sections state (custom base game sections)
  const [sections, setSections] = useState([]);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionDescription, setNewSectionDescription] = useState("");
  const [editingSectionId, setEditingSectionId] = useState(null);

  // Subgroup manager state — nested subgroups inside an Organizer
  const [subgroupManagerOpen, setSubgroupManagerOpen] = useState(false);
  const [subgroupManagerSection, setSubgroupManagerSection] = useState(null);
  // Subgroup sorter (3-panel drag-drop workspace)
  const [subgroupSorterOpen, setSubgroupSorterOpen] = useState(false);
  const [subgroupSorterSection, setSubgroupSorterSection] = useState(null);

  // Homecoming chatlog importer (only used when game.chatlogImportEnabled)
  const [chatlogImportOpen, setChatlogImportOpen] = useState(false);

  // Section-level map editor state
  const [sectionMapEditorOpen, setSectionMapEditorOpen] = useState(false);
  const [editingSectionMap, setEditingSectionMap] = useState(null);
  const [characterEditorOpen, setCharacterEditorOpen] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [activeSectionIdForMap, setActiveSectionIdForMap] = useState(null);
  
  // Organizer view toggle - when false, shows original Base Game/DLC grouping
  const [showOrganizerView, setShowOrganizerView] = useState(true);
  
  // Challenges state
  const [challenges, setChallenges] = useState([]);
  const [challengeDialogOpen, setChallengeDialogOpen] = useState(false);
  const [newChallenge, setNewChallenge] = useState({
    name: "",
    description: "",
    imageUrl: "",
    timerSeconds: null,
    dueDate: null,
    prerequisiteAchievementIds: []
  });
  const [editingChallenge, setEditingChallenge] = useState(null);
  
  // Share to Community state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareSubmitting, setShareSubmitting] = useState(false);
  
  // Browse Community Content state
  const [communityDialogOpen, setCommunityDialogOpen] = useState(false);
  const [communityOrganizers, setCommunityOrganizers] = useState([]);
  const [communityChallenges, setCommunityChallenges] = useState([]);
  const [communityGamePlanners, setCommunityGamePlanners] = useState([]);
  const [communityChecklists, setCommunityChecklists] = useState([]);
  const [communityGrids, setCommunityGrids] = useState([]);
  const [communityGridTemplates, setCommunityGridTemplates] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [adoptingId, setAdoptingId] = useState(null);
  const [communityMaps, setCommunityMaps] = useState([]);

  // Map submit-to-community state
  const [submitMapDialogOpen, setSubmitMapDialogOpen] = useState(false);
  const [mapToSubmit, setMapToSubmit] = useState(null);
  const [submitMapTags, setSubmitMapTags] = useState([]);
  const [submitMapDescription, setSubmitMapDescription] = useState("");
  const [submitMapShowSubmitter, setSubmitMapShowSubmitter] = useState(true);
  const [submittingMap, setSubmittingMap] = useState(false);
  
  // Achievement Facts state
  const [factsByAchievement, setFactsByAchievement] = useState({});
  const [showAchievementFacts, setShowAchievementFacts] = useState(true);
  
  // Achievement note counts
  const [achievementNoteCounts, setAchievementNoteCounts] = useState({});
  const [plannerRefreshKey, setPlannerRefreshKey] = useState(0);
  const [submitFactDialogOpen, setSubmitFactDialogOpen] = useState(false);
  const [submitFactAchievement, setSubmitFactAchievement] = useState(null);
  const [submittingFact, setSubmittingFact] = useState(false);

  // Milestone map state
  const [milestoneMap, setMilestoneMap] = useState({});
  
  // Bulk move state
  const [bulkMoveMode, setBulkMoveMode] = useState(false);
  const [selectedAchievements, setSelectedAchievements] = useState([]);
  const [bulkMoveDialogOpen, setBulkMoveDialogOpen] = useState(false);
  
  // GameFAQs integration
  const [gameFaqsDialogOpen, setGameFaqsDialogOpen] = useState(false);
  const [gameFaqsUrl, setGameFaqsUrl] = useState("");
  const [gameFaqsHtml, setGameFaqsHtml] = useState("");
  const [gameFaqsLoading, setGameFaqsLoading] = useState(false);
  const [gameFaqsResults, setGameFaqsResults] = useState(null);
  const [gameFaqsTab, setGameFaqsTab] = useState("url"); // "url" or "html"
  const [celebrationAchievements, setCelebrationAchievements] = useState([]);
  
  const [newAchievement, setNewAchievement] = useState({
    name: "",
    description: "",
    imageUrl: "",
    score: 0,
    tags: [],
    trophyType: "",
    earnedTime: "",
  });

  // Helper to filter achievements by rarity
  const filterByRarity = (achievements) => {
    if (rarityFilter === "all") return achievements;
    return achievements.filter(ach => {
      if (ach.rarity == null) return false;
      switch (rarityFilter) {
        case "ultra-rare": return ach.rarity <= 5;
        case "rare": return ach.rarity > 5 && ach.rarity <= 20;
        case "uncommon": return ach.rarity > 20 && ach.rarity <= 50;
        case "common": return ach.rarity > 50;
        default: return true;
      }
    });
  };

  const fetchDefaultSort = async () => {
    try {
      const response = await axios.get(`${API}/settings/preferences`);
      const defaultSort = response.data.defaultAchievementSort || "name_asc";
      // Persist secret-achievement prefs locally so AchievementCard can
      // decide what to hide. Defaults match the backend's defaults.
      setUserPrefs({
        hideSecretAchievements: response.data.hideSecretAchievements !== false,
        hideSecretImage: response.data.hideSecretImage !== false,
        hideSecretName: response.data.hideSecretName !== false,
      });
      
      // Parse the sort value (e.g., "name_asc" -> sortBy: "name", sortOrder: "asc")
      const sortMap = {
        "name_asc": { sortBy: "name", sortOrder: "asc" },
        "name_desc": { sortBy: "name", sortOrder: "desc" },
        "score_asc": { sortBy: "score", sortOrder: "asc" },
        "score_desc": { sortBy: "score", sortOrder: "desc" },
        "unlocked_first": { sortBy: "isUnlocked", sortOrder: "desc" },
        "locked_first": { sortBy: "isUnlocked", sortOrder: "asc" },
        "date_asc": { sortBy: "earnedAt", sortOrder: "asc" },
        "date_desc": { sortBy: "earnedAt", sortOrder: "desc" },
        "rarity_asc": { sortBy: "rarity", sortOrder: "asc" },
        "rarity_desc": { sortBy: "rarity", sortOrder: "desc" },
      };
      
      const sortConfig = sortMap[defaultSort] || { sortBy: "name", sortOrder: "asc" };
      setSortBy(sortConfig.sortBy);
      setSortOrder(sortConfig.sortOrder);
      
      // Also get session time preference
      if (response.data.sessionTimeHours) {
        setSessionTimeHours(response.data.sessionTimeHours);
      }
      
      // Get achievement view mode preference
      if (response.data.achievementViewMode) {
        setAchievementViewMode(response.data.achievementViewMode);
      }
    } catch (error) {
      console.error("Failed to fetch default sort preference:", error);
    } finally {
      setDefaultSortLoaded(true);
    }
  };

  const fetchPlatforms = async () => {
    try {
      const [platformsRes, systemsRes] = await Promise.all([
        axios.get(`${API}/platforms`),
        axios.get(`${API}/systems`)
      ]);
      setPlatforms(platformsRes.data);
      setSystems(systemsRes.data);
    } catch (error) {
      console.error("Failed to fetch platforms/systems:", error);
    }
  };

  const fetchXboxOAuthStatus = async () => {
    try {
      const response = await axios.get(`${API}/xbox/oauth/status`);
      setXboxOAuthStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch Xbox OAuth status:", error);
    }
  };

  // Guards for concurrent refetches. `fetchGenerationRef` is bumped on every
  // `fetchData()` call; only the response matching the latest generation is
  // allowed to write to state. `lastPartialToastRef` throttles the partial-
  // failure toast so a burst of moves doesn't spam the user.
  const fetchGenerationRef = useRef(0);
  const lastPartialToastRef = useRef(0);

  const fetchData = useCallback(async () => {
    // Concurrency guard: increment a generation number on every call.
    // When multiple fetches are in-flight (e.g. rapid bulk-moves), only the
    // most recent one is allowed to write to state, preventing a slow old
    // response from clobbering fresh data.
    const gen = ++fetchGenerationRef.current;
    const isStale = () => gen !== fetchGenerationRef.current;
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/games/${gameId}`),
        axios.get(`${API}/games/${gameId}/achievements-grouped?${statusFilter !== "all" ? `status=${statusFilter}&` : ""}${tagFilter !== "all" ? `tag=${tagFilter}&` : ""}${characterFilter !== "all" ? `character=${encodeURIComponent(characterFilter)}&` : ""}sortBy=${sortBy}&sortOrder=${sortOrder}`),
        axios.get(`${API}/tags`),
        axios.get(`${API}/games/${gameId}/dlcs`),
        axios.get(`${API}/game-tags`),
        axios.get(`${API}/games/${gameId}/sections`),
        axios.get(`${API}/games/${gameId}/challenges`),
      ]);

      if (isStale()) return;

      // Game data is critical — if it fails, we can't render the page
      if (results[0].status === "rejected") {
        console.error("Failed to fetch game:", results[0].reason);
        toast.error("Failed to load game data");
        return;
      }

      // For every non-critical request: on rejection, KEEP the previous state
      // instead of overwriting it with empty arrays. This is the root cause of
      // "DLCs and Organizers suddenly disappear" — a slow/failed refetch was
      // wiping the working list when the user was rapid-firing bulk moves.
      const gameRes = results[0].value;
      const groupedRejected = results[1].status !== "fulfilled";
      const dlcsRejected = results[3].status !== "fulfilled";
      const sectionsRejected = results[5].status !== "fulfilled";
      const tagsRes = results[2].status === "fulfilled" ? results[2].value : null;
      const gameTagsRes = results[4].status === "fulfilled" ? results[4].value : null;
      const challengesRes = results[6].status === "fulfilled" ? results[6].value : null;

      // Announce partial failures so the user knows to refresh manually if
      // something looks off (silently swallowing errors was the other half of
      // the bug).
      if (groupedRejected || dlcsRejected || sectionsRejected) {
        console.warn("Partial fetch failure — keeping previous state for:", {
          grouped: groupedRejected, dlcs: dlcsRejected, sections: sectionsRejected,
        });
        // Throttle to one toast every 4 seconds so a burst of moves during a
        // slow API doesn't drown the screen in errors.
        const now = Date.now();
        if (now - lastPartialToastRef.current > 4000) {
          lastPartialToastRef.current = now;
          toast.error("Some data failed to refresh — keeping current view. Retry the move or reload if it looks wrong.");
        }
      }

      setGame(gameRes.data);
      if (!groupedRejected) {
        const groupedRes = results[1].value;
        setGroupedAchievements(groupedRes.data);
        const allAchievements = [
          ...(groupedRes.data?.baseGame?.achievements || []),
          ...(groupedRes.data?.baseGame?.sections || []).flatMap(s => s.achievements || []),
          ...(groupedRes.data?.dlcs || []).flatMap(d => d.achievements || [])
        ];
        setAchievements(allAchievements);
      }
      if (tagsRes) setTags(tagsRes.data || []);
      if (!dlcsRejected) setDlcs(results[3].value.data || []);
      if (gameTagsRes) setAvailableGameTags(gameTagsRes.data || []);
      if (!sectionsRejected) setSections(results[5].value.data || []);
      if (challengesRes) setChallenges(challengesRes.data || []);
      
      // Initialize expanded sections for new DLCs and sections
      // Initialize expanded sections only if we successfully got new grouped
      // data. On failure we keep whatever the user already had expanded.
      if (!groupedRejected) {
        const groupedRes = results[1].value;
        setExpandedSections(prev => {
          const newExpanded = { base: true };
          // Expand DLCs
          (groupedRes.data.dlcs || []).forEach(dlc => {
            if (prev[dlc.id] === undefined) {
              newExpanded[dlc.id] = true;
            } else {
              newExpanded[dlc.id] = prev[dlc.id];
            }
          });
          // Expand sections
          (groupedRes.data.baseGame?.sections || []).forEach(section => {
            if (prev[`section-${section.id}`] === undefined) {
              newExpanded[`section-${section.id}`] = true;
            } else {
              newExpanded[`section-${section.id}`] = prev[`section-${section.id}`];
            }
          });
          // Challenges section
          if (prev.challenges === undefined) {
            newExpanded.challenges = true;
          } else {
            newExpanded.challenges = prev.challenges;
          }
          return { ...prev, ...newExpanded };
        });
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      // Don't wipe existing state on network errors — only warn.
      toast.error("Refresh failed. Your current view is intact.");
    } finally {
      // Only flip loading off if we're the freshest fetch.
      if (gen === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [gameId, statusFilter, tagFilter, characterFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchPlatforms();
    fetchXboxOAuthStatus();
    fetchDefaultSort();
    fetchMasterGenres();
    fetchChecklistTemplates();
  }, []);

  // Scroll to top when entering the page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [gameId]);

  const fetchChecklistTemplates = async () => {
    try {
      const response = await axios.get(`${API}/checklist-templates`);
      setChecklistTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch checklist templates:", error);
    }
  };

  const fetchMasterGenres = async () => {
    try {
      const response = await axios.get(`${API}/master/genres`);
      setMasterGenres(response.data);
    } catch (error) {
      console.error("Failed to fetch genres:", error);
    }
  };

  useEffect(() => {
    // Only fetch data after default sort is loaded or if user manually changes sort
    if (defaultSortLoaded) {
      fetchData();
    }
  }, [defaultSortLoaded, fetchData]);

  // Fetch achievement facts when game has a masterGameId
  useEffect(() => {
    const fetchFacts = async () => {
      if (!game?.masterGameId) {
        setFactsByAchievement({});
        return;
      }
      try {
        const response = await axios.get(`${API}/achievement-facts/game/${game.masterGameId}`);
        if (!response.data.disabled) {
          setFactsByAchievement(response.data.factsByAchievement || {});
        }
        setShowAchievementFacts(!response.data.disabled);
      } catch (error) {
        console.error("Failed to fetch achievement facts:", error);
      }
    };
    fetchFacts();
  }, [game?.masterGameId]);

  // Fetch milestone map for this game
  useEffect(() => {
    const fetchMilestoneMap = async () => {
      if (!gameId) return;
      try {
        const response = await axios.get(`${API}/stats/milestone-map?game_id=${gameId}`);
        setMilestoneMap(response.data.milestoneMap || {});
      } catch (error) {
        // Non-critical: silently fail
      }
    };
    fetchMilestoneMap();
  }, [gameId]);

  // Fetch achievement note counts
  useEffect(() => {
    const fetchNoteCounts = async () => {
      if (!gameId) return;
      try {
        const resp = await axios.get(`${API}/achievements/note-counts/${gameId}`);
        setAchievementNoteCounts(resp.data || {});
      } catch {
        // silent
      }
    };
    fetchNoteCounts();
  }, [gameId]);

  // Handle submit fact
  const handleSubmitFact = async (factData) => {
    if (!submitFactAchievement) return;
    
    setSubmittingFact(true);
    try {
      await axios.post(`${API}/achievement-facts/submit/${submitFactAchievement.id}`, factData);
      toast.success("Fact submitted for review! You'll be notified when it's approved.");
      setSubmitFactDialogOpen(false);
      setSubmitFactAchievement(null);
    } catch (error) {
      console.error("Failed to submit fact:", error);
      toast.error(error.response?.data?.detail || "Failed to submit fact");
    } finally {
      setSubmittingFact(false);
    }
  };

  // Handle facts click on achievement card
  const handleFactsClick = (achievement) => {
    // Get the master achievement ID if linked, otherwise use local achievement
    const masterAchId = achievement.masterAchievementId || achievement.id;
    const existingFacts = factsByAchievement[masterAchId] || [];
    
    if (existingFacts.length === 0) {
      // No facts - open submit dialog
      setSubmitFactAchievement({ ...achievement, id: masterAchId });
      setSubmitFactDialogOpen(true);
    }
    // If facts exist, the AchievementCard handles expansion internally
  };

  const getPlatformColor = (platformId) => {
    const defaultColors = {
      xbox: "#107C10",
      playstation: "#003087",
      steam: "#66C0F4",
      epic: "#FFFFFF",
    };
    if (defaultColors[platformId]) return defaultColors[platformId];
    const platform = platforms.find(p => p.id === platformId);
    return platform?.color || "#A1A1AA";
  };

  const getScoreAbbr = (platformId) => {
    const defaultAbbrs = {
      xbox: "GS",
      playstation: "XP",
      steam: "XP",
      epic: "XP",
    };
    if (defaultAbbrs[platformId]) return defaultAbbrs[platformId];
    const platform = platforms.find(p => p.id === platformId);
    return platform?.scoreAbbr || "XP";
  };

  // Sync achievements from platform API
  const handleSyncAchievements = async () => {
    if (!game) return;
    
    const platform = (game.platform || "").toLowerCase();
    let endpoint = null;
    let successMessage = "";
    
    if (platform === "xbox") {
      // Prefer OAuth endpoint if authenticated, fall back to OpenXBL
      if (xboxOAuthStatus.authenticated) {
        endpoint = `${API}/xbox/oauth/game/${gameId}/sync-achievements`;
        successMessage = "Xbox achievements synced via Xbox Live!";
      } else {
        endpoint = `${API}/xbox/game/${gameId}/sync-achievements`;
        successMessage = "Xbox achievements synced!";
      }
    } else if (platform === "steam") {
      endpoint = `${API}/steam/game/${gameId}/sync-achievements`;
      successMessage = "Steam achievements synced!";
    } else if (platform === "playstation") {
      endpoint = `${API}/playstation/game/${gameId}/sync-trophies`;
      successMessage = "PlayStation trophies synced!";
    } else {
      toast.info("Sync is only available for Xbox, Steam, and PlayStation games");
      return;
    }
    
    setSyncing(true);
    try {
      // Capture current unlocked IDs before sync to detect new unlocks
      // Include DLC game IDs so DLC achievements are also tracked for celebration
      let preUnlockedIds = new Set();
      const allGameIdsForCelebration = [gameId, ...dlcs.map(d => d.id)];
      try {
        const prePromises = allGameIdsForCelebration.map(gid =>
          axios.get(`${API}/achievements?gameId=${gid}&status=unlocked&limit=9999`).catch(() => ({ data: [] }))
        );
        const preResults = await Promise.all(prePromises);
        preResults.forEach(res => (res.data || []).forEach(a => preUnlockedIds.add(a.id)));
      } catch (_) { /* best effort */ }

      const response = await axios.post(endpoint);
      const { added, updated, message, isXbox360Limitation, scoreMismatch, xboxReportedEarned, scoreDifference, countDifference, missingAchievements, earnedScore, syncDisabled, platformLabel, scoreUnit } = response.data;
      
      // Handle syncDisabled response (legacy games with no API data)
      if (syncDisabled) {
        toast.warning(message || "Sync has been disabled for this game.", { duration: 8000 });
        fetchData(); // Refresh to update game state with syncDisabled flag
        return;
      }
      
      if (added > 0 || updated > 0) {
        if (isXbox360Limitation) {
          toast.info(message, { duration: 5000 });
        } else {
          toast.success(message || successMessage);
        }
        
        // Auto-deduplicate after sync to clean up any duplicates
        try {
          const dedupeResponse = await axios.post(`${API}/games/${gameId}/deduplicate-achievements?include_dlcs=true`);
          if (dedupeResponse.data.achievementsMerged > 0) {
            toast.info(`Auto-cleaned ${dedupeResponse.data.achievementsMerged} duplicate(s)`);
          }
        } catch (dedupeError) {
          console.error("Auto-deduplicate failed:", dedupeError);
        }
        
        // Show celebration for newly unlocked achievements (including DLC)
        if (added > 0 || updated > 0) {
          try {
            const postPromises = allGameIdsForCelebration.map(gid =>
              axios.get(`${API}/achievements?gameId=${gid}&sortBy=earnedAt&sortOrder=desc&status=unlocked&limit=9999`).catch(() => ({ data: [] }))
            );
            const postResults = await Promise.all(postPromises);
            const allUnlocked = postResults.flatMap(res => res.data || []);
            // Filter to only achievements that weren't unlocked before the sync
            const newlyUnlocked = allUnlocked.filter(a => !preUnlockedIds.has(a.id));
            if (newlyUnlocked.length > 0) {
              setCelebrationAchievements(newlyUnlocked);
            }
          } catch (celebErr) {
            console.error("Could not fetch new achievements for celebration:", celebErr);
          }
        }
        
        fetchData(); // Refresh the achievements list
      } else {
        toast.info("No new achievements found. Everything is up to date!");
      }
      
      // Check for score mismatch and show dialog
      if (scoreMismatch && missingAchievements?.length > 0) {
        setScoreMismatchData({
          xboxReportedEarned,
          earnedScore,
          scoreDifference,
          countDifference: countDifference || 0,
          missingAchievements,
          platformLabel: platformLabel || "Xbox",
          scoreUnit: scoreUnit || "GS",
        });
        setManualUnlockSelections({});
        setScoreMismatchDialogOpen(true);
      }
    } catch (error) {
      console.error("Sync failed:", error);
      const errorMsg = error.response?.data?.detail || "Failed to sync achievements";
      
      // Check if it's a "title not found" error for Xbox games
      if (game?.platform === "xbox" && errorMsg.includes("Could not find Xbox Title ID")) {
        toast.error(
          <div>
            <p>{errorMsg}</p>
            <button 
              onClick={() => {
                setXboxTitleSearch(game.name);
                setXboxTitleSearchOpen(true);
                handleSearchXboxTitles(game.name);
              }}
              className="mt-2 text-sm underline text-blue-400 hover:text-blue-300"
            >
              Search and link manually →
            </button>
          </div>,
          { duration: 8000 }
        );
      } else if (game?.platform === "playstation" && (
        errorMsg.includes("Communication ID") || errorMsg.includes("npCommunicationId") ||
        errorMsg.includes("Search and Link") || errorMsg.includes("not found") ||
        errorMsg.includes("Bad Request")
      )) {
        // PlayStation sync error - offer to search and link manually
        toast.error(
          <div>
            <p>{errorMsg}</p>
            <button 
              onClick={() => {
                setPsnTitleSearch(game.name);
                setPsnTitleSearchOpen(true);
                handleSearchPsnTitles(game.name);
              }}
              className="mt-2 text-sm underline text-blue-400 hover:text-blue-300"
            >
              Search and link manually
            </button>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSyncing(false);
    }
  };
  
  // Search Xbox titles from user's profile
  const handleSearchXboxTitles = async (query = "") => {
    setXboxTitleSearching(true);
    try {
      const response = await axios.get(`${API}/xbox/oauth/search-titles`, {
        params: { query: query || xboxTitleSearch, limit: 50 }
      });
      setXboxTitleResults(response.data.results || []);
      if (response.data.results?.length === 0) {
        toast.info("No matching titles found in your Xbox profile");
      }
    } catch (error) {
      console.error("Xbox title search failed:", error);
      toast.error(error.response?.data?.detail || "Failed to search Xbox titles");
    } finally {
      setXboxTitleSearching(false);
    }
  };
  
  // Link a game to an Xbox title and sync
  const handleLinkXboxTitle = async (titleId) => {
    setXboxTitleLinking(true);
    try {
      const response = await axios.post(`${API}/xbox/oauth/game/${gameId}/link-title?title_id=${titleId}`);
      const { added, updated, message } = response.data;
      
      toast.success(message || `Linked and synced! Added ${added}, updated ${updated} achievements`);
      setXboxTitleSearchOpen(false);
      setXboxTitleResults([]);
      
      // Auto-deduplicate after linking
      try {
        const dedupeResponse = await axios.post(`${API}/games/${gameId}/deduplicate-achievements?include_dlcs=true`);
        if (dedupeResponse.data.achievementsMerged > 0) {
          toast.info(`Auto-cleaned ${dedupeResponse.data.achievementsMerged} duplicate(s)`);
        }
      } catch (dedupeError) {
        console.error("Auto-deduplicate failed:", dedupeError);
      }
      
      fetchData();
    } catch (error) {
      console.error("Failed to link Xbox title:", error);
      toast.error(error.response?.data?.detail || "Failed to link and sync");
    } finally {
      setXboxTitleLinking(false);
    }
  };

  // Search PlayStation titles from user's profile
  const handleSearchPsnTitles = async (query = "") => {
    setPsnTitleSearching(true);
    try {
      const response = await axios.post(`${API}/playstation/search-games`, {
        query: query || psnTitleSearch
      });
      setPsnTitleResults(response.data || []);
      if (response.data?.length === 0) {
        toast.info("No matching titles found in your PlayStation profile");
      }
    } catch (error) {
      console.error("PlayStation title search failed:", error);
      toast.error(error.response?.data?.detail || "Failed to search PlayStation titles");
    } finally {
      setPsnTitleSearching(false);
    }
  };
  
  // Link a game to a PlayStation title and sync
  const handleLinkPsnTitle = async (npCommunicationId) => {
    setPsnTitleLinking(true);
    try {
      const response = await axios.post(
        `${API}/playstation/game/${gameId}/link-and-sync?np_communication_id=${encodeURIComponent(npCommunicationId)}`
      );
      const { added, updated, message } = response.data;
      
      toast.success(message || `Linked and synced! Added ${added}, updated ${updated} trophies`);
      setPsnTitleSearchOpen(false);
      setPsnTitleResults([]);
      
      // Auto-deduplicate after linking
      try {
        const dedupeResponse = await axios.post(`${API}/games/${gameId}/deduplicate-achievements?include_dlcs=true`);
        if (dedupeResponse.data.achievementsMerged > 0) {
          toast.info(`Auto-cleaned ${dedupeResponse.data.achievementsMerged} duplicate(s)`);
        }
      } catch (dedupeError) {
        console.error("Auto-deduplicate failed:", dedupeError);
      }
      
      fetchData();
    } catch (error) {
      console.error("Failed to link PlayStation title:", error);
      toast.error(error.response?.data?.detail || "Failed to link and sync");
    } finally {
      setPsnTitleLinking(false);
    }
  };


  // Handle manual unlock from score mismatch dialog.
  //
  // Was: sequential N+1 PATCH /achievements/{id} loop that aborted on the
  // first failure, leaving partial state ("Failed to save unlock status"
  // with 1-2 items mysteriously saved). Now: uses the same resilient batch
  // endpoint that the regular Manual Bulk Entry uses, which:
  //   • Sends every update in a single request
  //   • Returns per-item failures in an `errors` array instead of aborting
  //   • Does NOT downgrade existing platform-verified achievements
  // The follow-up verify-by-score call upgrades them to secondary_verified
  // when their total matches the missing GS — same behaviour as before.
  const handleSaveManualUnlocks = async () => {
    const selectedAchievements = Object.entries(manualUnlockSelections)
      .filter(([_, data]) => data.selected)
      .map(([id, data]) => ({
        id,
        earnedAt: data.earnedAt,
        earnedTime: data.earnedTime || null,
        unlockedOffline: data.unlockedOffline || false,
      }));

    if (selectedAchievements.length === 0) {
      toast.info("No achievements selected to unlock");
      return;
    }

    setSavingManualUnlocks(true);
    try {
      // Build payload for the bulk endpoint
      const payload = selectedAchievements.map(({ id, earnedAt, earnedTime, unlockedOffline }) => ({
        achievementId: id,
        isUnlocked: true,
        earnedAt: earnedAt || new Date().toISOString(),
        earnedTime: earnedTime || null,
        unlockedOffline: unlockedOffline,
      }));

      const { data: bulkRes } = await axios.post(
        `${API}/games/${gameId}/bulk-update-achievements`,
        { achievements: payload },
      );
      const updated = bulkRes?.updated ?? 0;
      const bulkErrors = bulkRes?.errors ?? [];

      if (updated === 0 && bulkErrors.length > 0) {
        // Nothing saved → surface the first error so the user knows why
        const firstErr = bulkErrors[0]?.error || "Unknown error";
        toast.error(`Failed to save: ${firstErr}`);
        return;
      }

      // Mark the ones that DID save as secondary_verified via score match
      const savedIds = selectedAchievements
        .map((a) => a.id)
        .filter((id) => !bulkErrors.some((e) => e.id === id));
      if (savedIds.length > 0) {
        try {
          await axios.post(`${API}/achievements/verify-by-score`, {
            gameId: gameId,
            achievementIds: savedIds,
          });
        } catch (verifyErr) {
          console.warn("Score-match verification failed, achievements saved as unverified:", verifyErr);
        }
      }

      if (bulkErrors.length > 0) {
        toast.warning(`Saved ${updated} achievement(s) — ${bulkErrors.length} failed: ${bulkErrors[0].error}`);
      } else {
        toast.success(`Marked ${updated} achievement(s) as unlocked (verified via score match)`);
      }

      // Remove incomplete-sync tag if we've resolved the mismatch
      const currentTags = game?.gameTags || [];
      if (currentTags.includes("incomplete-sync")) {
        const newTags = currentTags.filter((t) => t !== "incomplete-sync");
        try {
          await axios.patch(`${API}/games/${gameId}`, { gameTags: newTags });
        } catch (tagErr) {
          console.warn("Failed to clear incomplete-sync tag:", tagErr);
        }
      }

      setScoreMismatchDialogOpen(false);
      setScoreMismatchData(null);
      setManualUnlockSelections({});
      fetchData();
    } catch (error) {
      console.error("Failed to save manual unlocks:", error);
      const detail = error.response?.data?.detail || error.message || "Failed to save unlock status";
      toast.error(detail);
    } finally {
      setSavingManualUnlocks(false);
    }
  };

  // Skip manual unlocks and close dialog (adds incomplete-sync tag)
  const handleSkipManualUnlocks = async () => {
    // Ensure incomplete-sync tag is present
    const currentTags = game?.gameTags || [];
    if (!currentTags.includes("incomplete-sync")) {
      try {
        await axios.patch(`${API}/games/${gameId}`, { 
          gameTags: [...currentTags, "incomplete-sync"] 
        });
        toast.info("Game marked with 'incomplete sync' tag. You can complete manual entry later.");
      } catch (error) {
        console.error("Failed to add incomplete-sync tag:", error);
      }
    } else {
      toast.info("Sync incomplete. You can complete manual entry later from this page.");
    }
    
    setScoreMismatchDialogOpen(false);
    setScoreMismatchData(null);
    setManualUnlockSelections({});
    fetchData();
  };

  // Open incomplete sync dialog manually (for resolving incomplete syncs)
  const handleResolveIncompleteSync = async () => {
    if (!game) return;
    
    try {
      // Fetch locked achievements for this game
      const achRes = await axios.get(`${API}/games/${gameId}/achievements-grouped`);
      const allAchievements = [
        ...(achRes.data.baseGame?.achievements || []),
        ...(achRes.data.baseGame?.sections || []).flatMap(s => s.achievements || []),
        ...(achRes.data.dlcs || []).flatMap(d => d.achievements || [])
      ];
      
      const lockedAchievements = allAchievements.filter(a => !a.isUnlocked);
      
      if (lockedAchievements.length === 0) {
        toast.info("No locked achievements to resolve. Game appears to be complete!");
        // Remove incomplete-sync tag since all achievements are unlocked
        const currentTags = game.gameTags || [];
        if (currentTags.includes("incomplete-sync")) {
          await axios.patch(`${API}/games/${gameId}`, { 
            gameTags: currentTags.filter(t => t !== "incomplete-sync"),
            isIncompleteSync: false
          });
          fetchData();
        }
        return;
      }
      
      // Set up the mismatch data and open dialog
      setScoreMismatchData({
        xboxReportedEarned: game.xboxCurrentGamerscore || game.xboxTotalGamerscore || 0,
        earnedScore: game.earnedScore || 0,
        scoreDifference: (game.xboxCurrentGamerscore || game.xboxTotalGamerscore || 0) - (game.earnedScore || 0),
        missingAchievements: lockedAchievements.map(a => ({
          id: a.id,
          name: a.name,
          description: a.description,
          imageUrl: a.imageUrl,
          score: a.score || 0,
        })),
      });
      setManualUnlockSelections({});
      setScoreMismatchDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch locked achievements:", error);
      toast.error("Failed to load achievements");
    }
  };

  // Check if game can be synced
  const canSync = () => {
    if (!game) return false;
    if (game.syncDisabled) return false;
    const platform = (game.platform || "").toLowerCase();
    // Always allow sync attempt for supported platforms - backend has fallback resolution
    return ["xbox", "playstation", "steam"].includes(platform);
  };

  const handleToggleSyncDisabled = async () => {
    if (!game) return;
    try {
      const response = await axios.put(`${API}/xbox/game/${gameId}/toggle-sync-disabled`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle sync status");
    }
  };

  // Open manual sync dialog - loads all achievements for bulk unlock
  const handleOpenManualSync = async () => {
    setSyncOptionsOpen(false);
    try {
      // Fetch all achievements for this game
      const achRes = await axios.get(`${API}/games/${gameId}/achievements-grouped`);
      const allAchievements = [
        ...(achRes.data.baseGame?.achievements || []).map(a => ({ ...a, dlcId: null, dlcName: null })),
        ...(achRes.data.baseGame?.sections || []).flatMap(s => (s.achievements || []).map(a => ({ ...a, dlcId: null, dlcName: null }))),
        ...(achRes.data.dlcs || []).flatMap(d => (d.achievements || []).map(a => ({ ...a, dlcId: d.id, dlcName: d.name }))),
        // Legacy Xbox Challenges — flagged so the dialog can group them under their own header
        ...(achRes.data.challenges?.achievements || []).map(a => ({ ...a, dlcId: null, dlcName: null, isChallenge: true })),
      ];
      
      // Show ALL achievements — including already-unlocked/verified ones — so
      // the user can revisit and adjust any unlock time from here.
      const filtered = allAchievements;
      
      // Helper: count how many fields are missing/empty for an achievement
      const missingFieldCount = (ach) => {
        let count = 0;
        const ea = ach.earnedAt || "";
        if (!ea || ea.startsWith("1753") || ea.startsWith("0001")) count++; // missing date
        if (!ach.earnedTime) count++; // missing time
        if (!ach.unlockedOffline) count++; // offline not marked
        if (!ach.isUnlocked) count = 3; // locked = all fields missing
        return count;
      };
      
      // Sort: most missing fields first, then unlocked before locked, then alphabetical
      const sorted = filtered.sort((a, b) => {
        const aMissing = missingFieldCount(a);
        const bMissing = missingFieldCount(b);
        if (aMissing !== bMissing) return bMissing - aMissing; // more missing = higher
        if (a.isUnlocked && !b.isUnlocked) return -1;
        if (!a.isUnlocked && b.isUnlocked) return 1;
        return naturalCompare(a.name, b.name);
      });
      
      setManualSyncAchievements(sorted);
      
      // Pre-select already unlocked achievements (with their existing data, no auto-fill)
      const preSelected = {};
      sorted.forEach(ach => {
        if (ach.isUnlocked) {
          // Prefer the stored unlock/earned date so existing dates populate for editing.
          const existingDate = (ach.unlockedAt || ach.earnedAt || "").split('T')[0];
          // Keep sentinel dates empty so user must provide a real one
          const isSentinel = existingDate.startsWith("1753") || existingDate.startsWith("0001");
          preSelected[ach.id] = {
            selected: true,
            unlockDate: isSentinel ? "" : existingDate,
            earnedTime: ach.earnedTime || "",
            unlockedOffline: ach.unlockedOffline || false,
          };
        }
      });
      setManualSyncSelections(preSelected);
      setManualSyncDialogOpen(true);
    } catch (error) {
      console.error("Failed to load achievements for manual sync:", error);
      toast.error("Failed to load achievements");
    }
  };
  
  // Toggle achievement selection in manual sync
  const handleToggleManualSyncAchievement = (achId) => {
    const ach = manualSyncAchievements.find(a => a.id === achId);
    const current = manualSyncSelections[achId];
    
    // Warn when unchecking an already-unlocked achievement
    if (current?.selected && ach?.isUnlocked) {
      toast.warning(`"${ach.name}" will revert to Locked status when saved`, { duration: 4000 });
    }
    
    setManualSyncSelections(prev => {
      if (current?.selected) {
        const { [achId]: removed, ...rest } = prev;
        return rest;
      } else {
        // Select with empty fields - user must explicitly provide date/time/offline
        return {
          ...prev,
          [achId]: {
            selected: true,
            unlockDate: ""
          }
        };
      }
    });
  };
  
  // Update unlock date for a manual sync achievement
  const handleUpdateManualSyncDate = (achId, date) => {
    setManualSyncSelections(prev => ({
      ...prev,
      [achId]: {
        ...prev[achId],
        unlockDate: date
      }
    }));
  };
  
  // Select all achievements in manual sync
  const handleSelectAllManualSync = () => {
    const allSelected = {};
    manualSyncAchievements.forEach(ach => {
      allSelected[ach.id] = {
        selected: true,
        unlockDate: manualSyncSelections[ach.id]?.unlockDate || ach.unlockedAt?.split('T')[0] || ""
      };
    });
    setManualSyncSelections(allSelected);
  };
  
  // Deselect all achievements in manual sync
  const handleDeselectAllManualSync = () => {
    setManualSyncSelections({});
  };
  
  // Save manual sync selections
  const handleSaveManualSync = async () => {
    setSavingManualSync(true);
    try {
      const updates = [];
      
      for (const [achId, data] of Object.entries(manualSyncSelections)) {
        if (data.selected) {
          const ach = manualSyncAchievements.find(a => a.id === achId);
          
          // Skip if no date, no time, and no offline flag — user didn't provide any data
          const hasDate = data.unlockDate && data.unlockDate.trim();
          const hasTime = data.earnedTime && data.earnedTime.trim();
          const hasOffline = data.unlockedOffline === true;
          if (!hasDate && !hasTime && !hasOffline) continue;
          
          // Only update if status changed or date/time/offline changed
          const hasChanges = !ach?.isUnlocked || 
            ach.earnedAt?.split('T')[0] !== data.unlockDate ||
            ach.earnedTime !== data.earnedTime ||
            ach.unlockedOffline !== data.unlockedOffline;
          
          if (hasChanges) {
            updates.push({
              achievementId: achId,
              isUnlocked: true,
              earnedAt: data.earnedAt || (data.unlockDate ? `${data.unlockDate}T12:00:00Z` : new Date().toISOString()),
              earnedTime: data.earnedTime || null,
              unlockedOffline: data.unlockedOffline || false,
            });
          }
        }
      }
      
      // Also handle deselected (unlocking -> locking)
      for (const ach of manualSyncAchievements) {
        if (ach.isUnlocked && !manualSyncSelections[ach.id]?.selected) {
          updates.push({
            achievementId: ach.id,
            isUnlocked: false,
            earnedAt: null,
            earnedTime: null,
            unlockedOffline: false,
          });
        }
      }
      
      if (updates.length === 0) {
        toast.info("No changes to save");
        setManualSyncDialogOpen(false);
        return;
      }
      
      // Batch update achievements
      const response = await axios.post(`${API}/games/${gameId}/bulk-update-achievements`, {
        achievements: updates
      });
      
      const { updated, errors } = response.data;
      if (errors?.length > 0) {
        toast.warning(`Updated ${updated} achievements, but ${errors.length} failed`);
      } else {
        toast.success(`Successfully updated ${updated} achievements!`);
      }
      
      // Remove incomplete-sync tag if present
      const currentTags = game?.gameTags || [];
      if (currentTags.includes("incomplete-sync")) {
        await axios.patch(`${API}/games/${gameId}`, { 
          gameTags: currentTags.filter(t => t !== "incomplete-sync"),
          isIncompleteSync: false
        });
      }
      
      setManualSyncDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Manual sync save failed:", error);
      toast.error(error.response?.data?.detail || "Failed to save manual sync");
    } finally {
      setSavingManualSync(false);
    }
  };

  // Get sync button tooltip message
  const getSyncTooltip = () => {
    if (!game) return "";
    const platform = game.platform;
    
    if (platform === "xbox") {
      return "Sync achievements from Xbox Live";
    } else if (platform === "steam") {
      if (!game.steamAppId) return "Missing Steam App ID - try re-importing the game";
      return "Sync achievements from Steam";
    } else if (platform === "playstation") {
      if (!game.npCommunicationId) return "Missing NP Communication ID - try re-importing the game";
      return "Sync trophies from PlayStation";
    }
    return "";
  };

  const handleCreateAchievement = async () => {
    if (!newAchievement.name.trim()) {
      toast.error("Achievement name is required");
      return;
    }

    try {
      let score = newAchievement.score;
      
      // Auto-set score for PlayStation trophy types
      if (game?.platform === "playstation" && newAchievement.trophyType) {
        const trophy = TROPHY_TYPES.find(t => t.value === newAchievement.trophyType);
        score = trophy?.score || 0;
      }

      await axios.post(`${API}/achievements`, {
        ...newAchievement,
        score,
        gameId: gameId,
        platform: game?.platform,
        dlcId: selectedDlcForAdd || null,  // Link to DLC if selected
      });
      
      toast.success("Achievement added!");
      setDialogOpen(false);
      setNewAchievement({ name: "", description: "", imageUrl: "", score: 0, tags: [], trophyType: "", earnedTime: "" });
      setSelectedDlcForAdd(null);
      fetchData();
    } catch (error) {
      console.error("Failed to create achievement:", error);
      toast.error("Failed to add achievement");
    }
  };

  const handleToggleUnlock = async (achievement, force = false) => {
    // If unlocking (currently locked), open the timestamp dialog first
    if (!achievement.isUnlocked) {
      setEditingAchievement({
        ...achievement,
        earnedAt: achievement.earnedAt ? new Date(achievement.earnedAt) : new Date(),
        earnedTime: achievement.earnedTime || "",
        unlockedOffline: achievement.unlockedOffline || false,
      });
      setTimestampDialogOpen(true);
      return;
    }
    
    // PROTECTION: Verified achievements can only be re-locked after explicit
    // user confirmation (force). Without it, block as before.
    const isVerified = achievement.verificationStatus === "verified" || achievement.verificationStatus === "secondary_verified";
    if (isVerified && !force) {
      toast.error("Cannot lock a verified achievement. This unlock was confirmed via platform sync or Exophase.");
      return;
    }
    
    // If locking (currently unlocked), just lock it
    try {
      await axios.put(`${API}/achievements/${achievement.id}`, {
        isUnlocked: false,
        ...(force ? { forceUnlock: true } : {}),
      });
      fetchData();
      toast.success("Achievement locked");
    } catch (error) {
      console.error("Failed to toggle achievement:", error);
      toast.error(error.response?.data?.detail || "Failed to update achievement");
    }
  };

  const handleUpdateAchievement = async () => {
    if (!editingAchievement) return;

    try {
      let score = editingAchievement.score;
      
      // Auto-set score for PlayStation trophy types
      if (game?.platform === "playstation" && editingAchievement.trophyType) {
        const trophy = TROPHY_TYPES.find(t => t.value === editingAchievement.trophyType);
        score = trophy?.score || editingAchievement.score;
      }

      await axios.put(`${API}/achievements/${editingAchievement.id}`, {
        name: editingAchievement.name,
        description: editingAchievement.description,
        imageUrl: editingAchievement.imageUrl,
        score,
        tags: editingAchievement.tags,
        trophyType: editingAchievement.trophyType,
        earnedTime: editingAchievement.earnedTime,
        dlcId: editingAchievement.dlcId === "base" ? null : editingAchievement.dlcId,  // Move to DLC or base game
        sectionId: editingAchievement.sectionId || null,  // Move to section within base game
        isSecret: !!editingAchievement.isSecret,  // Per-user override of secret flag
        isPerCharacter: !!editingAchievement.isPerCharacter,  // Per-user override enabling multi-character unlocks
      });
      
      toast.success("Achievement updated!");
      setEditDialogOpen(false);
      setEditingAchievement(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update achievement:", error);
      toast.error("Failed to update achievement");
    }
  };

  const handleDeleteAchievement = async (achievementId) => {
    try {
      await axios.delete(`${API}/achievements/${achievementId}`);
      toast.success("Achievement deleted");
      fetchData();
    } catch (error) {
      console.error("Failed to delete achievement:", error);
      toast.error("Failed to delete achievement");
    }
  };

  const handleUpdateTimestamp = async () => {
    if (!editingAchievement) return;

    try {
      await axios.put(`${API}/achievements/${editingAchievement.id}`, {
        earnedAt: editingAchievement.earnedAt,
        earnedTime: editingAchievement.earnedTime || null,
        unlockedOffline: editingAchievement.unlockedOffline || false,
        isUnlocked: true,
      });
      
      toast.success("Timestamp updated!");
      setTimestampDialogOpen(false);
      setEditingAchievement(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update timestamp:", error);
      toast.error("Failed to update timestamp");
    }
  };

  const handleImageUpload = async (file, isEdit = false) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = response.data.url;

      if (isEdit && editingAchievement) {
        setEditingAchievement({ ...editingAchievement, imageUrl: url });
      } else {
        setNewAchievement({ ...newAchievement, imageUrl: url });
      }
      toast.success("Image uploaded!");
    } catch (error) {
      console.error("Failed to upload image:", error);
      toast.error("Failed to upload image");
    }
  };

  const handleCreateDlc = async () => {
    if (!newDlc.name.trim()) {
      toast.error("DLC name is required");
      return;
    }

    try {
      await axios.post(`${API}/games/${gameId}/dlcs`, {
        name: newDlc.name,
        boxArtUrl: newDlc.boxArtUrl || game?.boxArtUrl,
        releaseDate: newDlc.releaseDate || null,
        completionEstimate: newDlc.completionEstimate || null,
        platform: game?.platform,
        system: game?.system,
      });
      
      toast.success("DLC added!");
      setDlcDialogOpen(false);
      setNewDlc({ name: "", boxArtUrl: "", releaseDate: "", completionEstimate: "" });
      fetchData();
    } catch (error) {
      console.error("Failed to create DLC:", error);
      toast.error("Failed to add DLC");
    }
  };
  
  // Update DLC release date
  const handleUpdateDlcReleaseDate = async (dlcId, releaseDate) => {
    try {
      await axios.patch(`${API}/games/${gameId}/dlcs/${dlcId}`, {
        releaseDate: releaseDate || ""
      });
      toast.success("Release date updated");
      setEditingDlcId(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update DLC:", error);
      toast.error("Failed to update release date");
    }
  };

  const handleFetchIgdbDlcs = async () => {
    setFetchingIgdbDlcs(true);
    try {
      const response = await axios.post(`${API}/igdb/fetch-dlcs/${gameId}?organize_achievements=false`);
      const data = response.data;
      
      if (data.success) {
        if (data.dlcs_created > 0) {
          toast.success(`Found ${data.dlcs_found} DLCs, created ${data.dlcs_created} new entries`);
        } else if (data.dlcs_found > 0) {
          toast.info(`Found ${data.dlcs_found} DLCs on IGDB - all already exist in your library`);
        } else {
          toast.info(`No DLCs found on IGDB for "${game?.name}"`);
        }
        fetchData(); // Refresh to show new DLCs
      } else {
        toast.error(data.message || "Failed to fetch DLCs");
      }
    } catch (error) {
      console.error("Failed to fetch DLCs from IGDB:", error);
      toast.error(error.response?.data?.detail || "Failed to fetch DLCs from IGDB");
    } finally {
      setFetchingIgdbDlcs(false);
    }
  };

  const handleDeleteDlc = async (dlcId) => {
    try {
      await axios.delete(`${API}/games/${gameId}/dlcs/${dlcId}`);
      toast.success("DLC deleted");
      fetchData();
    } catch (error) {
      console.error("Failed to delete DLC:", error);
      toast.error("Failed to delete DLC");
    }
  };

  const handleUpdateDlc = async () => {
    if (!editingDlc || !editingDlc.name.trim()) {
      toast.error("DLC name is required");
      return;
    }
    try {
      // Use the PATCH endpoint for DLCs which properly handles releaseDate and completionEstimate
      await axios.patch(`${API}/games/${gameId}/dlcs/${editingDlc.id}`, {
        name: editingDlc.name,
        boxArtUrl: editingDlc.boxArtUrl || null,
        releaseDate: editingDlc.releaseDate || "",
        completionEstimate: editingDlc.completionEstimate || null,
      });
      toast.success("DLC updated");
      setEditingDlc(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update DLC:", error);
      toast.error("Failed to update DLC");
    }
  };

  const handleToggleOwned = async () => {
    try {
      await axios.put(`${API}/games/${gameId}`, {
        isOwned: !game.isOwned,
      });
      setGame({ ...game, isOwned: !game.isOwned });
      toast.success(game.isOwned ? "Marked as not owned" : "Marked as owned");
    } catch (error) {
      console.error("Failed to update owned status:", error);
      toast.error("Failed to update owned status");
    }
  };

  const handleToggleNoAchievements = async () => {
    try {
      const newValue = !game.hasNoAchievements;
      await axios.put(`${API}/games/${gameId}`, {
        hasNoAchievements: newValue,
      });
      setGame({ ...game, hasNoAchievements: newValue });
      toast.success(newValue 
        ? "Marked as having no achievements - excluded from Library Maintenance" 
        : "Removed no-achievements flag"
      );
    } catch (error) {
      console.error("Failed to update no-achievements status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleTogglePriority = async () => {
    setTogglingPriority(true);
    try {
      const response = await axios.post(`${API}/games/${gameId}/priority`);
      setGame({ ...game, isPriority: response.data.isPriority });
      toast.success(response.data.message);
    } catch (error) {
      console.error("Failed to toggle priority:", error);
      toast.error("Failed to update priority status");
    } finally {
      setTogglingPriority(false);
    }
  };

  const handleToggleBacklog = async () => {
    try {
      const response = await axios.post(`${API}/games/${gameId}/backlog`);
      setGame({ ...game, isBacklog: response.data.isBacklog });
      toast.success(response.data.message);
    } catch (error) {
      console.error("Failed to toggle backlog:", error);
      toast.error("Failed to update backlog status");
    }
  };

  const handleToggleHidden = async () => {
    try {
      const newValue = !game?.isHidden;
      await axios.put(`${API}/games/${gameId}`, {
        isHidden: newValue
      });
      setGame({ ...game, isHidden: newValue });
      toast.success(newValue ? "Game hidden from library" : "Game unhidden");
    } catch (error) {
      console.error("Failed to toggle hidden status:", error);
      toast.error("Failed to update hidden status");
    }
  };

  const handleToggleManualComplete = async () => {
    try {
      const response = await axios.post(`${API}/games/${gameId}/manual-complete`);
      setGame({ ...game, isManuallyComplete: response.data.isManuallyComplete });
      toast.success(response.data.message);
    } catch (error) {
      console.error("Failed to toggle complete status:", error);
      toast.error("Failed to update complete status");
    }
  };

  const handleToggleIncompleteSync = async () => {
    try {
      const newValue = !game?.isIncompleteSync;
      const currentGameTags = game?.gameTags || [];
      
      // Also update gameTags to add/remove 'incomplete-sync' tag
      let newGameTags;
      if (newValue) {
        // Adding incomplete sync - add tag if not present
        newGameTags = currentGameTags.includes('incomplete-sync') 
          ? currentGameTags 
          : [...currentGameTags, 'incomplete-sync'];
      } else {
        // Removing incomplete sync - remove tag
        newGameTags = currentGameTags.filter(tag => tag !== 'incomplete-sync');
      }
      
      await axios.put(`${API}/games/${gameId}`, {
        isIncompleteSync: newValue,
        gameTags: newGameTags,
      });
      setGame({ ...game, isIncompleteSync: newValue, gameTags: newGameTags });
      toast.success(newValue ? "Marked as incomplete sync" : "Removed incomplete sync flag");
    } catch (error) {
      console.error("Failed to toggle incomplete sync:", error);
      toast.error("Failed to update incomplete sync status");
    }
  };

  const handleRemoveCopyNumber = async () => {
    try {
      await axios.put(`${API}/games/${gameId}/copy-number`, null, {
        params: { copy_number: null }
      });
      setGame({ ...game, copyNumber: null });
      toast.success("Copy label removed");
    } catch (error) {
      console.error("Failed to remove copy number:", error);
      toast.error("Failed to remove copy label");
    }
  };

  const handleAddGameTag = async (tagId) => {
    try {
      const response = await axios.post(`${API}/games/${gameId}/user-tags/${tagId}`);
      setGame({ ...game, userTags: response.data.userTags });
      toast.success("Tag added to game");
    } catch (error) {
      console.error("Failed to add tag:", error);
      toast.error("Failed to add tag");
    }
  };

  const handleRemoveGameTag = async (tagId) => {
    try {
      const response = await axios.delete(`${API}/games/${gameId}/user-tags/${tagId}`);
      setGame({ ...game, userTags: response.data.userTags });
      toast.success("Tag removed from game");
    } catch (error) {
      console.error("Failed to remove tag:", error);
      toast.error("Failed to remove tag");
    }
  };

  const handleDeleteGame = async () => {
    try {
      await axios.delete(`${API}/games/${gameId}`);
      toast.success("Game deleted");
      navigate("/games");
    } catch (error) {
      console.error("Failed to delete game:", error);
      toast.error("Failed to delete game");
    }
  };

  const handleDeduplicateAchievements = async () => {
    setDeduplicating(true);
    try {
      const response = await axios.post(`${API}/games/${gameId}/deduplicate-achievements?include_dlcs=true`);
      const data = response.data;
      
      if (data.achievementsMerged > 0) {
        toast.success(`Removed ${data.achievementsMerged} duplicate achievements`);
        fetchData(); // Refresh to show updated achievements
      } else {
        toast.info("No duplicate achievements found");
      }
    } catch (error) {
      console.error("Failed to deduplicate achievements:", error);
      toast.error("Failed to deduplicate achievements");
    } finally {
      setDeduplicating(false);
    }
  };

  // Edit platforms/systems tags
  const openEditTagsDialog = () => {
    setEditPlatforms(game?.platforms || (game?.platform ? [game.platform] : []));
    setEditSystems(game?.systems || (game?.system ? [game.system] : []));
    setEditTagsDialogOpen(true);
  };

  const handleRemovePlatform = (platformToRemove) => {
    setEditPlatforms(editPlatforms.filter(p => p !== platformToRemove));
  };

  const handleRemoveSystem = (systemToRemove) => {
    setEditSystems(editSystems.filter(s => s !== systemToRemove));
  };

  const handleSaveTags = async () => {
    try {
      await axios.put(`${API}/games/${gameId}`, {
        platforms: editPlatforms,
        systems: editSystems,
        // Update primary platform/system if arrays are not empty
        platform: editPlatforms.length > 0 ? editPlatforms[0] : game?.platform,
        system: editSystems.length > 0 ? editSystems[0] : game?.system,
      });
      toast.success("Tags updated successfully");
      setEditTagsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to update tags:", error);
      toast.error("Failed to update tags");
    }
  };

  const handleUpdateGameInfo = async () => {
    try {
      await axios.put(`${API}/games/${gameId}`, {
        name: game.name,
        platform: game.platform,
        system: game.system || null,
        systems: game.systems || [],
        boxArtUrl: game.boxArtUrl || null,
        releaseDate: game.releaseDate || null,
        publisher: game.publisher || null,
        developer: game.developer || null,
        genres: game.genres || [],
        specialHardware: game.specialHardware || null,
        completionEstimate: game.completionEstimate || null,
        hasNoAchievements: game.hasNoAchievements || false,
      });
      toast.success("Game updated");
      setGameInfoDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to update game:", error);
      toast.error("Failed to update game");
    }
  };

  // Quick update for completion estimate only
  const handleUpdateCompletionEstimate = async (newEstimate) => {
    try {
      await axios.put(`${API}/games/${gameId}`, {
        name: game.name,
        platform: game.platform,
        system: game.system || null,
        boxArtUrl: game.boxArtUrl || null,
        releaseDate: game.releaseDate || null,
        publisher: game.publisher || null,
        developer: game.developer || null,
        genres: game.genres || [],
        specialHardware: game.specialHardware || null,
        completionEstimate: newEstimate,
      });
      toast.success(newEstimate ? "Completion estimate updated" : "Completion estimate removed");
      // Update local state immediately
      setGame(prev => ({ ...prev, completionEstimate: newEstimate }));
    } catch (error) {
      console.error("Failed to update completion estimate:", error);
      toast.error("Failed to update completion estimate");
    }
  };

  // Notes handlers - accept text directly for use with GameHeaderNew component
  const handleAddNote = async (noteText, noteTitle, isPrivate, extras) => {
    const text = noteText || newNoteText;
    if (!text?.trim()) {
      toast.error("Please enter a note");
      return;
    }
    
    try {
      await axios.post(`${API}/games/${gameId}/notes`, { 
        text, 
        title: noteTitle || null,
        noteType: "note",
        isPrivate: isPrivate || false,
        imageUrl: extras?.imageUrl || null,
      });
      toast.success("Note added");
      setNewNoteText("");
      setNotesDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to add note:", error);
      toast.error("Failed to add note");
    }
  };

  const handleAddChecklist = async (checklistData) => {
    try {
      await axios.post(`${API}/games/${gameId}/notes`, checklistData);
      toast.success("Checklist created");
      setNewNoteText("");
      setNotesDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to create checklist:", error);
      toast.error("Failed to create checklist");
    }
  };

  const handleToggleChecklistItem = async (noteId, itemId, checked) => {
    // Optimistic local update so the checkbox flips instantly. Previously this
    // called fetchData() after every toggle, reloading the entire game +
    // achievements — painfully slow on large (90+ item) checklists.
    const patchChecked = (val) => setGame((prev) => prev ? {
      ...prev,
      notes: (prev.notes || []).map((n) =>
        n.id === noteId
          ? { ...n, checklistItems: (n.checklistItems || []).map((it) => it.id === itemId ? { ...it, checked: val } : it) }
          : n
      ),
    } : prev);

    patchChecked(checked);
    try {
      await axios.patch(`${API}/games/${gameId}/notes/${noteId}/items/${itemId}`, { checked });
    } catch (error) {
      console.error("Failed to toggle item:", error);
      toast.error("Failed to update checklist");
      patchChecked(!checked); // revert on failure
    }
  };

  const handleSaveChecklistPrefs = async (noteId, prefs) => {
    // Optimistically persist the checklist's view preference on local state so
    // it survives note refetches, then save to the backend.
    setGame((prev) => prev ? {
      ...prev,
      notes: (prev.notes || []).map((n) => n.id === noteId ? { ...n, ...prefs } : n),
    } : prev);
    try {
      await axios.patch(`${API}/games/${gameId}/notes/${noteId}/checklist-prefs`, prefs);
    } catch (error) {
      console.error("Failed to save checklist preferences:", error);
    }
  };

  const handleShareChecklist = async (shareData) => {
    if (!checklistToShare) return;
    try {
      await axios.post(`${API}/games/${gameId}/notes/${checklistToShare.id}/share`, shareData);
      toast.success("Checklist shared to community!");
      setShareChecklistDialogOpen(false);
      setChecklistToShare(null);
    } catch (error) {
      console.error("Failed to share checklist:", error);
      toast.error("Failed to share checklist");
    }
  };

  // Map handlers
  const handleAddMap = async (mapData) => {
    try {
      await axios.post(`${API}/games/${gameId}/notes`, mapData);
      toast.success("Map created");
      setNotesDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to create map:", error);
      toast.error("Failed to create map");
    }
  };

  const handleToggleMapPin = async (noteId, pinId, checked) => {
    try {
      await axios.patch(`${API}/games/${gameId}/notes/${noteId}/pins/${pinId}`, { checked });
      fetchData();
    } catch (error) {
      console.error("Failed to toggle pin:", error);
      toast.error("Failed to update pin");
    }
  };

  // Character handlers
  const handleUpdatePerCharacter = async (achievementId, unlocks) => {
    try {
      await axios.patch(`${API}/achievements/${achievementId}`, {
        perCharacterUnlocks: unlocks,
      });
      fetchData();
    } catch (error) {
      console.error("Failed to update per-character unlocks:", error);
      toast.error("Failed to update character unlocks");
    }
  };

  // Granular, per-entry save so rapid edits to different characters can never
  // overwrite each other (avoids the whole-array lost-update race).
  // Surgically merge a single updated achievement into local state so a save
  // reflects instantly without a full (slow) fetchData() refetch.
  const applyAchievementUpdate = (updated) => {
    if (!updated || !updated.id) return;
    const merge = (a) => {
      if (a.id !== updated.id) return a;
      const merged = { ...a, ...updated };
      // Never downgrade a per-character badge to "account wide" on a partial
      // update. The flag is a stable property of the badge; a save/remove of a
      // single character unlock must not flip it (fixes the production glitch
      // where the badge showed account-wide until a page refresh).
      if (a.isPerCharacter || updated.isPerCharacter || (Array.isArray(merged.perCharacterUnlocks) && merged.perCharacterUnlocks.length > 0)) {
        merged.isPerCharacter = true;
      }
      return merged;
    };
    setAchievements((prev) => prev.map(merge));
    setGroupedAchievements((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      if (next.baseGame) {
        next.baseGame = {
          ...next.baseGame,
          achievements: (next.baseGame.achievements || []).map(merge),
          sections: (next.baseGame.sections || []).map((s) => ({ ...s, achievements: (s.achievements || []).map(merge) })),
        };
      }
      next.dlcs = (next.dlcs || []).map((d) => ({ ...d, achievements: (d.achievements || []).map(merge) }));
      return next;
    });
  };

  const handleSetCharacterUnlock = async (achievementId, characterNoteId, unlockedAt, characterGameId) => {
    try {
      const res = await axios.put(`${API}/achievements/${achievementId}/per-character/${characterNoteId}`, {
        unlockedAt: unlockedAt ?? null,
        gameId: characterGameId ?? gameId,
      });
      applyAchievementUpdate(res.data);
      return true;
    } catch (error) {
      console.error("Failed to save character unlock:", error);
      toast.error("Failed to save character unlock");
      return false;
    }
  };

  const handleRemoveCharacterUnlock = async (achievementId, characterNoteId) => {
    try {
      const res = await axios.delete(`${API}/achievements/${achievementId}/per-character/${characterNoteId}`);
      applyAchievementUpdate(res.data);
      return true;
    } catch (error) {
      console.error("Failed to remove character unlock:", error);
      toast.error("Failed to remove character unlock");
      return false;
    }
  };

  const handleSaveCharacter = async (payload) => {
    try {
      if (editingCharacter?.id) {
        await axios.put(`${API}/games/${gameId}/notes/${editingCharacter.id}`, payload);
        toast.success("Character updated");
      } else {
        await axios.post(`${API}/games/${gameId}/notes`, { ...payload, noteType: "character" });
        toast.success("Character added");
      }
      setCharacterEditorOpen(false);
      setEditingCharacter(null);
      fetchData();
    } catch (error) {
      console.error("Failed to save character:", error);
      toast.error("Failed to save character");
    }
  };

  // Section-level map handlers
  const handleAddSectionMap = async (mapData) => {
    try {
      await axios.post(`${API}/games/${gameId}/notes`, { ...mapData, sectionId: activeSectionIdForMap });
      toast.success("Map added to section");
      setSectionMapEditorOpen(false);
      setEditingSectionMap(null);
      setActiveSectionIdForMap(null);
      fetchData();
    } catch (error) {
      console.error("Failed to create section map:", error);
      toast.error("Failed to create map");
    }
  };

  const handleEditSectionMap = async (mapData) => {
    try {
      await axios.put(`${API}/games/${gameId}/notes/${editingSectionMap.id}`, mapData);
      toast.success("Map updated");
      setSectionMapEditorOpen(false);
      setEditingSectionMap(null);
      setActiveSectionIdForMap(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update map:", error);
      toast.error("Failed to update map");
    }
  };

  const handleOpenSubmitMapDialog = (note) => {
    setMapToSubmit(note);
    setSubmitMapDescription("");
    setSubmitMapTags([]);
    setSubmitMapShowSubmitter(true);
    setSubmitMapDialogOpen(true);
  };

  const handleSubmitMapToCommunity = async () => {
    if (!mapToSubmit || !game) return;
    setSubmittingMap(true);
    try {
      await axios.post(`${API}/community/maps/submit`, {
        title: mapToSubmit.title || "Untitled Map",
        description: submitMapDescription,
        masterGameId: game.masterGameId || game.id,
        gameName: game.name,
        gamePlatform: game.platform,
        mapImageUrl: mapToSubmit.mapImageUrl,
        pins: (mapToSubmit.pins || []).map(p => ({ id: p.id, x: p.x, y: p.y, label: p.label })),
        sectionName: mapToSubmit.sectionId
          ? (groupedAchievements?.baseGame?.sections || []).find(s => s.id === mapToSubmit.sectionId)?.name || null
          : null,
        showSubmitter: submitMapShowSubmitter,
        tags: submitMapTags,
      });
      toast.success("Map submitted for community review!");
      setSubmitMapDialogOpen(false);
      setMapToSubmit(null);
    } catch (error) {
      console.error("Failed to submit map:", error);
      toast.error(error?.response?.data?.detail || "Failed to submit map");
    } finally {
      setSubmittingMap(false);
    }
  };

  const handleEditMap = async (noteId, mapData) => {
    try {
      await axios.put(`${API}/games/${gameId}/notes/${noteId}`, mapData);
      toast.success("Map updated");
      fetchData();
    } catch (error) {
      console.error("Failed to update map:", error);
      toast.error("Failed to update map");
    }
  };

  const handleEditChecklist = async (noteId, updatedData) => {
    try {
      await axios.put(`${API}/games/${gameId}/notes/${noteId}`, {
        text: updatedData.text,
        description: updatedData.description,
        checklistItems: updatedData.checklistItems
      });
      toast.success("Checklist updated");
      fetchData();
    } catch (error) {
      console.error("Failed to update checklist:", error);
      toast.error("Failed to update checklist");
    }
  };

  const handleUpdateNote = async (noteId, noteText, noteTitle, isPrivate, extras) => {
    const id = noteId || editingNote?.id;
    const text = noteText || editingNote?.text;

    if (!id) {
      toast.error("Missing note id");
      return;
    }
    // Determine if this is a pure organizer move (only noteOrganizerId /
    // noteSubgroupId changes). For those, empty text is fine — character
    // and map notes often have no `text` field but still need to be
    // movable between organizers.
    const isOrganizerOnly = !!extras && (
      Object.prototype.hasOwnProperty.call(extras, "noteOrganizerId") ||
      Object.prototype.hasOwnProperty.call(extras, "noteSubgroupId")
    ) && !noteText && noteTitle === undefined && isPrivate === undefined;

    if (!isOrganizerOnly && !text?.trim()) {
      toast.error("Please enter a note");
      return;
    }

    try {
      const payload = {};
      if (text) payload.text = text;
      if (noteTitle !== undefined) payload.title = noteTitle;
      if (isPrivate !== undefined) payload.isPrivate = isPrivate;
      if (extras && Object.prototype.hasOwnProperty.call(extras, "imageUrl")) {
        payload.imageUrl = extras.imageUrl;
      }
      if (extras && Object.prototype.hasOwnProperty.call(extras, "noteOrganizerId")) {
        payload.noteOrganizerId = extras.noteOrganizerId;
      }
      if (extras && Object.prototype.hasOwnProperty.call(extras, "noteSubgroupId")) {
        payload.noteSubgroupId = extras.noteSubgroupId;
      }
      await axios.put(`${API}/games/${gameId}/notes/${id}`, payload);
      if (!isOrganizerOnly) toast.success("Note updated");
      setEditingNote(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update note:", error);
      toast.error("Failed to update note");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?\n\nDeleted notes cannot be recovered.")) return;
    try {
      await axios.delete(`${API}/games/${gameId}/notes/${noteId}`);
      toast.success("Note deleted");
      fetchData();
    } catch (error) {
      console.error("Failed to delete note:", error);
      toast.error("Failed to delete note");
    }
  };

  const handleToggleNotePlanner = async (noteId) => {
    try {
      const res = await axios.patch(`${API}/games/${gameId}/notes/${noteId}/toggle-planner`);
      if (res.data?.pinnedToPlanner) {
        toast.success("Pinned to planner");
      } else {
        toast.success("Removed from planner");
      }
      fetchData();
    } catch (error) {
      console.error("Failed to toggle planner pin:", error);
      toast.error("Failed to toggle planner pin");
    }
  };

  // Link handlers
  const handleAddLink = async (title, url, isPrivate, description) => {
    if (!title?.trim() || !url?.trim()) {
      toast.error("Please enter both title and URL");
      return;
    }
    
    try {
      await axios.post(`${API}/games/${gameId}/links`, { title: title.trim(), url: url.trim(), isPrivate: isPrivate || false, description: description || "" });
      toast.success("Link added");
      fetchData();
    } catch (error) {
      console.error("Failed to add link:", error);
      toast.error("Failed to add link");
    }
  };

  const handleEditLink = async (linkId, title, url, isPrivate, description) => {
    if (!linkId || !title?.trim() || !url?.trim()) {
      toast.error("Please enter both title and URL");
      return;
    }
    
    try {
      await axios.put(`${API}/games/${gameId}/links/${linkId}`, { title: title.trim(), url: url.trim(), isPrivate, description: description || "" });
      toast.success("Link updated");
      fetchData();
    } catch (error) {
      console.error("Failed to update link:", error);
      toast.error("Failed to update link");
    }
  };

  const handleDeleteLink = async (linkId) => {
    try {
      await axios.delete(`${API}/games/${gameId}/links/${linkId}`);
      toast.success("Link deleted");
      fetchData();
    } catch (error) {
      console.error("Failed to delete link:", error);
      toast.error("Failed to delete link");
    }
  };

  const handleReorderNotes = async (noteIds) => {
    try {
      await axios.put(`${API}/games/${gameId}/notes/reorder`, { noteIds });
      fetchData();
    } catch (error) {
      console.error("Failed to reorder notes:", error);
      toast.error("Failed to reorder");
    }
  };

  const handleReorderLinks = async (linkIds) => {
    try {
      await axios.put(`${API}/games/${gameId}/links/reorder`, { linkIds });
      fetchData();
    } catch (error) {
      console.error("Failed to reorder links:", error);
      toast.error("Failed to reorder");
    }
  };

  const handleReorderContent = async (orderedIds) => {
    try {
      await axios.put(`${API}/games/${gameId}/content/reorder`, { orderedIds });
      // No fetchData needed — order is purely a frontend display preference
      // and the local optimistic update already reflects the change.
    } catch (error) {
      console.error("Failed to reorder content:", error);
      toast.error("Failed to reorder");
    }
  };

  // Refresh from database handler - searches Master DB first, then IGDB as fallback
  const handleRefreshSearch = async () => {
    if (!refreshSearch.trim()) {
      toast.error("Please enter a search term");
      return;
    }
    
    setRefreshLoading(true);
    setRefreshResults([]);
    setSelectedRefreshGame(null);
    
    // Trademark symbols (™ ® ℠) are noise for matching — strip them so a title
    // synced with them still finds the clean MDB/IGDB entry.
    const cleanedSearch = refreshSearch.replace(/[™®℠]/g, "").trim();
    
    try {
      if (refreshSource === "master" || !masterDbSearched) {
        // First, search Master Database
        const masterResponse = await axios.get(`${API}/master/games`, {
          params: { search: cleanedSearch, limit: 20 }
        });
        // The API returns { games: [...], total: N, ... }
        const masterResults = masterResponse.data?.games || [];
        
        setMasterDbSearched(true);
        
        if (masterResults.length > 0) {
          // Transform to match expected format
          const transformedResults = masterResults.map(g => ({
            id: g.id,
            name: g.name,
            cover_url: g.boxArtUrl,
            released: g.releaseDate,
            developers: g.developer ? [g.developer] : [],
            publishers: g.publisher ? [g.publisher] : [],
            genres: g.genres,
            platforms: g.platforms || [],
            systems: g.systems || [],
            region: g.region || "",
            source: "master"
          }));
          setRefreshResults(transformedResults);
          setRefreshSource("master");
          return;
        } else {
          // No results in Master DB. Do NOT auto-fall back to IGDB — stop here
          // so the dialog can offer "Request this game" first, with IGDB only
          // as an explicit secondary action ("Search IGDB Instead").
          setRefreshResults([]);
          setRefreshSource("master");
          return;
        }
      }
      
      // Search IGDB — only reached when explicitly requested (the user clicked
      // "Search IGDB Instead", which sets refreshSource to "igdb").
      const response = await axios.get(`${API}/igdb/search`, {
        params: { query: cleanedSearch, limit: 50 }
      });
      const results = response.data.results || [];
      
      // Mark results as from IGDB
      const transformedResults = results.map(g => ({ ...g, source: "igdb" }));
      setRefreshResults(transformedResults);
      setRefreshSource("igdb");
      
      if (results.length === 0) {
        toast.info("No games found in IGDB either. Try a different search term.");
        // Reset masterDbSearched so user can search Master DB again with different term
        setMasterDbSearched(false);
      }
    } catch (error) {
      console.error("Search failed:", error);
      toast.error(error.response?.data?.detail || "Search failed");
      // Reset on error so user can try again
      setMasterDbSearched(false);
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleSelectRefreshGame = (game) => {
    setSelectedRefreshGame(game);
  };

  const handleRefreshGame = async () => {
    if (!selectedRefreshGame) {
      toast.error("Please select a game from the search results");
      return;
    }
    
    if (refreshFields.length === 0) {
      toast.error("Please select at least one field to update");
      return;
    }
    
    setRefreshLoading(true);
    
    try {
      const source = selectedRefreshGame.source || refreshSource;
      
      if (source === "master") {
        // Update from Master Database - directly copy fields
        const masterGame = await axios.get(`${API}/master/games/${selectedRefreshGame.id}`);
        const masterData = masterGame.data;
        
        const updatePayload = {};
        if (refreshFields.includes("boxArtUrl") && masterData.boxArtUrl) {
          updatePayload.boxArtUrl = masterData.boxArtUrl;
        }
        if (refreshFields.includes("releaseDate") && masterData.releaseDate) {
          updatePayload.releaseDate = masterData.releaseDate;
        }
        if (refreshFields.includes("publisher") && masterData.publisher) {
          updatePayload.publisher = masterData.publisher;
        }
        if (refreshFields.includes("developer") && masterData.developer) {
          updatePayload.developer = masterData.developer;
        }
        if (refreshFields.includes("genres") && masterData.genres?.length > 0) {
          updatePayload.genres = masterData.genres;
        }
        if (refreshFields.includes("completionEstimate") && masterData.completionEstimate) {
          updatePayload.completionEstimate = masterData.completionEstimate;
        }
        
        // Sync system/systems from Master DB if available
        if (masterData.systems?.length > 0) {
          updatePayload.system = masterData.systems[0];
          updatePayload.systems = masterData.systems;
        }
        
        // Sync contentTags from Master DB if available
        if (masterData.contentTags?.length > 0) {
          updatePayload.contentTags = masterData.contentTags;
        }
        
        // Also link to Master DB
        updatePayload.masterGameId = selectedRefreshGame.id;
        
        // Update game metadata
        await axios.put(`${API}/games/${gameId}`, {
          name: game.name,
          platform: game.platform,
          ...updatePayload
        });
        
        const updatedFields = Object.keys(updatePayload).filter(k => k !== "masterGameId");
        let message = updatedFields.length > 0 
          ? `Updated: ${updatedFields.join(", ")}` 
          : "Linked to Master DB";
        
        // Sync achievements & DLCs if selected
        if (refreshFields.includes("achievements")) {
          try {
            const syncResult = await axios.post(`${API}/master/sync-to-library/${gameId}`);
            const { added, updated, dlcsCreated } = syncResult.data;
            
            let achMessage = [];
            if (added > 0) achMessage.push(`${added} added`);
            if (updated > 0) achMessage.push(`${updated} updated`);
            if (dlcsCreated > 0) achMessage.push(`${dlcsCreated} DLCs created`);
            
            if (achMessage.length > 0) {
              message += ` | Achievements: ${achMessage.join(", ")}`;
            } else {
              message += ` | Achievements already synced`;
            }
          } catch (syncError) {
            console.error("Achievement sync failed:", syncError);
            message += ` | Achievement sync failed`;
          }
        }
        
        toast.success(message);
      } else {
        // Update from IGDB
        const response = await axios.post(`${API}/games/${gameId}/refresh`, {
          source: "igdb",
          source_id: selectedRefreshGame.id,
          fields: refreshFields.filter(f => f !== "achievements"), // achievements not supported from IGDB
        });
        
        toast.success(`Updated from IGDB: ${response.data.updatedFields.join(", ")}`);
      }
      
      setRefreshDialogOpen(false);
      setRefreshSearch("");
      setRefreshResults([]);
      setSelectedRefreshGame(null);
      setMasterDbSearched(false);
      fetchData();
    } catch (error) {
      console.error("Refresh failed:", error);
      toast.error(error.response?.data?.detail || "Failed to refresh game data");
    } finally {
      setRefreshLoading(false);
    }
  };

  const toggleRefreshField = (field) => {
    if (refreshFields.includes(field)) {
      setRefreshFields(refreshFields.filter(f => f !== field));
    } else {
      setRefreshFields([...refreshFields, field]);
    }
  };

  // Cover Art Functions (IGDB + SteamGridDB)
  const openCoverDialog = () => {
    if (game) {
      setCoverSearchQuery(game.name || "");
    }
    setCoverResults({ igdb: [], steamgriddb: [], combined: [] });
    setSelectedCover(null);
    setCoverSourceFilter("all");
    setCoverDialogOpen(true);
  };

  const searchCovers = async () => {
    if (!coverSearchQuery.trim()) {
      toast.error("Enter a game name to search");
      return;
    }
    
    setCoverLoading(true);
    setCoverResults({ igdb: [], steamgriddb: [], combined: [] });
    
    try {
      const response = await axios.get(`${API}/covers/search/${encodeURIComponent(coverSearchQuery.trim())}`);
      setCoverResults(response.data.results || { igdb: [], steamgriddb: [], combined: [] });
      
      if (response.data.total_combined === 0) {
        toast.info("No covers found");
      } else {
        toast.success(`Found ${response.data.total_combined} games with covers`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to search for covers");
    } finally {
      setCoverLoading(false);
    }
  };

  const applyCover = async () => {
    if (!selectedCover || !game) return;
    
    setCoverUploading(true);
    try {
      // Upload to Cloudinary first
      const uploadResponse = await axios.post(`${API}/cloudinary/upload-url`, {
        url: selectedCover.url,
        folder: "game_covers",
        public_id: `user_${game.id}_cover`
      });
      
      const cloudinaryUrl = uploadResponse.data.url;
      
      // Update ONLY the box art. Sending the whole `game` object back through
      // PUT /games/{id} (GameUpdate) fails validation (422) for master-linked
      // games whose `contentTags` are objects, not strings — which is why cover
      // updates failed for some games but not others.
      await axios.put(`${API}/games/${game.id}`, {
        boxArtUrl: cloudinaryUrl
      });
      
      toast.success("Box art updated successfully!");
      fetchData();
      setCoverDialogOpen(false);
    } catch (error) {
      // If Cloudinary upload fails, try using the source URL directly
      try {
        await axios.put(`${API}/games/${game.id}`, {
          boxArtUrl: selectedCover.url
        });
        toast.success(`Box art updated (using ${selectedCover.source} URL)`);
        fetchData();
        setCoverDialogOpen(false);
      } catch (err) {
        toast.error("Failed to update box art");
      }
    } finally {
      setCoverUploading(false);
    }
  };

  const getFilteredCoverResults = () => {
    if (coverSourceFilter === "all") {
      return coverResults.combined || [];
    } else if (coverSourceFilter === "igdb") {
      return coverResults.igdb || [];
    } else if (coverSourceFilter === "steamgriddb") {
      return coverResults.steamgriddb || [];
    }
    return [];
  };

  const addGenre = () => {
    if (genreInput.trim() && !game.genres?.includes(genreInput.trim())) {
      setGame({ ...game, genres: [...(game.genres || []), genreInput.trim()] });
      setGenreInput("");
    }
  };

  const removeGenre = (genre) => {
    setGame({ ...game, genres: (game.genres || []).filter(g => g !== genre) });
  };

  const toggleTag = (tagId, isEdit = false) => {
    if (isEdit && editingAchievement) {
      const currentTags = editingAchievement.tags || [];
      const newTags = currentTags.includes(tagId)
        ? currentTags.filter((t) => t !== tagId)
        : [...currentTags, tagId];
      setEditingAchievement({ ...editingAchievement, tags: newTags });
    } else {
      const currentTags = newAchievement.tags || [];
      const newTags = currentTags.includes(tagId)
        ? currentTags.filter((t) => t !== tagId)
        : [...currentTags, tagId];
      setNewAchievement({ ...newAchievement, tags: newTags });
    }
  };

  // Toggle section expansion
  // ============ SECTION HANDLERS ============
  const handleCreateSection = async () => {
    if (!newSectionName.trim()) {
      toast.error("Organizer name is required");
      return;
    }
    try {
      await axios.post(`${API}/games/${gameId}/sections`, {
        name: newSectionName.trim(),
        description: newSectionDescription.trim() || null,
      });
      toast.success("Organizer created!");
      setNewSectionName("");
      setNewSectionDescription("");
      setSectionDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to create section:", error);
      toast.error("Failed to create section");
    }
  };

  const handleUpdateSection = async (sectionId, newName, newDescription) => {
    try {
      await axios.put(`${API}/games/${gameId}/sections/${sectionId}`, {
        name: newName,
        description: newDescription !== undefined ? (newDescription.trim() || null) : undefined,
      });
      toast.success("Organizer updated!");
      setEditingSectionId(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update section:", error);
      toast.error("Failed to update section");
    }
  };

  const handleDeleteSection = async (sectionId) => {
    try {
      await axios.delete(`${API}/games/${gameId}/sections/${sectionId}`);
      toast.success("Organizer deleted (achievements moved to base game)");
      fetchData();
    } catch (error) {
      console.error("Failed to delete section:", error);
      toast.error("Failed to delete section");
    }
  };

  // ============ CHALLENGE HANDLERS ============
  const handleCreateChallenge = async () => {
    if (!newChallenge.name.trim()) {
      toast.error("Challenge name is required");
      return;
    }
    try {
      await axios.post(`${API}/games/${gameId}/challenges`, {
        ...newChallenge,
        gameId: gameId,
      });
      toast.success("Challenge created!");
      setNewChallenge({
        name: "",
        description: "",
        imageUrl: "",
        timerSeconds: null,
        dueDate: null,
        prerequisiteAchievementIds: []
      });
      setChallengeDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to create challenge:", error);
      toast.error("Failed to create challenge");
    }
  };

  const handleUpdateChallenge = async () => {
    if (!editingChallenge) return;
    try {
      await axios.put(`${API}/games/${gameId}/challenges/${editingChallenge.id}`, {
        name: editingChallenge.name,
        description: editingChallenge.description,
        imageUrl: editingChallenge.imageUrl,
        timerSeconds: editingChallenge.timerSeconds,
        dueDate: editingChallenge.dueDate,
        prerequisiteAchievementIds: editingChallenge.prerequisiteAchievementIds,
      });
      toast.success("Challenge updated!");
      setEditingChallenge(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update challenge:", error);
      toast.error("Failed to update challenge");
    }
  };

  const handleToggleChallenge = async (challengeId) => {
    try {
      await axios.post(`${API}/games/${gameId}/challenges/${challengeId}/complete`);
      toast.success("Challenge status updated!");
      fetchData();
    } catch (error) {
      console.error("Failed to toggle challenge:", error);
      toast.error("Failed to update challenge");
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    try {
      await axios.delete(`${API}/games/${gameId}/challenges/${challengeId}`);
      toast.success("Challenge deleted!");
      fetchData();
    } catch (error) {
      console.error("Failed to delete challenge:", error);
      toast.error("Failed to delete challenge");
    }
  };

  // ============ SHARE TO COMMUNITY HANDLERS ============
  const handleShareToCommunity = async (shareData) => {
    const {
      shareTab,
      selectedOrganizersToShare,
      selectedChallengesToShare,
      selectedOrganizersForPlanner,
      shareDescription,
      shareShowSubmitter,
      newGamePlanner,
      shareTags = [],
      includeScorecards = false,
    } = shareData;

    if (shareTab === "organizers" && selectedOrganizersToShare.length === 0) {
      toast.error("Please select at least one organizer to share");
      return;
    }
    if (shareTab === "challenges" && selectedChallengesToShare.length === 0) {
      toast.error("Please select at least one challenge to share");
      return;
    }
    if (shareTab === "planner") {
      if (selectedOrganizersForPlanner.length < 2) {
        toast.error("Please select at least 2 organizers for the Game Planner");
        return;
      }
      if (!newGamePlanner.name.trim()) {
        toast.error("Please enter a name for the Game Planner");
        return;
      }
    }

    setShareSubmitting(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      if (shareTab === "organizers") {
        // Submit each selected organizer
        for (const sectionId of selectedOrganizersToShare) {
          const section = groupedAchievements?.baseGame?.sections?.find(s => s.id === sectionId);
          if (!section) continue;

          // Get achievement names for this section (base game + DLC achievements)
          const baseAchNames = (section.achievements || []).map(a => a.name);
          const dlcAchNames = (groupedAchievements?.dlcs || []).flatMap(dlc => 
            (dlc.achievements || []).filter(a => a.sectionId === sectionId).map(a => a.name)
          );
          const achievementNames = [...baseAchNames, ...dlcAchNames];
          
          if (achievementNames.length === 0) {
            toast.error(`Organizer "${section.name}" has no achievements`);
            errorCount++;
            continue;
          }

          try {
            await axios.post(`${API}/community/organizers/submit`, {
              name: section.name,
              description: section.description || shareDescription || `Community organizer for ${game?.name}`,
              masterGameId: game?.masterGameId || gameId,
              gameName: game?.name || "Unknown Game",
              gamePlatform: game?.platform || "unknown",
              achievementNames: achievementNames,
              sortOrder: section.sortOrder || 0,
              showSubmitter: shareShowSubmitter,
              tags: shareTags,
            });
            successCount++;
          } catch (err) {
            console.error(`Failed to submit organizer ${section.name}:`, err);
            errorCount++;
          }
        }
      } else if (shareTab === "challenges") {
        // Submit each selected challenge
        for (const challengeId of selectedChallengesToShare) {
          const challenge = challenges.find(c => c.id === challengeId);
          if (!challenge) continue;

          // Get prerequisite achievement names
          const prereqNames = [];
          if (challenge.prerequisiteAchievementIds?.length > 0) {
            const allAchievements = [
              ...(groupedAchievements?.baseGame?.achievements || []),
              ...(groupedAchievements?.baseGame?.sections || []).flatMap(s => s.achievements || []),
              ...(groupedAchievements?.dlcs || []).flatMap(d => d.achievements || [])
            ];
            for (const achId of challenge.prerequisiteAchievementIds) {
              const ach = allAchievements.find(a => a.id === achId);
              if (ach) prereqNames.push(ach.name);
            }
          }

          try {
            await axios.post(`${API}/community/challenges/submit`, {
              name: challenge.name,
              description: challenge.description || shareDescription || `Community challenge for ${game?.name}`,
              masterGameId: game?.masterGameId || gameId,
              gameName: game?.name || "Unknown Game",
              gamePlatform: game?.platform || "unknown",
              imageUrl: challenge.imageUrl || null,
              difficulty: challenge.difficulty || "medium",
              timerSeconds: challenge.timerSeconds || null,
              prerequisiteAchievementNames: prereqNames,
              showSubmitter: shareShowSubmitter,
              tags: shareTags,
            });
            successCount++;
          } catch (err) {
            console.error(`Failed to submit challenge ${challenge.name}:`, err);
            errorCount++;
          }
        }
      } else if (shareTab === "planner") {
        // Submit Game Planner
        try {
          await axios.post(`${API}/community/game-planners/submit`, {
            name: newGamePlanner.name,
            description: newGamePlanner.description || shareDescription || `Game Planner for ${game?.name}`,
            masterGameId: game?.masterGameId || gameId,
            gameName: game?.name || "Unknown Game",
            gamePlatform: game?.platform || "unknown",
            organizerIds: selectedOrganizersForPlanner,
            showSubmitter: shareShowSubmitter,
            tags: shareTags,
            includeScorecards,
          });
          successCount = 1;
        } catch (err) {
          console.error("Failed to submit Game Planner:", err);
          errorCount = 1;
        }
      }

      if (successCount > 0) {
        toast.success(`${shareTab === "planner" ? "Game Planner" : `${successCount} item(s)`} submitted for review!`);
        setShareDialogOpen(false);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} item(s) failed to submit`);
      }
    } catch (error) {
      console.error("Failed to share to community:", error);
      toast.error("Failed to share to community");
    } finally {
      setShareSubmitting(false);
    }
  };

  // ============ BROWSE COMMUNITY CONTENT HANDLERS ============
  const fetchCommunityContent = async () => {
    if (!game?.masterGameId && !gameId) {
      setCommunityOrganizers([]);
      setCommunityChallenges([]);
      setCommunityGamePlanners([]);
      setCommunityChecklists([]);
      setCommunityGrids([]);
      setCommunityGridTemplates([]);
      setCommunityMaps([]);
      return;
    }
    
    setCommunityLoading(true);
    try {
      const masterGameIdToUse = game?.masterGameId || gameId;
      const userGameId = game?.gameId || game?.id || gameId;
      
      const [organizersRes, challengesRes, plannersRes, checklistsRes, gridsRes, gridTemplatesRes, mapsRes] = await Promise.all([
        axios.get(`${API}/community/organizers?masterGameId=${masterGameIdToUse}&status=approved`).catch(() => ({ data: { organizers: [] } })),
        axios.get(`${API}/community/challenges?masterGameId=${masterGameIdToUse}&status=approved`).catch(() => ({ data: { challenges: [] } })),
        axios.get(`${API}/community/game-planners?masterGameId=${masterGameIdToUse}&status=approved`).catch(() => ({ data: { planners: [] } })),
        axios.get(`${API}/community/checklists?game_id=${userGameId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/scorecards/community-grids?game_id=${masterGameIdToUse}`).catch(() => ({ data: [] })),
        axios.get(`${API}/scorecards/templates?public_only=true&game_id=${userGameId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/community/maps?masterGameId=${masterGameIdToUse}`).catch(() => ({ data: { maps: [] } })),
      ]);
      
      // API returns nested objects
      const organizers = organizersRes?.data?.organizers || [];
      const challenges = challengesRes?.data?.challenges || [];
      const planners = plannersRes?.data?.planners || [];
      setCommunityOrganizers(Array.isArray(organizers) ? organizers : []);
      setCommunityChallenges(Array.isArray(challenges) ? challenges : []);
      setCommunityGamePlanners(Array.isArray(planners) ? planners : []);
      setCommunityChecklists(Array.isArray(checklistsRes?.data) ? checklistsRes.data : []);
      setCommunityGrids(Array.isArray(gridsRes?.data) ? gridsRes.data : []);
      setCommunityGridTemplates(Array.isArray(gridTemplatesRes?.data) ? gridTemplatesRes.data : []);
      setCommunityMaps(mapsRes?.data?.maps || []);
    } catch (error) {
      console.error("Failed to fetch community content:", error);
      setCommunityOrganizers([]);
      setCommunityChallenges([]);
      setCommunityGamePlanners([]);
      setCommunityChecklists([]);
      setCommunityGrids([]);
      setCommunityGridTemplates([]);
      setCommunityMaps([]);
    } finally {
      setCommunityLoading(false);
    }
  };

  const handleAdoptOrganizer = async (organizerId) => {
    setAdoptingId(organizerId);
    try {
      await axios.post(`${API}/community/organizers/${organizerId}/adopt`, {
        gameId: gameId
      });
      toast.success("Organizer adopted! Achievements have been organized.");
      fetchData(); // Refresh to show new organizer
      fetchCommunityContent(); // Refresh counts
    } catch (error) {
      console.error("Failed to adopt organizer:", error);
      toast.error(error.response?.data?.detail || "Failed to adopt organizer");
    } finally {
      setAdoptingId(null);
    }
  };

  const handleAdoptChallenge = async (challengeId) => {
    setAdoptingId(challengeId);
    try {
      await axios.post(`${API}/community/challenges/${challengeId}/adopt`, {
        gameId: gameId
      });
      toast.success("Challenge adopted! It's now in your challenges list.");
      fetchData(); // Refresh to show new challenge
      fetchCommunityContent(); // Refresh counts
    } catch (error) {
      console.error("Failed to adopt challenge:", error);
      toast.error(error.response?.data?.detail || "Failed to adopt challenge");
    } finally {
      setAdoptingId(null);
    }
  };

  const handleAdoptGamePlanner = async (plannerId) => {
    setAdoptingId(plannerId);
    try {
      const response = await axios.post(`${API}/community/game-planners/${plannerId}/adopt`);
      toast.success(response.data?.message || "Game Planner adopted! Organizers have been added.");
      fetchData(); // Refresh to show new organizers
      fetchCommunityContent(); // Refresh counts
    } catch (error) {
      console.error("Failed to adopt Game Planner:", error);
      toast.error(error.response?.data?.detail || "Failed to adopt Game Planner");
    } finally {
      setAdoptingId(null);
    }
  };

  const handleCopyChecklist = async (checklistId) => {
    setAdoptingId(checklistId);
    try {
      await axios.post(`${API}/community/checklists/${checklistId}/copy?game_id=${gameId}`);
      toast.success("Checklist copied to your game!");
      fetchData();
    } catch (error) {
      console.error("Failed to copy checklist:", error);
      toast.error(error.response?.data?.detail || "Failed to copy checklist");
    } finally {
      setAdoptingId(null);
    }
  };

  const handleAdoptGrid = async (gridData) => {
    setAdoptingId(gridData.id);
    try {
      await axios.post(`${API}/scorecards`, {
        name: gridData.name || "Grid",
        gameId: gameId,
        rows: gridData.rows || [],
        cols: gridData.cols || [],
        cells: gridData.cells || {},
      });
      toast.success("Grid added to your game!");
      fetchData();
    } catch (error) {
      console.error("Failed to adopt grid:", error);
      toast.error(error.response?.data?.detail || "Failed to adopt grid");
    } finally {
      setAdoptingId(null);
    }
  };

  const handleAdoptGridTemplate = async (templateId) => {
    setAdoptingId(templateId);
    try {
      await axios.post(`${API}/scorecards`, {
        name: "Grid",
        gameId: gameId,
        fromTemplateId: templateId,
      });
      toast.success("Grid created from template!");
      fetchData();
    } catch (error) {
      console.error("Failed to create grid from template:", error);
      toast.error(error.response?.data?.detail || "Failed to create grid from template");
    } finally {
      setAdoptingId(null);
    }
  };

  const handleAdoptMap = async (mapId) => {
    setAdoptingId(mapId);
    try {
      const res = await axios.post(`${API}/community/maps/${mapId}/adopt`);
      if (res.data?.success) {
        toast.success(res.data.message || "Map adopted!");
        fetchData();
        fetchCommunityContent();
      } else {
        toast.error(res.data?.message || "Could not adopt map");
      }
    } catch (error) {
      console.error("Failed to adopt map:", error);
      toast.error(error.response?.data?.detail || "Failed to adopt map");
    } finally {
      setAdoptingId(null);
    }
  };

  const handleLikeMap = async (mapId) => {
    try {
      const res = await axios.post(`${API}/community/maps/${mapId}/like`);
      setCommunityMaps(prev => prev.map(m =>
        m.id === mapId ? { ...m, isLikedByUser: res.data.liked, likesCount: res.data.likesCount } : m
      ));
    } catch (error) {
      console.error("Failed to like map:", error);
      toast.error("Failed to update like");
    }
  };

  // ============ BULK MOVE HANDLERS ============
  const toggleAchievementSelection = (achievementId) => {
    setSelectedAchievements(prev => 
      prev.includes(achievementId) 
        ? prev.filter(id => id !== achievementId)
        : [...prev, achievementId]
    );
  };

  const handleBulkMove = async (targetType, targetId, parentSectionId = null) => {
    if (selectedAchievements.length === 0) {
      toast.error("No achievements selected");
      return;
    }
    try {
      await axios.post(`${API}/games/${gameId}/bulk-move`, {
        achievementIds: selectedAchievements,
        targetType,
        targetId,
        parentSectionId,
      });
      toast.success(`Moved ${selectedAchievements.length} achievements!`);
      setSelectedAchievements([]);
      setBulkMoveMode(false);
      setBulkMoveDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to bulk move:", error);
      toast.error("Failed to move achievements");
    }
  };

  // GameFAQs handlers
  const handleFetchFromGameFAQs = () => {
    // Pre-populate URL with game name if Xbox 360
    if (game?.system?.toLowerCase().includes("360")) {
      const slug = game.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      setGameFaqsUrl(`https://gamefaqs.gamespot.com/xbox360/XXXXX-${slug}/achievements`);
    }
    setGameFaqsDialogOpen(true);
  };

  const handleGameFaqsFetch = async () => {
    setGameFaqsLoading(true);
    setGameFaqsResults(null);
    try {
      let response;
      if (gameFaqsTab === "url") {
        response = await axios.post(`${API}/gamefaqs/fetch-achievements`, {
          gamefaqs_url: gameFaqsUrl
        });
      } else {
        response = await axios.post(`${API}/gamefaqs/parse-html`, {
          html_content: gameFaqsHtml
        });
      }
      setGameFaqsResults(response.data);
      if (response.data.count > 0) {
        toast.success(`Found ${response.data.count} achievements from GameFAQs`);
      }
    } catch (error) {
      console.error("GameFAQs fetch error:", error);
      toast.error(error.response?.data?.detail || "Failed to fetch from GameFAQs");
    } finally {
      setGameFaqsLoading(false);
    }
  };

  const handleApplyGameFAQs = async () => {
    if (!gameFaqsResults?.achievements || gameFaqsResults.achievements.length === 0) {
      toast.error("No achievements to apply");
      return;
    }
    
    setGameFaqsLoading(true);
    try {
      const response = await axios.post(`${API}/gamefaqs/apply-to-game/${gameId}`, {
        game_id: gameId,
        achievements: gameFaqsResults.achievements,
        update_images: true,
        update_descriptions: true,
        add_missing: true
      });
      
      const { updatedCount, addedCount } = response.data;
      let message = [];
      if (updatedCount > 0) message.push(`Updated ${updatedCount} achievements`);
      if (addedCount > 0) message.push(`Added ${addedCount} new achievements`);
      
      if (message.length > 0) {
        toast.success(message.join(", "));
      } else {
        toast.info("No changes made - all achievements already up to date");
      }
      
      setGameFaqsDialogOpen(false);
      setGameFaqsResults(null);
      setGameFaqsUrl("");
      setGameFaqsHtml("");
      fetchData();
    } catch (error) {
      console.error("GameFAQs apply error:", error);
      toast.error(error.response?.data?.detail || "Failed to apply GameFAQs data");
    } finally {
      setGameFaqsLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Effective unlock timestamp (ms) for date sorting. Falls back to the
  // earliest per-character unlock date when the top-level earnedAt is missing
  // or a placeholder ("1753..."/"0001..."), so per-character-unlocked
  // achievements order by their actual date instead of sinking to the back.
  const getEffectiveEarnedTime = (ach) => {
    const isPlaceholder = (d) => typeof d === "string" && (d.startsWith("1753") || d.startsWith("0001"));
    const ea = ach.earnedAt;
    if (ea && !isPlaceholder(ea)) {
      const t = new Date(ea).getTime();
      if (!isNaN(t)) return t;
    }
    const times = (ach.perCharacterUnlocks || [])
      .map((u) => u && u.unlockedAt)
      .filter(Boolean)
      .map((d) => new Date(d).getTime())
      .filter((t) => !isNaN(t));
    return times.length ? Math.min(...times) : null;
  };

  // Sort achievements using current sortBy/sortOrder settings with natural number sorting
  const sortAchievements = (achs) => {
    if (!achs || achs.length === 0) return achs;
    const sorted = [...achs].sort((a, b) => {
      let comparison = 0;
      const nameA = (a.name || "").trim();
      const nameB = (b.name || "").trim();
      switch (sortBy) {
        case "name":
          comparison = nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
          break;
        case "score":
          comparison = (a.score || 0) - (b.score || 0);
          if (comparison === 0) comparison = nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
          break;
        case "isUnlocked":
          comparison = (a.isUnlocked ? 1 : 0) - (b.isUnlocked ? 1 : 0);
          if (comparison === 0) comparison = nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
          break;
        case "rarity":
          comparison = (a.rarity || 999) - (b.rarity || 999);
          if (comparison === 0) comparison = nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
          break;
        case "earnedAt":
          const dateA = getEffectiveEarnedTime(a);
          const dateB = getEffectiveEarnedTime(b);
          comparison = (dateA !== null ? dateA : 0) - (dateB !== null ? dateB : 0);
          if (comparison === 0) comparison = nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
          break;
        default:
          // Default: use natural name sort
          comparison = nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });
    return sorted;
  };

  // Render a single achievement item using the AchievementCard component
  // showDlcTag: whether to show DLC source tag (used in organizer view for DLC achievements)
  // dlcName: the name of the DLC (if applicable)
  const renderAchievement = (achievement, { showDlcTag = false, dlcName = null } = {}) => {
    const masterAchId = achievement.masterAchievementId || achievement.id;
    const achFacts = factsByAchievement[masterAchId] || [];
    const hasMasterGame = !!game?.masterGameId;
    
    // Determine DLC name from achievement if not passed
    const effectiveDlcName = dlcName || achievement.dlcName || null;
    const shouldShowDlcTag = showDlcTag && effectiveDlcName;
    
    // List view - compact single row
    if (achievementViewMode === "list") {
      const achMilestones = milestoneMap[achievement.id] || null;
      return (
        <div 
          key={achievement.id} 
          className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${
            achievement.isUnlocked 
              ? "bg-green-500/5 border-green-500/20" 
              : "bg-[#0A0A0A] border-white/5 hover:border-white/10"
          }`}
          data-testid={`ach-list-${achievement.id}`}
        >
          {bulkMoveMode && (
            <button 
              onClick={() => toggleAchievementSelection(achievement.id)}
              className="flex-shrink-0"
            >
              {selectedAchievements.includes(achievement.id) ? (
                <CheckSquare className="w-4 h-4 text-blue-400" />
              ) : (
                <Square className="w-4 h-4 text-white/40 hover:text-white/60" />
              )}
            </button>
          )}
          
          {/* Image */}
          <div className="w-10 h-10 flex-shrink-0 rounded overflow-hidden bg-black/50">
            {achievement.imageUrl ? (
              <AchievementImage
                src={achievement.imageUrl}
                additionalImages={achievement.additionalImages}
                alt=""
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Trophy className="w-4 h-4 text-white/20" />
              </div>
            )}
          </div>
          
          {/* Name & Description */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`font-medium truncate ${achievement.isUnlocked ? "text-green-300" : "text-white"}`}>
                {achievement.name}
              </span>
              {achievement.isSecret && (
                <span className="text-[10px] px-1 py-0.5 rounded bg-gray-500/20 text-gray-400">Secret</span>
              )}
              {shouldShowDlcTag && (
                <span className="text-[10px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-400">{effectiveDlcName}</span>
              )}
              {achMilestones && <MilestoneBadge milestones={achMilestones} />}
            </div>
            {achievement.description && (
              <p className="text-xs text-white/40 break-words">{achievement.description}</p>
            )}
          </div>
          
          {/* Score */}
          <div className="flex-shrink-0 text-yellow-400 font-semibold text-sm">
            {achievement.score}{getScoreAbbr(game?.platform)}
          </div>
          
          {/* Unlock toggle */}
          {(() => {
            const isVerifiedLock = achievement.isUnlocked && (achievement.verificationStatus === "verified" || achievement.verificationStatus === "secondary_verified");
            return (
              <button
                onClick={() => !isVerifiedLock && handleToggleUnlock(achievement)}
                className={`flex-shrink-0 p-1.5 rounded transition-colors ${
                  isVerifiedLock
                    ? "bg-green-500/20 text-green-400 cursor-not-allowed opacity-60"
                    : achievement.isUnlocked 
                      ? "bg-green-500/20 text-green-400 hover:bg-green-500/30" 
                      : "bg-white/5 text-white/40 hover:bg-white/10 hover:text-white"
                }`}
                title={isVerifiedLock ? "Verified — cannot be locked" : achievement.isUnlocked ? "Mark as locked" : "Mark as unlocked"}
                data-testid={`toggle-unlock-list-${achievement.id}`}
              >
                {achievement.isUnlocked ? <Check className="w-4 h-4" /> : <Trophy className="w-4 h-4" />}
              </button>
            );
          })()}
        </div>
      );
    }
    
    // Grid view - original card layout
    return (
      <div key={achievement.id} className="flex items-start gap-2">
        {bulkMoveMode && (
          <button 
            onClick={() => toggleAchievementSelection(achievement.id)}
            className="mt-3 flex-shrink-0"
          >
            {selectedAchievements.includes(achievement.id) ? (
              <CheckSquare className="w-5 h-5 text-blue-400" />
            ) : (
              <Square className="w-5 h-5 text-white/40 hover:text-white/60" />
            )}
          </button>
        )}
        <div className="flex-1">
          <AchievementCard
            achievement={achievement}
            game={game}
            tags={tags}
            scoreAbbr={getScoreAbbr(game?.platform)}
            userPrefs={userPrefs}
            characterNotes={(game?.notes || []).filter((n) => n.noteType === "character")}
            onUpdatePerCharacter={handleUpdatePerCharacter}
            onSetCharacterUnlock={handleSetCharacterUnlock}
            onRemoveCharacterUnlock={handleRemoveCharacterUnlock}
            onToggleUnlock={handleToggleUnlock}
            onEditTimestamp={(ach) => {
              setEditingAchievement({
                ...ach,
                earnedAt: ach.earnedAt ? new Date(ach.earnedAt) : new Date(),
                earnedTime: ach.earnedTime || "",
                unlockedOffline: ach.unlockedOffline || false,
              });
              setTimestampDialogOpen(true);
            }}
            onEdit={(ach) => {
              setEditingAchievement({ ...ach });
              setEditDialogOpen(true);
            }}
            onDelete={handleDeleteAchievement}
            facts={achFacts}
            factCount={achFacts.length}
            showFacts={showAchievementFacts}
            hasMasterGame={hasMasterGame}
            onFactsClick={handleFactsClick}
            showDlcTag={shouldShowDlcTag}
            dlcName={effectiveDlcName}
            noteCount={achievementNoteCounts[achievement.id] || 0}
            milestones={milestoneMap[achievement.id] || null}
            onPlannerChange={() => {
              setPlannerRefreshKey(k => k + 1);
              // Also refresh note counts to update planner count badge
              if (gameId) {
                axios.get(`${API}/achievements/note-counts/${gameId}`).then(res => setAchievementNoteCounts(res.data || {})).catch(() => {});
              }
            }}
          />
        </div>
      </div>
    );
  };

  // Stats calculations
  const totalAchievements = achievements.length;
  const unlockedAchievements = achievements.filter((a) => a.isUnlocked).length;
  const completionPct = totalAchievements > 0 ? Math.round((unlockedAchievements / totalAchievements) * 100) : 0;
  const totalScore = achievements.reduce((sum, a) => sum + (a.score || 0), 0);
  const earnedScore = achievements.filter((a) => a.isUnlocked).reduce((sum, a) => sum + (a.score || 0), 0);
  const scorePct = totalScore > 0 ? Math.round((earnedScore / totalScore) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="text-center py-12 space-y-4">
        <p className="text-white/50 text-lg">Game not found</p>
        <p className="text-white/30 text-sm max-w-md mx-auto">
          This game might be orphaned (missing ownership data) or belong to a different account.
        </p>
        <div className="flex justify-center gap-3 mt-6">
          <Button onClick={() => navigate(-1)} variant="outline">
            Go Back
          </Button>
          <Button 
            onClick={async () => {
              try {
                const response = await axios.get(`${API}/games/diagnose/${gameId}`);
                const diag = response.data;
                if (diag.issue === "orphaned" && diag.can_repair) {
                  // Smart repair (option C): if there's a suggested parent the
                  // current user owns, show the choice dialog so they can
                  // one-click "Claim DLC under <parent>" OR open the relink
                  // dialog for full control.
                  setRepairChoice({
                    game: diag.game,
                    suggested_parent: diag.suggested_parent,
                    is_dlc: diag.is_dlc,
                  });
                } else if (diag.found && !diag.issue) {
                  toast.info("Game data looks correct. Try refreshing the page.");
                  window.location.reload();
                } else if (diag.issue === "wrong_owner") {
                  toast.error(diag.message || "This game belongs to a different account");
                } else {
                  // Game record doesn't exist — check if there are orphaned
                  // achievements pointing at it and offer to relink them to a
                  // real game in the user's library.
                  const orphanRes = await axios.get(`${API}/games/orphan-achievements/${gameId}`);
                  const info = orphanRes.data;
                  if (!info.orphan_count) {
                    toast.error(diag.message || "Unable to diagnose issue");
                    return;
                  }
                  setOrphanInfo(info);
                  setOrphanDialogOpen(true);
                }
              } catch (error) {
                console.error("Diagnose failed:", error);
                toast.error(error.response?.data?.detail || "Failed to diagnose game");
              }
            }}
            className="bg-amber-600 hover:bg-amber-500"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Diagnose & Repair
          </Button>
          <Button 
            onClick={async () => {
              if (confirm("Are you sure you want to force delete this game? This cannot be undone.")) {
                try {
                  await axios.delete(`${API}/games/force-delete/${gameId}`);
                  toast.success("Game deleted");
                  navigate("/games");
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Failed to delete game");
                }
              }
            }}
            variant="destructive"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Force Delete
          </Button>
        </div>

        {/* Smart Repair Choice Dialog (option C) — shown when the game record
            exists but is orphaned (user_id = null). If the orphan is a DLC
            whose parent the current user owns, offer one-click repair.
            Otherwise (or via the "Choose a different target" link) open the
            full Orphan Relink dialog. */}
        <Dialog open={!!repairChoice} onOpenChange={(o) => !o && setRepairChoice(null)}>
          <DialogContent
            data-testid="orphan-repair-choice-dialog"
            className="bg-[#0f0f0f] border-amber-500/30 max-w-lg"
          >
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Wrench className="w-5 h-5 text-amber-400" />
                Repair "{repairChoice?.game?.name}"
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <p className="text-white/70">
                This {repairChoice?.is_dlc ? "DLC" : "game"} record exists in the database but isn't linked to any account.
              </p>

              {repairChoice?.suggested_parent?.owned_by_current_user ? (
                <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center gap-3">
                  {repairChoice.suggested_parent.boxArtUrl && (
                    <img
                      src={repairChoice.suggested_parent.boxArtUrl}
                      alt=""
                      className="w-12 h-16 object-cover rounded flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-emerald-300/80">Recommended</div>
                    <div className="text-white truncate">Claim under {repairChoice.suggested_parent.name}</div>
                    <div className="text-white/40 text-xs">
                      {repairChoice.suggested_parent.platform || "Unknown platform"} · already in your library
                    </div>
                  </div>
                </div>
              ) : repairChoice?.suggested_parent ? (
                <div className="rounded border border-white/10 bg-white/[0.02] p-3 text-xs text-white/60">
                  Linked to <span className="text-white">{repairChoice.suggested_parent.name}</span>, but that base game isn't in your library.
                  Use "Choose a different target" below to pick where this {repairChoice?.is_dlc ? "DLC's achievements" : "game's achievements"} should live.
                </div>
              ) : (
                <div className="rounded border border-white/10 bg-white/[0.02] p-3 text-xs text-white/60">
                  No parent base game is referenced. You can either claim this record as a standalone entry
                  or pick a different target to merge its achievements into.
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                {repairChoice?.suggested_parent?.owned_by_current_user && (
                  <Button
                    disabled={repairBusy}
                    onClick={async () => {
                      setRepairBusy(true);
                      try {
                        const r = await axios.post(`${API}/games/repair/${gameId}`);
                        toast.success(r.data?.message || "Repaired");
                        setRepairChoice(null);
                        window.location.reload();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to repair");
                      } finally {
                        setRepairBusy(false);
                      }
                    }}
                    className="bg-emerald-600 hover:bg-emerald-500"
                    data-testid="repair-claim-under-parent"
                  >
                    <Wrench className="w-4 h-4 mr-2" />
                    Claim under {repairChoice.suggested_parent.name}
                  </Button>
                )}

                {!repairChoice?.suggested_parent?.owned_by_current_user && (
                  <Button
                    disabled={repairBusy}
                    onClick={async () => {
                      // No good parent; offer plain "claim ownership" as a standalone repair
                      setRepairBusy(true);
                      try {
                        const r = await axios.post(`${API}/games/repair/${gameId}`);
                        toast.success(r.data?.message || "Repaired");
                        setRepairChoice(null);
                        window.location.reload();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to repair");
                      } finally {
                        setRepairBusy(false);
                      }
                    }}
                    className="bg-amber-600 hover:bg-amber-500"
                    data-testid="repair-claim-standalone"
                  >
                    <Wrench className="w-4 h-4 mr-2" />
                    Claim as standalone
                  </Button>
                )}

                <Button
                  variant="outline"
                  disabled={repairBusy}
                  className="bg-white/5 border-white/10 text-white/80"
                  data-testid="repair-choose-target"
                  onClick={async () => {
                    // Escape hatch → open the full Orphan Relink dialog
                    try {
                      const orphanRes = await axios.get(`${API}/games/orphan-achievements/${gameId}`);
                      const info = orphanRes.data;
                      setRepairChoice(null);
                      if (!info.orphan_count) {
                        toast.info("This record has no achievements to re-link.");
                        return;
                      }
                      setOrphanInfo(info);
                      setOrphanDialogOpen(true);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to load candidates");
                    }
                  }}
                >
                  Choose a different target…
                </Button>

                <Button
                  variant="ghost"
                  disabled={repairBusy}
                  className="text-white/60"
                  onClick={() => setRepairChoice(null)}
                  data-testid="repair-cancel"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Orphan Relink Dialog — opened from Diagnose & Repair when achievements
            reference a gameId whose game record no longer exists. */}
        <Dialog open={orphanDialogOpen} onOpenChange={setOrphanDialogOpen}>
          <DialogContent
            data-testid="orphan-relink-dialog"
            className="bg-[#0f0f0f] border-amber-500/30 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
          >
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Orphaned Achievements Found
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-3 text-sm">
              <p className="text-white/70">
                <span className="font-medium text-white">{orphanInfo?.orphan_count}</span> achievement{orphanInfo?.orphan_count === 1 ? "" : "s"} reference a game ID
                that no longer exists in the database. Pick the correct game from your library to re-link them.
              </p>
              {orphanInfo?.achievements?.length > 0 && (
                <details className="bg-black/30 rounded p-2">
                  <summary className="text-white/60 cursor-pointer text-xs">
                    Show {Math.min(orphanInfo.achievements.length, 200)} orphan achievement name{orphanInfo.achievements.length === 1 ? "" : "s"}
                  </summary>
                  <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto text-xs text-white/60">
                    {orphanInfo.achievements.map(a => (
                      <li key={a.id} className="truncate">• {a.name}</li>
                    ))}
                  </ul>
                </details>
              )}
              <div>
                <Label className="text-white/80 text-xs mb-1 block">Suggested target games {orphanInfo?.candidates?.length ? `(${orphanInfo.candidates.length})` : ""}</Label>
                {orphanInfo?.candidates?.length ? (
                  <div className="space-y-1.5 max-h-72 overflow-y-auto">
                    {orphanInfo.candidates.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`orphan-target-${c.id}`}
                        disabled={orphanRelinking}
                        onClick={async () => {
                          setOrphanRelinking(true);
                          try {
                            const r = await axios.post(`${API}/games/relink-orphan-achievements`, {
                              source_game_id: gameId,
                              target_game_id: c.id,
                            });
                            toast.success(`Moved ${r.data.moved} achievement${r.data.moved === 1 ? "" : "s"} to "${r.data.target_game_name}"`);
                            setOrphanDialogOpen(false);
                            navigate(`/games/${c.id}`);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to relink achievements");
                          } finally {
                            setOrphanRelinking(false);
                          }
                        }}
                        className="w-full flex items-center gap-3 p-2 rounded border border-white/10 bg-white/[0.02] hover:border-amber-500/30 hover:bg-white/[0.05] disabled:opacity-50 text-left"
                      >
                        {c.boxArtUrl && (
                          <img src={c.boxArtUrl} alt="" className="w-10 h-14 object-cover rounded flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-white truncate flex items-center gap-2">
                            <span className="truncate">{c.name}</span>
                            {c.isDLC && (
                              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30 flex-shrink-0">
                                DLC
                              </span>
                            )}
                          </div>
                          <div className="text-white/40 text-xs flex items-center gap-2">
                            <span>{c.platform || "Unknown platform"}</span>
                            {c.match_count > 0 && (
                              <span className="text-green-300">
                                {c.match_count} achievement-ID match{c.match_count === 1 ? "" : "es"}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/40 text-xs italic">
                    No likely matches in your library. You can still pick any game below.
                  </p>
                )}
              </div>
              <div className="pt-2 border-t border-white/10">
                <p className="text-white/40 text-[11px] mb-2">Or search your entire library:</p>
                <input
                  type="text"
                  placeholder="Type a game name and press Enter…"
                  data-testid="orphan-manual-search"
                  className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm text-white placeholder-white/30"
                  onKeyDown={async (e) => {
                    if (e.key !== "Enter" || !e.target.value.trim()) return;
                    try {
                      const r = await axios.get(`${API}/games`, { params: { search: e.target.value.trim(), limit: 20 } });
                      const games = (r.data?.games || r.data || []).filter(g => !g.isDLC);
                      if (games.length === 0) {
                        toast.info("No games matched");
                      } else {
                        setOrphanInfo({ ...orphanInfo, candidates: games.map(g => ({ ...g, match_count: 0 })) });
                      }
                    } catch {
                      toast.error("Search failed");
                    }
                  }}
                />
              </div>
            </div>
            <div className="pt-3 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setOrphanDialogOpen(false)}
                disabled={orphanRelinking}
                className="bg-white/5 border-white/10 text-white/70"
                data-testid="orphan-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                disabled={orphanRelinking}
                className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                data-testid="orphan-delete-all-btn"
                onClick={async () => {
                  if (!confirm(`Delete all ${orphanInfo?.orphan_count} orphan achievement(s)? This cannot be undone.`)) return;
                  setOrphanRelinking(true);
                  try {
                    const res = await axios.delete(`${API}/games/orphan-achievements/${gameId}`);
                    toast.success(`Deleted ${res.data?.deleted ?? orphanInfo?.orphan_count} orphan achievement(s)`);
                    setOrphanDialogOpen(false);
                    navigate("/games");
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to delete");
                  } finally {
                    setOrphanRelinking(false);
                  }
                }}
              >
                Delete orphans instead
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div data-testid="game-detail-page" className="space-y-6">
      {/* Achievement Celebration Overlay */}
      {celebrationAchievements.length > 0 && (
        <AchievementCelebration
          achievements={celebrationAchievements}
          onClose={() => setCelebrationAchievements([])}
        />
      )}
      {/* Back Button */}
      <Button
        data-testid="back-to-games-btn"
        variant="ghost"
        onClick={() => navigate(-1)}
        className="text-white/60 hover:text-white"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      {/* Two-column layout: Game Info on left, Content on right (on large screens) */}
      <div className="flex flex-col xl:flex-row gap-6">
        {/* Left Column: Game Header - flexible with min/max width */}
        <div className="xl:min-w-[500px] xl:max-w-[600px] xl:flex-shrink-0 overflow-hidden">
          <GameHeaderNew
            game={game}
            platforms={platforms}
            dlcs={dlcs}
            availableGameTags={availableGameTags}
            achievements={achievements}
            groupedAchievements={groupedAchievements}
            sessionTimeHours={sessionTimeHours}
            systemsLookup={systemsLookup}
            masterGenres={masterGenres}
            // Handlers
            onToggleOwned={handleToggleOwned}
            onToggleNoAchievements={handleToggleNoAchievements}
            onTogglePriority={handleTogglePriority}
            onToggleBacklog={handleToggleBacklog}
            onToggleHidden={handleToggleHidden}
            onToggleManualComplete={handleToggleManualComplete}
            onToggleIncompleteSync={handleToggleIncompleteSync}
            onResolveIncompleteSync={handleResolveIncompleteSync}
            onRemoveCopyNumber={handleRemoveCopyNumber}
            onEditInfo={() => setGameInfoDialogOpen(true)}
            onEditTags={openEditTagsDialog}
            onRefreshMetadata={() => {
              setRefreshSearch((game?.name || "").replace(/[™®℠]/g, "").trim());
              setRefreshDialogOpen(true);
            }}
            onGetCoverArt={openCoverDialog}
            onSyncAchievements={handleSyncAchievements}
            onManualSync={handleOpenManualSync}
            onToggleSyncDisabled={handleToggleSyncDisabled}
            onExophaseVerify={() => setExophaseVerifyOpen(true)}
            chatlogImportEnabled={!!game?.chatlogImportEnabled}
            onOpenChatlogImport={() => setChatlogImportOpen(true)}
            onDelete={handleDeleteGame}
            onAddGameTag={handleAddGameTag}
            onRemoveGameTag={handleRemoveGameTag}
            onAddNote={handleAddNote}
            onEditNote={handleUpdateNote}
            onDeleteNote={handleDeleteNote}
            onToggleChecklistItem={handleToggleChecklistItem}
            onShareChecklist={handleShareChecklist}
            onEditChecklist={handleEditChecklist}
            onAddChecklist={handleAddChecklist}
            onReorderNotes={handleReorderNotes}
            checklistTemplates={checklistTemplates}
            onAddLink={handleAddLink}
            onEditLink={handleEditLink}
            onDeleteLink={handleDeleteLink}
            onReorderLinks={handleReorderLinks}
            onReorderContent={handleReorderContent}
            // Map handlers
            onAddMap={handleAddMap}
            onEditMap={handleEditMap}
            onToggleMapPin={handleToggleMapPin}
            onSubmitMapToCommunity={handleOpenSubmitMapDialog}
            onToggleNotePlanner={handleToggleNotePlanner}
            onAddCharacter={() => { setEditingCharacter(null); setCharacterEditorOpen(true); }}
            onEditCharacter={(n) => { setEditingCharacter(n); setCharacterEditorOpen(true); }}
            onUpdateCompletionEstimate={handleUpdateCompletionEstimate}
            onLinkPlayStation={() => {
              setPsnTitleSearch(game?.name || "");
              setPsnTitleSearchOpen(true);
              handleSearchPsnTitles(game?.name || "");
            }}
            // Loading states
            togglingPriority={togglingPriority}
            syncing={syncing}
            refreshLoading={refreshLoading}
            canSync={canSync()}
          />
        </div>

        {/* Right Column: Content/DLC Section - takes remaining space */}
        <div className="flex-1">
          <Card className="bg-[#0A0A0A] border-white/5 h-fit">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Package className="w-5 h-5" />
            Content ({(groupedAchievements?.dlcs?.length || 0) + (groupedAchievements?.baseGame?.sections?.length || 0) + 2} items)
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Browse Community Button */}
            <Button
              size="sm"
              variant="outline"
              className="border-green-500/30 text-green-400 hover:bg-green-500/10"
              onClick={() => {
                setCommunityDialogOpen(true);
                fetchCommunityContent();
              }}
              data-testid="browse-community-btn"
            >
              <Users className="w-4 h-4 mr-1" />
              Community
            </Button>
            
            {/* Share to Community Button */}
            {((groupedAchievements?.baseGame?.sections?.length || 0) > 0 || challenges.length > 0) && (
              <Button
                size="sm"
                variant="outline"
                className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                onClick={() => setShareDialogOpen(true)}
                data-testid="share-to-community-btn"
              >
                <Share2 className="w-4 h-4 mr-1" />
                Share
              </Button>
            )}
            
            <Button
              size="sm"
              variant="outline"
              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
              onClick={handleFetchIgdbDlcs}
              disabled={fetchingIgdbDlcs}
              data-testid="fetch-igdb-dlcs-btn"
            >
              {fetchingIgdbDlcs ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1" />
              )}
              Fetch from IGDB
            </Button>
            
            <AddDlcDialog
              open={dlcDialogOpen}
              onOpenChange={setDlcDialogOpen}
              dlc={newDlc}
              onDlcChange={setNewDlc}
              onSave={handleCreateDlc}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Base Game & DLC Group */}
          <div className="border border-white/10 rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedContentGroups(prev => ({ ...prev, baseGame: !prev.baseGame }))}
              className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-blue-900/30 to-purple-900/20 hover:from-blue-900/40 hover:to-purple-900/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Gamepad2 className="w-4 h-4 text-blue-400" />
                <span className="text-white font-medium">Base Game & DLC</span>
                <span className="text-xs text-white/50">
                  ({1 + (groupedAchievements?.dlcs?.length || 0)} items)
                </span>
              </div>
              <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${expandedContentGroups.baseGame ? 'rotate-180' : ''}`} />
            </button>
            {expandedContentGroups.baseGame && (
              <div className="p-3 bg-black/20 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* Base Game Content Card */}
                <button
                  onClick={() => {
                    document.getElementById('section-base')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    setExpandedSections(prev => ({ ...prev, base: true }));
                    // Jump the paginated achievement list to the Base Game page.
                    setJumpToGroupId("base");
                  }}
                  className="flex items-center gap-3 p-3 bg-[#121212] rounded-lg border border-white/10 hover:border-white/30 hover:bg-[#181818] transition-all text-left group"
                  data-testid="content-base-game"
                >
                  {game?.boxArtUrl ? (
                    <img src={game.boxArtUrl} alt={game?.name} className="w-10 h-10 rounded object-contain bg-black/50 flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Gamepad2 className="w-5 h-5 text-white/60" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate text-sm group-hover:text-blue-400 transition-colors">Base Game</p>
                    <div className="flex items-center gap-2 text-xs text-white/50">
                      {/* Total count includes unsectioned + sectioned achievements */}
                      <span>
                        {(groupedAchievements?.baseGame?.achievements || []).length + 
                         (groupedAchievements?.baseGame?.sections || []).reduce((sum, s) => sum + (s.achievements || []).length, 0)} ach
                      </span>
                      {/* Total score includes unsectioned + sectioned achievements */}
                      <span className="text-yellow-400">
                        {(groupedAchievements?.baseGame?.achievements || []).reduce((sum, a) => sum + (a.score || 0), 0) +
                         (groupedAchievements?.baseGame?.sections || []).flatMap(s => s.achievements || []).reduce((sum, a) => sum + (a.score || 0), 0)} {getScoreAbbr(game?.platform)}
                      </span>
                    </div>
                    <div className="mt-1 h-1 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all"
                        style={{ 
                          width: `${(() => {
                            const allAchs = [
                              ...(groupedAchievements?.baseGame?.achievements || []),
                              ...(groupedAchievements?.baseGame?.sections || []).flatMap(s => s.achievements || [])
                            ];
                            const total = allAchs.length;
                            const unlocked = allAchs.filter(a => a.isUnlocked).length;
                            return total > 0 ? (unlocked / total) * 100 : 0;
                          })()}%` 
                        }}
                      />
                    </div>
                  </div>
                </button>
                
                {/* DLC Cards - in Base Game & DLC group */}
                {(groupedAchievements?.dlcs || []).map((dlc) => {
                  const dlcScore = (dlc.achievements || []).reduce((sum, a) => sum + (a.score || 0), 0);
                  const dlcInfo = dlcs.find(d => d.id === dlc.id);
                  const progressPct = dlc.totalAchievements > 0 ? (dlc.unlockedAchievements / dlc.totalAchievements) * 100 : 0;
                  const releaseDate = dlc.releaseDate || dlcInfo?.releaseDate;
                  
                  return (
                    <div key={dlc.id} className="relative group">
                      <button
                        onClick={() => {
                          // Ensure the Base Game achievement panel is open so
                          // the paginated list is visible, then jump the list
                          // to this DLC's first page.
                          setExpandedSections(prev => ({ ...prev, base: true }));
                          document.getElementById('section-base')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          setJumpToGroupId(dlc.id);
                        }}
                        className="w-full flex items-center gap-3 p-3 bg-[#121212] rounded-lg border border-white/10 hover:border-purple-500/50 hover:bg-[#181818] transition-all text-left"
                        data-testid={`content-dlc-${dlc.id}`}
                      >
                        {dlc.boxArtUrl || dlcInfo?.boxArtUrl ? (
                          <img 
                            src={dlc.boxArtUrl || dlcInfo?.boxArtUrl} 
                            alt={dlc.name} 
                            className="w-10 h-10 rounded object-contain bg-black/50 flex-shrink-0"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center flex-shrink-0">
                            <Package className="w-5 h-5 text-purple-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate text-sm group-hover:text-purple-400 transition-colors">{dlc.name}</p>
                          <div className="flex items-center gap-2 text-xs text-white/50">
                            <span>{dlc.totalAchievements || 0} ach</span>
                            <span className="text-yellow-400">{dlcScore} {getScoreAbbr(game?.platform)}</span>
                            {dlc.completionEstimate && (
                              <span className="text-cyan-400/70">{dlc.completionEstimate}</span>
                            )}
                          </div>
                          <div className="mt-1 h-1 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-500 to-pink-400 transition-all"
                              style={{ width: `${progressPct}%` }}
                            />
                          </div>
                        </div>
                      </button>
                      {/* Edit/Delete buttons */}
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-5 w-5 p-0 text-white/40 hover:text-white bg-black/50"
                          onClick={(e) => { e.stopPropagation(); setEditingDlcId(dlc.id); }}
                          title="Edit release date"
                        >
                          <Calendar className="w-3 h-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-5 w-5 p-0 text-white/40 hover:text-white bg-black/50"
                          onClick={(e) => { e.stopPropagation(); setEditingDlc({ ...dlcInfo }); }}
                          title="Edit DLC"
                          data-testid={`edit-dlc-btn-${dlc.id}`}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-5 w-5 p-0 text-red-400 hover:text-red-300 bg-black/50"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-white">Delete DLC?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will delete &quot;{dlc.name}&quot; and all its achievements. This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-transparent border-white/10">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteDlc(dlc.id)}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                      {/* Edit Release Date inline */}
                      {editingDlcId === dlc.id && (
                        <div 
                          className="absolute inset-0 bg-[#0A0A0A]/95 rounded-lg p-3 flex flex-col justify-center z-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <p className="text-xs text-white/60 mb-2">Release Date</p>
                          <Input
                            type="date"
                            defaultValue={releaseDate || ""}
                            className="bg-black/50 border-white/10 text-sm mb-2"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleUpdateDlcReleaseDate(dlc.id, e.target.value);
                              } else if (e.key === 'Escape') {
                                setEditingDlcId(null);
                              }
                            }}
                            onBlur={(e) => handleUpdateDlcReleaseDate(dlc.id, e.target.value)}
                          />
                          <p className="text-[10px] text-white/40">Press Enter to save, Esc to cancel</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Organizers Group */}
          <div className="border border-white/10 rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedContentGroups(prev => ({ ...prev, dlcs: !prev.dlcs }))}
              className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-cyan-900/30 to-teal-900/20 hover:from-cyan-900/40 hover:to-teal-900/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-cyan-400" />
                <span className="text-white font-medium">Organizers</span>
                <span className="text-xs text-white/50">
                  ({groupedAchievements?.baseGame?.sections?.length || 0} items)
                </span>
              </div>
              <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${expandedContentGroups.dlcs ? 'rotate-180' : ''}`} />
            </button>
            {expandedContentGroups.dlcs && (
              <div className="p-3 bg-black/20">
                {(groupedAchievements?.baseGame?.sections || []).length === 0 ? (
                  <p className="text-white/40 text-sm text-center py-4">No organizers created yet</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[...(groupedAchievements?.baseGame?.sections || [])].sort((a, b) => naturalCompare(a.name, b.name)).map((section) => {
                      // Include both base game section achievements AND DLC achievements in this organizer
                      const baseAchs = section.achievements || [];
                      const dlcAchs = (groupedAchievements?.dlcs || []).flatMap(dlc => 
                        (dlc.achievements || []).filter(a => a.sectionId === section.id)
                      );
                      const allSectionAchs = [...baseAchs, ...dlcAchs];
                      const sectionScore = allSectionAchs.reduce((sum, a) => sum + (a.score || 0), 0);
                      const totalCount = allSectionAchs.length;
                      const unlockedCount = allSectionAchs.filter(a => a.isUnlocked).length;
                      const progressPct = totalCount > 0 ? (unlockedCount / totalCount) * 100 : 0;
                      
                      return (
                        <button
                          key={section.id}
                          onClick={() => {
                            // Turn Organizer View ON, activate this organizer's
                            // page, and scroll to the base game panel where
                            // organizers now render.
                            setShowOrganizerView(true);
                            setActiveOrganizerId(section.id);
                            setExpandedSections(prev => ({ ...prev, base: true }));
                            requestAnimationFrame(() => {
                              document.getElementById(`section-section-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                ?? document.getElementById('section-base')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            });
                          }}
                          className="flex items-center gap-3 p-3 bg-[#121212] rounded-lg border border-cyan-500/30 hover:border-cyan-400/50 hover:bg-[#181818] transition-all text-left group"
                          data-testid={`content-section-${section.id}`}
                        >
                          <div className="w-10 h-10 rounded bg-gradient-to-br from-cyan-500/20 to-teal-500/20 flex items-center justify-center flex-shrink-0">
                            <Layers className="w-5 h-5 text-cyan-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate text-sm group-hover:text-cyan-400 transition-colors">{section.name}</p>
                            {section.description && (
                              <p className="text-white/30 text-xs truncate">{section.description}</p>
                            )}
                            <div className="flex items-center gap-2 text-xs text-white/50">
                              <span>{totalCount} ach</span>
                              <span className="text-yellow-400">{sectionScore} {getScoreAbbr(game?.platform)}</span>
                            </div>
                            <div className="mt-1 h-1 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-cyan-500 to-teal-400 transition-all"
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Challenges Group */}
          <div className="border border-white/10 rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedContentGroups(prev => ({ ...prev, challenges: !prev.challenges }))}
              className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-orange-900/30 to-amber-900/20 hover:from-orange-900/40 hover:to-amber-900/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-orange-400" />
                <span className="text-white font-medium">Challenges</span>
                <span className="text-xs text-white/50">
                  ({challenges.length} items)
                </span>
              </div>
              <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${expandedContentGroups.challenges ? 'rotate-180' : ''}`} />
            </button>
            {expandedContentGroups.challenges && (
              <div className="p-3 bg-black/20">
                <button
                  onClick={() => {
                    document.getElementById('section-challenges')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    setExpandedSections(prev => ({ ...prev, challenges: true }));
                  }}
                  className="w-full flex items-center gap-3 p-3 bg-[#121212] rounded-lg border border-orange-500/30 hover:border-orange-400/50 hover:bg-[#181818] transition-all text-left group"
                  data-testid="content-challenges"
                >
                  <div className="w-10 h-10 rounded bg-gradient-to-br from-orange-500/30 to-yellow-500/30 flex items-center justify-center flex-shrink-0 ring-2 ring-orange-500/30">
                    <Target className="w-5 h-5 text-orange-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate text-sm group-hover:text-orange-400 transition-colors">All Challenges</p>
                    <div className="flex items-center gap-2 text-xs text-white/50">
                      <span>{challenges.length} challenge{challenges.length !== 1 ? 's' : ''}</span>
                      <span className="text-green-400">{challenges.filter(c => c.isCompleted).length} completed</span>
                    </div>
                    <div className="mt-1 h-1 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-orange-500 to-amber-400 transition-all"
                        style={{ 
                          width: `${challenges.length > 0 
                            ? (challenges.filter(c => c.isCompleted).length / challenges.length) * 100 
                            : 0}%` 
                        }}
                      />
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
          
          {/* My Planner Group */}
          <div className="border border-white/10 rounded-lg overflow-hidden" data-testid="content-planner-section">
            <button
              onClick={() => setExpandedContentGroups(prev => ({ ...prev, planner: !prev.planner }))}
              className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-cyan-900/30 to-teal-900/20 hover:from-cyan-900/40 hover:to-teal-900/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-cyan-400" />
                <span className="text-white font-medium">My Planner</span>
                {achievementNoteCounts.__plannerCount > 0 && (
                  <span className="text-xs text-cyan-400/80 bg-cyan-500/10 px-1.5 py-0.5 rounded">
                    {achievementNoteCounts.__plannerCount}
                  </span>
                )}
              </div>
              <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${expandedContentGroups.planner ? 'rotate-180' : ''}`} />
            </button>
            {expandedContentGroups.planner && (
              <div className="p-3 bg-black/20">
                <PlannerPanel gameId={gameId} refreshKey={plannerRefreshKey} />
              </div>
            )}
          </div>

          {/* Edit DLC Dialog */}
          <EditDlcDialog
            open={!!editingDlc}
            onOpenChange={(open) => { if (!open) setEditingDlc(null); }}
            dlc={editingDlc}
            onDlcChange={setEditingDlc}
            onSave={handleUpdateDlc}
            gameId={gameId}
          />
        </CardContent>
        </Card>
        </div>
      </div>

      {/* Full-width User Notes section — lives below the two-column header
          so notes/maps/checklists/grids/links can spread across the full
          page width instead of being constrained to the narrow left column. */}
      <GameContentSection
        game={game}
        onAddNote={handleAddNote}
        onEditNote={handleUpdateNote}
        onDeleteNote={handleDeleteNote}
        onToggleChecklistItem={handleToggleChecklistItem}
        onShareChecklist={handleShareChecklist}
        onEditChecklist={handleEditChecklist}
        onAddChecklist={handleAddChecklist}
        onSaveChecklistPrefs={handleSaveChecklistPrefs}
        checklistTemplates={checklistTemplates}
        onAddLink={handleAddLink}
        onEditLink={handleEditLink}
        onDeleteLink={handleDeleteLink}
        onAddMap={handleAddMap}
        onEditMap={handleEditMap}
        onToggleMapPin={handleToggleMapPin}
        onSubmitMapToCommunity={handleOpenSubmitMapDialog}
        onToggleNotePlanner={handleToggleNotePlanner}
        onAddCharacter={() => { setEditingCharacter(null); setCharacterEditorOpen(true); }}
        onEditCharacter={(n) => { setEditingCharacter(n); setCharacterEditorOpen(true); }}
        achievements={achievements}
        onReorderContent={handleReorderContent}
      />

      {/* Achievements Section - Grouped by Content */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            Achievements ({totalAchievements})
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Filters */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32 bg-black/50 border-white/10">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unlocked">Unlocked</SelectItem>
                <SelectItem value="locked">Locked</SelectItem>
              </SelectContent>
            </Select>

            {/* Character (unlocked-by) Filter — only when the game has character notes */}
            {(game?.notes || []).some((n) => n.noteType === "character") && (
              <Select value={characterFilter} onValueChange={setCharacterFilter}>
                <SelectTrigger className="w-44 bg-black/50 border-white/10" data-testid="character-filter-trigger">
                  <User className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Character" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="all">All Characters</SelectItem>
                  {(game?.notes || [])
                    .filter((n) => n.noteType === "character")
                    .sort((a, b) => (a.title || a.name || "").localeCompare(b.title || b.name || "", undefined, { sensitivity: "base", numeric: true }))
                    .map((n) => (
                      <SelectItem key={n.id} value={n.id} data-testid={`character-filter-${n.id}`}>
                        {n.title || n.name || "Unnamed"}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}

            {/* Rarity Filter */}
            <Select value={rarityFilter} onValueChange={setRarityFilter}>
              <SelectTrigger className="w-36 bg-black/50 border-white/10">
                <Gem className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Rarity" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="all">All Rarity</SelectItem>
                <SelectItem value="ultra-rare">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-500" />
                    Ultra Rare (0-5%)
                  </span>
                </SelectItem>
                <SelectItem value="rare">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-500" />
                    Rare (5-20%)
                  </span>
                </SelectItem>
                <SelectItem value="uncommon">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    Uncommon (20-50%)
                  </span>
                </SelectItem>
                <SelectItem value="common">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-gray-500" />
                    Common (50%+)
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-36 bg-black/50 border-white/10">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="createdAt">Date Added</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="score">Score</SelectItem>
                <SelectItem value="earnedAt">Unlock Date</SelectItem>
                <SelectItem value="rarity">Rarity</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort Order Toggle */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              className="bg-black/50 border-white/10 hover:bg-white/10"
              title={sortOrder === "asc" ? "Ascending (A→Z, Low→High)" : "Descending (Z→A, High→Low)"}
              data-testid="sort-order-toggle"
            >
              {sortOrder === "asc" ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
            </Button>

            {/* View Mode Toggle */}
            <div className="flex items-center rounded-lg border border-white/10 overflow-hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setAchievementViewMode("grid")}
                className={`rounded-none h-9 ${achievementViewMode === "grid" ? "bg-white/10" : "hover:bg-white/5"}`}
                title="Grid View"
                data-testid="view-mode-grid"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setAchievementViewMode("list")}
                className={`rounded-none h-9 ${achievementViewMode === "list" ? "bg-white/10" : "hover:bg-white/5"}`}
                title="List View"
                data-testid="view-mode-list"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>

            {/* Organizer View Toggle - only show if there are organizers/sections */}
            {(groupedAchievements?.baseGame?.sections?.length > 0) && (
              <div className="flex items-center gap-2 px-2 py-1 bg-black/30 rounded-lg border border-white/10">
                <Layers className="w-4 h-4 text-cyan-400" />
                <span className="text-xs text-white/60">Organizers</span>
                <Switch
                  checked={showOrganizerView}
                  onCheckedChange={setShowOrganizerView}
                  className="data-[state=checked]:bg-cyan-600"
                  data-testid="organizer-view-toggle"
                />
              </div>
            )}

            {/* Add Achievement */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-achievement-btn" className="bg-white text-black hover:bg-gray-200">
                  <Plus className="w-4 h-4 mr-1" />
                  ADD
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    Add Achievement
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  {/* DLC Selection */}
                  {dlcs.length > 0 && (
                    <div className="space-y-2">
                      <Label>Add to</Label>
                      <Select value={selectedDlcForAdd || "base"} onValueChange={(v) => setSelectedDlcForAdd(v === "base" ? null : v)}>
                        <SelectTrigger className="bg-black/50 border-white/10">
                          <SelectValue placeholder="Select content" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          <SelectItem value="base">Base Game</SelectItem>
                          {dlcs.map(dlc => (
                            <SelectItem key={dlc.id} value={dlc.id}>{dlc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      data-testid="achievement-name-input"
                      placeholder="Achievement name"
                      value={newAchievement.name}
                      onChange={(e) => setNewAchievement({ ...newAchievement, name: e.target.value })}
                      className="bg-black/50 border-white/10 focus:border-white/40"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      data-testid="achievement-desc-input"
                      placeholder="How to unlock this achievement"
                      value={newAchievement.description}
                      onChange={(e) => setNewAchievement({ ...newAchievement, description: e.target.value })}
                      className="bg-black/50 border-white/10 focus:border-white/40"
                    />
                  </div>

                  {/* Trophy Type for PlayStation */}
                  {game?.platform === "playstation" ? (
                    <div className="space-y-2">
                      <Label>Trophy Type</Label>
                      <Select
                        value={newAchievement.trophyType}
                        onValueChange={(v) => {
                          const trophy = TROPHY_TYPES.find(t => t.value === v);
                          setNewAchievement({ 
                            ...newAchievement, 
                            trophyType: v,
                            score: trophy?.score || 0 
                          });
                        }}
                      >
                        <SelectTrigger className="bg-black/50 border-white/10">
                          <SelectValue placeholder="Select trophy type" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          {TROPHY_TYPES.map((trophy) => (
                            <SelectItem key={trophy.value} value={trophy.value}>
                              <span style={{ color: trophy.color }}>●</span> {trophy.label} ({trophy.score} XP)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-white/50">Score will be set automatically based on trophy type</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Score</Label>
                      <Input
                        data-testid="achievement-score-input"
                        type="number"
                        placeholder="0"
                        value={newAchievement.score}
                        onChange={(e) => setNewAchievement({ ...newAchievement, score: parseInt(e.target.value) || 0 })}
                        className="bg-black/50 border-white/10 focus:border-white/40"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Image</Label>
                    <div className="flex gap-2 mb-2">
                      <Button
                        type="button"
                        variant={imageMode === "url" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setImageMode("url")}
                        className={imageMode === "url" ? "bg-white text-black" : "border-white/20"}
                      >
                        <LinkIcon className="w-4 h-4 mr-1" />
                        URL
                      </Button>
                      <Button
                        type="button"
                        variant={imageMode === "upload" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setImageMode("upload")}
                        className={imageMode === "upload" ? "bg-white text-black" : "border-white/20"}
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </Button>
                    </div>
                    {imageMode === "url" ? (
                      <Input
                        placeholder="https://example.com/image.jpg"
                        value={newAchievement.imageUrl}
                        onChange={(e) => setNewAchievement({ ...newAchievement, imageUrl: e.target.value })}
                        className="bg-black/50 border-white/10"
                      />
                    ) : (
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                        className="bg-black/50 border-white/10"
                      />
                    )}
                  </div>

                  {tags.length > 0 && (
                    <div className="space-y-2">
                      <Label>Tags</Label>
                      <div className="flex flex-wrap gap-2">
                        {tags
                          .filter((tag) => tag.name?.toLowerCase() !== "incomplete sync")
                          .sort((a, b) => naturalCompare(a?.name, b?.name))
                          .map((tag) => {
                            const color = getTagColor(tag);
                            return (
                              <button
                                key={tag.id}
                                type="button"
                                onClick={() => toggleTag(tag.id)}
                                className={`px-2 py-1 rounded text-xs transition-all ${
                                  newAchievement.tags.includes(tag.id)
                                    ? "ring-2 ring-white"
                                    : "opacity-60 hover:opacity-100"
                                }`}
                                style={{ backgroundColor: color }}
                              >
                                {tag.name}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  <Button
                    data-testid="save-achievement-btn"
                    onClick={handleCreateAchievement}
                    className="w-full bg-white text-black hover:bg-gray-200 font-bold uppercase tracking-wider"
                  >
                    Add Achievement
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Bulk Move Controls */}
          {bulkMoveMode && (
            <div className="flex items-center justify-between p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Move className="w-5 h-5 text-blue-400" />
                <span className="text-sm text-white">
                  {selectedAchievements.length} achievement(s) selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => setBulkMoveDialogOpen(true)}
                  disabled={selectedAchievements.length === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Move Selected
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setBulkMoveMode(false);
                    setSelectedAchievements([]);
                  }}
                  className="border-white/20"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Base Game Achievements */}
          <div id="section-base" className="border border-white/10 rounded-lg scroll-mt-4">
            <div className="flex items-center p-4 bg-[#121212]">
              <button
                onClick={() => toggleSection('base')}
                className="flex-1 flex items-center gap-4 hover:bg-[#181818] transition-colors -ml-4 -my-4 pl-4 py-4"
              >
                {game?.boxArtUrl ? (
                  <img src={game.boxArtUrl} alt={game?.name} className="w-14 h-14 rounded object-contain bg-black/50 hidden sm:block" />
                ) : (
                  <div className="w-14 h-14 rounded bg-gradient-to-br from-blue-500/20 to-purple-500/20 items-center justify-center hidden sm:flex">
                    <Gamepad2 className="w-7 h-7 text-white/60" />
                  </div>
                )}
                <div className="text-left flex-1">
                  <div className="flex items-center gap-2">
                    <Gamepad2 className="w-5 h-5 text-blue-400 sm:hidden" />
                    <span className="text-white font-medium">Base Game</span>
                    {(groupedAchievements?.baseGame?.sections?.length || 0) > 0 && (
                      <span className="text-xs px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">
                        {groupedAchievements.baseGame.sections.length} section(s)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-white/50 mt-1">
                    <span>
                      {/* Count ALL achievements in baseGame structure (unsectioned + all sections) */}
                      {(groupedAchievements?.baseGame?.achievements || []).length + 
                       (groupedAchievements?.baseGame?.sections || []).reduce((sum, s) => 
                         sum + (s.achievements || []).length, 0)} achievements
                    </span>
                    <span className="text-yellow-400">
                      {/* Total score from ALL achievements in baseGame structure */}
                      {(groupedAchievements?.baseGame?.achievements || []).reduce((sum, a) => sum + (a.score || 0), 0) +
                       (groupedAchievements?.baseGame?.sections || []).flatMap(s => s.achievements || [])
                         .reduce((sum, a) => sum + (a.score || 0), 0)} {getScoreAbbr(game?.platform)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-400"
                        style={{ 
                          width: `${(() => {
                            // Include ALL achievements in baseGame structure for progress
                            const baseAchs = groupedAchievements?.baseGame?.achievements || [];
                            const sectionAchs = (groupedAchievements?.baseGame?.sections || [])
                              .flatMap(s => s.achievements || []);
                            const allBaseAchs = [...baseAchs, ...sectionAchs];
                            const total = allBaseAchs.length;
                            const unlocked = allBaseAchs.filter(a => a.isUnlocked).length;
                            return total > 0 ? (unlocked / total) * 100 : 0;
                          })()}%` 
                        }}
                      />
                    </div>
                    <span className="text-xs text-white/40">
                      {(() => {
                        const baseAchs = groupedAchievements?.baseGame?.achievements || [];
                        const sectionAchs = (groupedAchievements?.baseGame?.sections || [])
                          .flatMap(s => s.achievements || []);
                        const allBaseAchs = [...baseAchs, ...sectionAchs];
                        const unlocked = allBaseAchs.filter(a => a.isUnlocked).length;
                        return `${unlocked}/${allBaseAchs.length}`;
                      })()}
                    </span>
                  </div>
                </div>
                <div className={`transform transition-transform mr-2 ${expandedSections.base ? 'rotate-180' : ''}`}>
                  <ChevronDown className="w-5 h-5 text-white/40" />
                </div>
              </button>
              
              {/* Section controls */}
              <div className="flex items-center gap-1 sm:gap-1 -mr-2 flex-wrap justify-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setBulkMoveMode(!bulkMoveMode);
                    setSelectedAchievements([]);
                  }}
                  className={`text-xs px-2 ${bulkMoveMode ? 'bg-blue-500/20 text-blue-400' : 'text-white/60 hover:text-white'}`}
                >
                  <Move className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Bulk</span>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSectionDialogOpen(true);
                  }}
                  className="text-xs text-cyan-400 hover:text-cyan-300 px-2"
                >
                  <FolderPlus className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Organizer</span>
                </Button>
              </div>
            </div>
            {expandedSections.base && (
              <div className="bg-black/20">
                {/* When Organizer View is OFF - show ALL base game achievements together, sorted */}
                {/* Exclude DLC achievements - they should show in their DLC sections */}
                {!showOrganizerView ? (
                  <div className="p-4 space-y-3">
                    {(() => {
                      // Base game (unsectioned only — achievements in
                      // organizers are hidden from the main list and appear
                      // in their organizer instead) + every DLC's
                      // unsectioned achievements packed into 100-per-page.
                      const baseUnsectioned = (groupedAchievements?.baseGame?.achievements || [])
                        .filter(a => !a.dlcId && !a.sectionId);
                      const sortedBase = sortAchievements(baseUnsectioned);
                      const dlcs = groupedAchievements?.dlcs || [];

                      const groups = [
                        { id: "base", label: "Base Game", items: filterByRarity(sortedBase) },
                        ...dlcs.map(d => ({
                          id: d.id,
                          label: d.name || "DLC",
                          iconUrl: d.boxArtUrl || d.imageUrl,
                          items: filterByRarity(sortAchievements(
                            (d.achievements || []).filter(a => a.isChallenge !== true && !a.sectionId)
                          )),
                        })),
                      ];
                      const totalFiltered = groups.reduce((s, g) => s + g.items.length, 0);
                      if (totalFiltered === 0) {
                        return (
                          <p className="text-white/50 text-center py-4 text-sm">
                            {rarityFilter !== "all" ? "No achievements match the rarity filter" : "No achievements"}
                          </p>
                        );
                      }
                      return (
                        <PackedGroupedList
                          groups={groups}
                          renderItem={(ach) => renderAchievement(ach, { showDlcTag: false })}
                          pageSize={100}
                          onGroupPageMap={setGroupPageMap}
                          jumpToGroupId={jumpToGroupId}
                          onJumpConsumed={() => setJumpToGroupId(null)}
                        />
                      );
                    })()}
                  </div>
                ) : (
                  <>
                    {/* Organizer View ON — Base (unsectioned) + DLCs unified
                        into 100-per-page packed pagination. Organizer sections
                        render separately below. */}
                    <div className="p-4 space-y-3">
                      {(() => {
                        const unsectionedBase = sortAchievements(
                          filterByRarity(
                            (groupedAchievements?.baseGame?.achievements || [])
                              .filter(a => !a.sectionId)
                          )
                        );
                        const dlcs = groupedAchievements?.dlcs || [];
                        const groups = [
                          { id: "base", label: "Base Game (Unsectioned)", items: unsectionedBase },
                          ...dlcs.map(d => ({
                            id: d.id,
                            label: d.name || "DLC",
                            iconUrl: d.boxArtUrl || d.imageUrl,
                            items: filterByRarity(sortAchievements(
                              (d.achievements || []).filter(a => a.isChallenge !== true && !a.sectionId)
                            )),
                          })),
                        ];
                        const totalFiltered = groups.reduce((s, g) => s + g.items.length, 0);
                        if (totalFiltered === 0) {
                          return (
                            <p className="text-white/50 text-center py-4 text-sm">
                              {rarityFilter !== "all" ? "No achievements match the rarity filter" : "No unsectioned base game achievements"}
                            </p>
                          );
                        }
                        return (
                          <PackedGroupedList
                            groups={groups}
                            renderItem={(ach) => renderAchievement(ach, { showDlcTag: false })}
                            pageSize={100}
                            onGroupPageMap={setGroupPageMap}
                            jumpToGroupId={jumpToGroupId}
                            onJumpConsumed={() => setJumpToGroupId(null)}
                          />
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* DLC Achievements — always hidden since base + DLCs render
              inline together via PackedGroupedList in both organizer view
              modes above. */}
          {false && (groupedAchievements?.dlcs || []).map((dlc) => {
            const dlcScore = (dlc.achievements || []).reduce((sum, a) => sum + (a.score || 0), 0);
            const dlcInfo = dlcs.find(d => d.id === dlc.id);
            const progressPct = dlc.totalAchievements > 0 ? (dlc.unlockedAchievements / dlc.totalAchievements) * 100 : 0;
            
            return (
              <div key={dlc.id} id={`section-${dlc.id}`} className="border border-purple-500/30 rounded-lg overflow-hidden scroll-mt-4">
                <button
                  onClick={() => toggleSection(dlc.id)}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-900/40 via-purple-800/30 to-pink-900/20 hover:from-purple-900/50 hover:via-purple-800/40 hover:to-pink-900/30 transition-all border-b border-purple-500/20"
                >
                  <div className="flex items-center gap-4">
                    {dlc.boxArtUrl || dlcInfo?.boxArtUrl ? (
                      <img 
                        src={dlc.boxArtUrl || dlcInfo?.boxArtUrl} 
                        alt={dlc.name}
                        className="w-14 h-14 rounded object-contain bg-black/50 hidden sm:block ring-2 ring-purple-500/30"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-14 h-14 rounded bg-gradient-to-br from-purple-500/30 to-pink-500/30 items-center justify-center hidden sm:flex ring-2 ring-purple-500/30">
                        <Package className="w-7 h-7 text-purple-400" />
                      </div>
                    )}
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-purple-400 sm:hidden" />
                        <span className="text-white font-medium">{dlc.name}</span>
                        {dlc.releaseDate && (
                          <span className="text-xs text-purple-300/70 hidden sm:inline">
                            ({new Date(dlc.releaseDate).toLocaleDateString()})
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-white/50 mt-1">
                        <span>{dlc.totalAchievements || 0} achievements</span>
                        <span className="text-yellow-400">{dlcScore} {getScoreAbbr(game?.platform)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="w-32 h-2 bg-black/30 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-purple-500 to-pink-400"
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                        <span className="text-xs text-white/40">
                          {dlc.unlockedAchievements || 0}/{dlc.totalAchievements || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={`transform transition-transform ${expandedSections[dlc.id] ? 'rotate-180' : ''}`}>
                    <ChevronDown className="w-5 h-5 text-purple-300/60" />
                  </div>
                </button>
                {expandedSections[dlc.id] && (
                  <div className="p-4 space-y-3 bg-black/40">
                    {(() => {
                      // When organizer view is ON, hide achievements that are in organizers (have sectionId)
                      // When organizer view is OFF, show ALL DLC achievements
                      const displayAchievements = showOrganizerView 
                        ? (dlc.achievements || []).filter(a => !a.sectionId && !a.isInOrganizer)
                        : (dlc.achievements || []);
                      const filtered = sortAchievements(filterByRarity(displayAchievements));
                      
                      if (filtered.length === 0) {
                        return (
                          <p className="text-white/50 text-center py-4 text-sm">
                            {rarityFilter !== "all" ? "No achievements match the rarity filter" : 
                              showOrganizerView ? "All achievements are in organizers" : "No achievements in this DLC"}
                          </p>
                        );
                      }
                      return <PaginatedList items={filtered} renderItem={renderAchievement} label="achievements" />;
                    })()}
                  </div>
                )}
              </div>
            );
          })}

          {/* Legacy Xbox Challenges - rendered as a separate amber section */}
          {(() => {
            const chal = groupedAchievements?.challenges?.achievements || [];
            if (chal.length === 0) return null;
            const total = chal.length;
            const unlocked = chal.filter(a => a.isUnlocked).length;
            const score = chal.reduce((sum, a) => sum + (a.score || 0), 0);
            const filtered = filterByRarity(sortAchievements(chal));
            const isExpanded = expandedSections.xboxChallenges !== false;
            return (
              <div id="section-xbox-challenges" className="border border-amber-500/30 rounded-lg overflow-hidden scroll-mt-4" data-testid="xbox-challenges-section">
                <button
                  onClick={() => toggleSection("xboxChallenges")}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-amber-900/30 via-amber-800/20 to-yellow-900/10 hover:from-amber-900/40 hover:via-amber-800/30 hover:to-yellow-900/20 transition-all border-b border-amber-500/20"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded bg-gradient-to-br from-amber-500/25 to-yellow-500/15 items-center justify-center hidden sm:flex ring-2 ring-amber-500/30">
                      <AlertTriangle className="w-6 h-6 text-amber-400" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        Legacy Xbox Challenges
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          Legacy
                        </span>
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-white/50 mt-0.5">
                        <span>{unlocked}/{total} earned</span>
                        <span className="text-yellow-400">{score} {getScoreAbbr(game?.platform)}</span>
                        <span className="text-white/30 italic hidden md:inline">discontinued by Xbox — 0 GS each</span>
                      </div>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-amber-400" /> : <ChevronDown className="w-5 h-5 text-amber-400" />}
                </button>
                {isExpanded && (
                  <div className="bg-black/20 p-4 space-y-3">
                    {filtered.length === 0 ? (
                      <p className="text-white/50 text-center py-4 text-sm">
                        {rarityFilter !== "all" ? "No challenges match the rarity filter" : "No challenges"}
                      </p>
                    ) : (
                      <PaginatedList items={filtered} renderItem={ach => renderAchievement(ach)} label="challenges" />
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Organizer Sections — paginated one-per-page when the game has
              multiple organizers. A chip nav at the top lets users jump to
              any organizer directly. Only the currently selected organizer
              renders below the chips. */}
          {showOrganizerView && (() => {
            const sortedSections = [...(groupedAchievements?.baseGame?.sections || [])]
              .sort((a, b) => naturalCompare(a.name, b.name));
            if (sortedSections.length === 0) return null;
            const currentIdx = Math.max(
              0,
              sortedSections.findIndex(s => s.id === activeOrganizerId)
            );
            const current = sortedSections[currentIdx];
            return (
              <>
                {sortedSections.length > 1 && (
                  <div className="flex flex-wrap items-center gap-1.5" data-testid="organizer-nav-chips">
                    <span className="text-[10px] uppercase tracking-wider text-white/40 pr-1">Organizers</span>
                    {sortedSections.map((s, i) => {
                      // Match the header count inside the tab — base-game
                      // achievements PLUS DLC achievements referencing this
                      // section via `sectionId`. Fixes the tab chip showing
                      // `· 0` for organizers whose achievements live on DLC.
                      const baseCount = (s.achievements || []).length;
                      const dlcCount = (groupedAchievements?.dlcs || []).reduce(
                        (n, dlc) => n + (dlc.achievements || []).filter(a => a.sectionId === s.id).length,
                        0
                      );
                      const total = baseCount + dlcCount;
                      return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setActiveOrganizerId(s.id)}
                        className={`px-2 py-1 rounded text-xs border transition-colors ${
                          s.id === current?.id
                            ? "bg-cyan-500/25 border-cyan-400/60 text-cyan-100 font-semibold"
                            : "border-white/10 text-white/70 hover:border-white/30 hover:bg-white/5"
                        }`}
                        title={`Organizer ${i + 1} of ${sortedSections.length}: ${s.name}`}
                        data-testid={`organizer-nav-chip-${s.id}`}
                      >
                        {s.name}
                        <span className="ml-1 text-white/40">
                          · {total}
                        </span>
                      </button>
                      );
                    })}
                  </div>
                )}
                {current && [current].map((section) => (
            <div key={section.id} id={`section-section-${section.id}`} className="border border-cyan-500/30 rounded-lg scroll-mt-4">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-cyan-900/40 via-teal-800/30 to-cyan-900/20 border-b border-cyan-500/20 rounded-t-lg">
                <button
                  onClick={() => toggleSection(`section-${section.id}`)}
                  className="flex-1 flex items-center gap-4"
                >
                  <div className="w-14 h-14 rounded bg-gradient-to-br from-cyan-500/30 to-teal-500/30 items-center justify-center hidden sm:flex ring-2 ring-cyan-500/30">
                    <Layers className="w-7 h-7 text-cyan-400" />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <Layers className="w-5 h-5 text-cyan-400 sm:hidden" />
                      <span className="text-white font-medium">{section.name}</span>
                      {(section.subsections || []).length > 0 && (
                        <span
                          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                          data-testid={`subgroup-count-${section.id}`}
                        >
                          {section.subsections.length} subgroup{section.subsections.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    {section.description && (
                      <p className="text-white/40 text-xs mt-0.5 line-clamp-2">{section.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-sm text-white/50 mt-1">
                      {(() => {
                        const baseAchs = section.achievements || [];
                        const dlcAchs = (groupedAchievements?.dlcs || []).flatMap(dlc => 
                          (dlc.achievements || []).filter(a => a.sectionId === section.id)
                        );
                        const allAchs = [...baseAchs, ...dlcAchs];
                        const unlocked = allAchs.filter(a => a.isUnlocked).length;
                        const sectionScore = allAchs.reduce((sum, a) => sum + (a.score || 0), 0);
                        return (
                          <>
                            <span>{allAchs.length} achievements</span>
                            <span className="text-yellow-400">{sectionScore} {getScoreAbbr(game?.platform)}</span>
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-32 h-2 bg-black/30 rounded-full overflow-hidden">
                        {(() => {
                          const baseAchs = section.achievements || [];
                          const dlcAchs = (groupedAchievements?.dlcs || []).flatMap(dlc => 
                            (dlc.achievements || []).filter(a => a.sectionId === section.id)
                          );
                          const allAchs = [...baseAchs, ...dlcAchs];
                          const total = allAchs.length;
                          const unlocked = allAchs.filter(a => a.isUnlocked).length;
                          const pct = total > 0 ? (unlocked / total) * 100 : 0;
                          return (
                            <div 
                              className="h-full bg-gradient-to-r from-cyan-500 to-teal-400"
                              style={{ width: `${pct}%` }}
                            />
                          );
                        })()}
                      </div>
                      <span className="text-xs text-white/40">
                        {(() => {
                          const baseAchs = section.achievements || [];
                          const dlcAchs = (groupedAchievements?.dlcs || []).flatMap(dlc => 
                            (dlc.achievements || []).filter(a => a.sectionId === section.id)
                          );
                          const allAchs = [...baseAchs, ...dlcAchs];
                          const unlocked = allAchs.filter(a => a.isUnlocked).length;
                          return `${unlocked}/${allAchs.length}`;
                        })()}
                      </span>
                    </div>
                  </div>
                  <div className={`transform transition-transform ${expandedSections[`section-${section.id}`] ? 'rotate-180' : ''}`}>
                    <ChevronDown className="w-5 h-5 text-cyan-300/60" />
                  </div>
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                      <MoreVertical className="w-4 h-4 text-white/40" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#0A0A0A] border-white/10">
                    <DropdownMenuItem 
                      onClick={() => {
                        setEditingSectionId(section.id);
                        setNewSectionName(section.name);
                        setNewSectionDescription(section.description || "");
                      }}
                      className="text-white/80 hover:bg-white/10"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setSubgroupManagerSection(section);
                        setSubgroupManagerOpen(true);
                      }}
                      className="text-white/80 hover:bg-white/10"
                      data-testid={`manage-subgroups-${section.id}`}
                    >
                      <Layers className="w-4 h-4 mr-2" />
                      Manage Subgroups
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setSubgroupSorterSection(section);
                        setSubgroupSorterOpen(true);
                      }}
                      className="text-white/80 hover:bg-white/10"
                      data-testid={`sort-subgroups-${section.id}`}
                    >
                      <Layers className="w-4 h-4 mr-2 text-cyan-400" />
                      Sort into Subgroups…
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDeleteSection(section.id)}
                      className="text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {expandedSections[`section-${section.id}`] && (
                <div className="p-4 space-y-3 bg-black/30">
                  {/* Section Map Notes */}
                  {(() => {
                    const sectionMaps = (game?.notes || []).filter(
                      n => n.noteType === "map" && n.sectionId === section.id
                    );
                    if (sectionMaps.length === 0) return null;
                    return (
                      <div className="space-y-2 mb-3" data-testid={`section-maps-${section.id}`}>
                        <p className="text-white/40 text-xs font-medium uppercase tracking-wider flex items-center gap-1.5">
                          <MapPin className="w-3 h-3 text-amber-400" /> Maps
                        </p>
                        {sectionMaps.map(note => (
                          <MapDisplay
                            key={note.id}
                            note={note}
                            onTogglePin={handleToggleMapPin}
                            onEdit={(n) => {
                              setEditingSectionMap(n);
                              setActiveSectionIdForMap(section.id);
                              setSectionMapEditorOpen(true);
                            }}
                            onDelete={handleDeleteNote}
                            onSubmitToCommunity={handleOpenSubmitMapDialog}
                          />
                        ))}
                      </div>
                    );
                  })()}

                  {/* Section Achievements — grouped by subsection when the
                      organizer has subgroups. Achievements without a subgroup
                      remain in an "Ungrouped" bucket; users can toggle whether
                      it sits above or below the subgroups (persisted in
                      localStorage per organizer). */}
                  {(() => {
                    // Combine base game section achievements with DLC achievements that have this sectionId
                    const sectionAchievements = [
                      ...(section.achievements || []),
                      ...(groupedAchievements?.dlcs || []).flatMap(dlc => 
                        (dlc.achievements || []).filter(a => a.sectionId === section.id)
                      )
                    ];
                    // Always apply natural sort then the user's chosen sort
                    // .trim() handles inconsistent leading/trailing whitespace in achievement names
                    const applySort = (arr) => [...arr].sort((a, b) => {
                      let cmp = 0;
                      const nameA = (a.name || "").trim();
                      const nameB = (b.name || "").trim();
                      if (sortBy === "score") {
                        cmp = (a.score || 0) - (b.score || 0);
                      } else if (sortBy === "isUnlocked") {
                        cmp = (a.isUnlocked ? 1 : 0) - (b.isUnlocked ? 1 : 0);
                      } else if (sortBy === "rarity") {
                        cmp = (a.rarity || 999) - (b.rarity || 999);
                      } else if (sortBy === "earnedAt") {
                        const teA = getEffectiveEarnedTime(a);
                        const teB = getEffectiveEarnedTime(b);
                        cmp = (teA !== null ? teA : 0) - (teB !== null ? teB : 0);
                      }
                      // Name natural sort as primary (when sortBy=name) or tiebreaker
                      if (cmp === 0 || sortBy === "name") {
                        cmp = nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
                      }
                      return sortOrder === "asc" ? cmp : -cmp;
                    });

                    const subs = (section.subsections || []);
                    const sortedFiltered = filterByRarity(applySort(sectionAchievements));

                    // Fast path: no subgroups — render as before.
                    if (subs.length === 0) {
                      if (sortedFiltered.length === 0 && (game?.notes || []).filter(n => n.noteType === "map" && n.sectionId === section.id).length === 0) {
                        return (
                          <p className="text-white/50 text-center py-4 text-sm">
                            No achievements in this organizer
                          </p>
                        );
                      }
                      return <PaginatedList items={sortedFiltered} renderItem={ach => renderAchievement(ach, { showDlcTag: true })} label="achievements" />;
                    }

                    // Subgroups exist — bucket achievements.
                    const buckets = new Map();
                    subs.forEach(sub => buckets.set(sub.id, []));
                    const ungrouped = [];
                    for (const ach of sortedFiltered) {
                      const sid = ach.subSectionId;
                      if (sid && buckets.has(sid)) buckets.get(sid).push(ach);
                      else ungrouped.push(ach);
                    }

                    // Toggleable ordering: user picks "ungrouped first" or "last"
                    const posKey = `organizerUngroupedPos:${section.id}`;
                    const ungroupedPos = (typeof window !== 'undefined' && localStorage.getItem(posKey)) || 'top';

                    const renderSubgroup = (sub) => {
                      const items = buckets.get(sub.id) || [];
                      const unlocked = items.filter(a => a.isUnlocked).length;
                      const Icon = getSubgroupIconComp(sub.icon);
                      const color = sub.color || '#06B6D4';
                      const collapseKey = `subgroupExpanded:${section.id}:${sub.id}`;
                      const isExpanded = expandedSections[collapseKey] !== false;
                      return (
                        <div
                          key={sub.id}
                          className="border border-white/10 rounded-md bg-black/20"
                          data-testid={`subgroup-block-${sub.id}`}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              const el = e.currentTarget;
                              const oldTop = el.getBoundingClientRect().top;
                              setExpandedSections(prev => ({
                                ...prev,
                                [collapseKey]: prev[collapseKey] === false ? true : false,
                              }));
                              // Anchor scroll so the header stays put in the
                              // viewport instead of jumping when the subgroup
                              // folds/unfolds.
                              requestAnimationFrame(() => {
                                const delta = el.getBoundingClientRect().top - oldTop;
                                if (delta !== 0) window.scrollBy(0, delta);
                              });
                            }}
                            className="w-full flex items-center justify-between px-3 py-2 border-b border-white/10 rounded-t-md sticky top-24 md:top-0 z-20 backdrop-blur hover:brightness-125 transition-all"
                            style={{ background: `linear-gradient(${color}22, ${color}22), #0A0A0A` }}
                            data-testid={`subgroup-toggle-${sub.id}`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 shrink-0 text-white/50" />
                              ) : (
                                <ChevronRight className="w-4 h-4 shrink-0 text-white/50" />
                              )}
                              <Icon className="w-4 h-4 shrink-0" style={{ color }} />
                              <span className="text-sm font-medium text-white truncate">{sub.name}</span>
                            </div>
                            <span className="text-xs text-white/50 shrink-0">
                              {unlocked}/{items.length}
                            </span>
                          </button>
                          {isExpanded && (
                            <div className="p-3">
                              {items.length === 0 ? (
                                <p className="text-white/40 text-center py-3 text-xs">
                                  No achievements in this subgroup yet
                                </p>
                              ) : (
                                <PaginatedList
                                  items={items}
                                  renderItem={ach => renderAchievement(ach, { showDlcTag: true })}
                                  label="achievements"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    };

                    const renderUngrouped = () => (
                      ungrouped.length > 0 ? (
                        <div
                          className="border border-dashed border-white/10 rounded-md overflow-hidden"
                          data-testid={`subgroup-ungrouped-${section.id}`}
                        >
                          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-white/5">
                            <span className="text-xs uppercase tracking-wider text-white/50">
                              Ungrouped
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  const next = ungroupedPos === 'top' ? 'bottom' : 'top';
                                  localStorage.setItem(posKey, next);
                                  // Force re-render (state ping)
                                  setExpandedSections(prev => ({ ...prev }));
                                }}
                                className="text-[10px] text-cyan-400/70 hover:text-cyan-300 transition-colors"
                                title="Toggle ungrouped position"
                                data-testid={`subgroup-toggle-pos-${section.id}`}
                              >
                                Show {ungroupedPos === 'top' ? 'below' : 'above'}
                              </button>
                              <span className="text-xs text-white/50">
                                {ungrouped.filter(a => a.isUnlocked).length}/{ungrouped.length}
                              </span>
                            </div>
                          </div>
                          <div className="p-3">
                            <PaginatedList
                              items={ungrouped}
                              renderItem={ach => renderAchievement(ach, { showDlcTag: true })}
                              label="achievements"
                            />
                          </div>
                        </div>
                      ) : null
                    );

                    return (
                      <div className="space-y-3">
                        {ungroupedPos === 'top' && renderUngrouped()}
                        {subs.map(renderSubgroup)}
                        {ungroupedPos !== 'top' && renderUngrouped()}
                      </div>
                    );
                  })()}

                  {/* Add Map to Section button */}
                  <div className="pt-2 border-t border-white/5">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingSectionMap(null);
                        setActiveSectionIdForMap(section.id);
                        setSectionMapEditorOpen(true);
                      }}
                      className="h-7 text-xs text-amber-400/60 hover:text-amber-400 hover:bg-amber-500/10"
                      data-testid={`add-section-map-${section.id}`}
                    >
                      <MapPin className="w-3 h-3 mr-1.5" />
                      Add Map
                    </Button>
                  </div>
                </div>
              )}
            </div>
                ))}
              </>
            );
          })()}

          {/* Custom Challenges Section */}
          <div id="section-challenges" className="border border-orange-500/30 rounded-lg overflow-hidden scroll-mt-4">
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-900/40 via-amber-800/30 to-yellow-900/20">
              <button
                onClick={() => toggleSection('challenges')}
                className="flex-1 flex items-center gap-4"
              >
                <div className="w-14 h-14 rounded bg-gradient-to-br from-orange-500/30 to-yellow-500/30 items-center justify-center hidden sm:flex ring-2 ring-orange-500/30">
                  <Target className="w-7 h-7 text-orange-400" />
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-orange-400 sm:hidden" />
                    <span className="text-white font-medium">Custom Challenges</span>
                    <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded">
                      {challenges.filter(c => !c.isCompleted).length} active
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-white/50 mt-1">
                    <span>{challenges.length} challenge(s)</span>
                    <span className="text-green-400">{challenges.filter(c => c.isCompleted).length} completed</span>
                  </div>
                </div>
                <div className={`transform transition-transform ${expandedSections.challenges ? 'rotate-180' : ''}`}>
                  <ChevronDown className="w-5 h-5 text-orange-300/60" />
                </div>
              </button>
              <Button
                size="sm"
                onClick={() => setChallengeDialogOpen(true)}
                className="ml-2 bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
            {expandedSections.challenges && (
              <div className="p-4 space-y-3 bg-black/40">
                {!challenges || challenges.length === 0 ? (
                  <div className="text-center py-8">
                    <Target className="w-12 h-12 text-orange-400/30 mx-auto mb-3" />
                    <p className="text-white/50 text-sm">No custom challenges yet</p>
                    <p className="text-white/30 text-xs mt-1">Create personal goals and track your progress</p>
                  </div>
                ) : (
                  [...(challenges || [])].sort((a, b) => {
                    // Sort challenges using the same sorting method as achievements
                    let comparison = 0;
                    
                    switch (sortBy) {
                      case "name":
                        comparison = naturalCompare(a.name, b.name);
                        break;
                      case "score":
                        // Challenges don't have scores, sort by name as fallback
                        comparison = naturalCompare(a.name, b.name);
                        break;
                      case "earnedAt":
                        // Map to completedAt for challenges
                        const dateA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
                        const dateB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
                        comparison = dateA - dateB;
                        break;
                      case "rarity":
                        // Challenges don't have rarity, sort by name as fallback
                        comparison = naturalCompare(a.name, b.name);
                        break;
                      case "createdAt":
                      default:
                        const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                        const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                        comparison = createdA - createdB;
                        break;
                      case "isUnlocked":
                        // Map to isCompleted for challenges
                        comparison = (a.isCompleted === b.isCompleted) ? 0 : a.isCompleted ? 1 : -1;
                        break;
                    }
                    
                    return sortOrder === "asc" ? comparison : -comparison;
                  }).map((challenge) => (
                    <div 
                      key={challenge.id}
                      className={`p-4 rounded-lg border ${
                        challenge.isCompleted 
                          ? 'bg-green-900/20 border-green-500/30' 
                          : 'bg-[#1A1A1A] border-white/10'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => handleToggleChallenge(challenge.id)}
                          className={`mt-1 flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            challenge.isCompleted
                              ? 'bg-green-500 border-green-500'
                              : challenge.prerequisitesMet
                                ? 'border-orange-500/50 hover:border-orange-400'
                                : 'border-gray-500/30 cursor-not-allowed'
                          }`}
                          disabled={!challenge.prerequisitesMet && !challenge.isCompleted}
                          title={!challenge.prerequisitesMet ? 'Prerequisites not met' : ''}
                        >
                          {challenge.isCompleted && <Check className="w-4 h-4 text-white" />}
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className={`font-medium ${challenge.isCompleted ? 'text-green-400 line-through' : 'text-white'}`}>
                              {challenge.name}
                            </h4>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                                  <MoreVertical className="w-4 h-4 text-white/40" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-[#0A0A0A] border-white/10">
                                <DropdownMenuItem 
                                  onClick={() => setEditingChallenge({...challenge})}
                                  className="text-white/80 hover:bg-white/10"
                                >
                                  <Edit2 className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteChallenge(challenge.id)}
                                  className="text-red-400 hover:bg-red-500/10"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          {challenge.description && (
                            <p className="text-sm text-white/50 mt-1">{challenge.description}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                            {challenge.timerSeconds && (
                              <span className="flex items-center gap-1 text-blue-400">
                                <Timer className="w-3 h-3" />
                                {Math.floor(challenge.timerSeconds / 3600)}h {Math.floor((challenge.timerSeconds % 3600) / 60)}m
                              </span>
                            )}
                            {challenge.dueDate && (
                              <span className="flex items-center gap-1 text-purple-400">
                                <Calendar className="w-3 h-3" />
                                {format(new Date(challenge.dueDate), 'MMM d, yyyy')}
                              </span>
                            )}
                            {challenge.prerequisiteAchievementIds?.length > 0 && (
                              <span className={`flex items-center gap-1 ${challenge.prerequisitesMet ? 'text-green-400' : 'text-yellow-400'}`}>
                                <Trophy className="w-3 h-3" />
                                {challenge.prerequisitesMet ? 'Prerequisites met' : `${challenge.prerequisiteAchievementIds.length} prerequisite(s)`}
                              </span>
                            )}
                            {challenge.isCompleted && challenge.completedAt && (
                              <span className="text-green-400">
                                Completed {format(new Date(challenge.completedAt), 'MMM d, yyyy')}
                              </span>
                            )}
                          </div>
                        </div>
                        {challenge.imageUrl && (
                          <img 
                            src={challenge.imageUrl} 
                            alt={challenge.name}
                            className="w-12 h-12 rounded object-contain bg-black/50"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>


          {/* Trivia Section */}
          {showAchievementFacts && Object.keys(factsByAchievement).length > 0 && (
            <div id="section-trivia" className="border border-purple-500/30 rounded-lg overflow-hidden scroll-mt-4">
              <button
                onClick={() => toggleSection('trivia')}
                className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-900/40 via-indigo-800/30 to-purple-900/20 hover:from-purple-900/50 hover:via-indigo-800/40 hover:to-purple-900/30 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded bg-gradient-to-br from-purple-500/30 to-indigo-500/30 items-center justify-center hidden sm:flex ring-2 ring-purple-500/30">
                    <Lightbulb className="w-7 h-7 text-purple-400" />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-purple-400 sm:hidden" />
                      <span className="text-white font-medium">Achievement Trivia</span>
                      <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">
                        {Object.values(factsByAchievement).reduce((sum, arr) => sum + arr.length, 0)} facts
                      </span>
                    </div>
                    <p className="text-sm text-white/50 mt-1">
                      Community-contributed facts and trivia
                    </p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-purple-300/60 transition-transform ${expandedSections.trivia ? 'rotate-180' : ''}`} />
              </button>
              {expandedSections.trivia && (
                <div className="p-4 space-y-3 bg-black/40 max-h-[600px] overflow-y-auto">
                  {Object.entries(factsByAchievement).map(([achId, facts]) => (
                    <div key={achId} className="space-y-2">
                      {facts.map(fact => (
                        <div key={fact.id} className="p-3 bg-[#121212] rounded-lg border border-purple-500/10">
                          <div className="flex items-start gap-3">
                            {fact.achievementImageUrl && (
                              <img src={fact.achievementImageUrl} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-white font-medium text-sm">{fact.achievementName}</span>
                                {fact.category && getCategoryInfo && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">
                                    {getCategoryInfo(fact.category)?.label || fact.category}
                                  </span>
                                )}
                              </div>
                              <FormattedText text={fact.factText} className="text-white/70 text-sm leading-relaxed" />
                              {fact.submittedByUsername && fact.showSubmitter && (
                                <p className="text-white/30 text-xs mt-1">by {fact.submittedByUsername}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {(!achievements || achievements.length === 0) && (
            <p className="text-white/50 text-center py-8">
              No achievements yet. Add your first one!
            </p>
          )}
        </CardContent>
      </Card>

      {/* Edit Achievement Dialog */}
      <EditAchievementDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        achievement={editingAchievement}
        setAchievement={setEditingAchievement}
        dlcs={dlcs}
        sections={sections}
        tags={tags}
        platform={game?.platform}
        onSave={handleUpdateAchievement}
        toggleTag={toggleTag}
      />

      {/* Timestamp Dialog */}
      <TimestampDialog
        open={timestampDialogOpen}
        onOpenChange={setTimestampDialogOpen}
        achievement={editingAchievement}
        setAchievement={setEditingAchievement}
        onSave={handleUpdateTimestamp}
        gameReleaseDate={game?.releaseDate}
      />

      {/* Game Info Dialog */}
      <GameInfoDialog
        open={gameInfoDialogOpen}
        onOpenChange={setGameInfoDialogOpen}
        game={game}
        setGame={setGame}
        platforms={platforms}
        systems={systems}
        genreInput={genreInput}
        setGenreInput={setGenreInput}
        addGenre={addGenre}
        removeGenre={removeGenre}
        onSave={handleUpdateGameInfo}
        onToggleNoAchievements={handleToggleNoAchievements}
      />

      {/* Add Note Dialog */}
      <AddNoteDialog
        open={notesDialogOpen}
        onOpenChange={setNotesDialogOpen}
        noteText={newNoteText}
        setNoteText={setNewNoteText}
        onSave={handleAddNote}
        checklistTemplates={checklistTemplates}
        onSaveChecklist={handleAddChecklist}
        characters={(game?.notes || []).filter(n => n.noteType === "character")}
        achievements={achievements}
      />

      {/* Share Checklist Dialog */}
      <ShareChecklistDialog
        open={shareChecklistDialogOpen}
        onOpenChange={setShareChecklistDialogOpen}
        checklist={checklistToShare}
        onShare={handleShareChecklist}
      />

      {/* Refresh from Database Dialog */}
      <RefreshDialog
        open={refreshDialogOpen}
        onOpenChange={setRefreshDialogOpen}
        refreshSource={refreshSource}
        setRefreshSource={setRefreshSource}
        refreshSearch={refreshSearch}
        setRefreshSearch={setRefreshSearch}
        refreshResults={refreshResults}
        setRefreshResults={setRefreshResults}
        selectedRefreshGame={selectedRefreshGame}
        setSelectedRefreshGame={setSelectedRefreshGame}
        refreshFields={refreshFields}
        toggleRefreshField={toggleRefreshField}
        refreshLoading={refreshLoading}
        onSearch={handleRefreshSearch}
        onSelectGame={handleSelectRefreshGame}
        onRefresh={handleRefreshGame}
        masterDbSearched={masterDbSearched}
        setMasterDbSearched={setMasterDbSearched}
      />
      
      {/* Unified Cover Art Selection Dialog (IGDB + SteamGridDB) */}
      <CoverArtDialog
        open={coverDialogOpen}
        onOpenChange={setCoverDialogOpen}
        game={game}
        searchQuery={coverSearchQuery}
        onSearchQueryChange={setCoverSearchQuery}
        onSearch={searchCovers}
        loading={coverLoading}
        results={coverResults}
        sourceFilter={coverSourceFilter}
        onSourceFilterChange={setCoverSourceFilter}
        getFilteredResults={getFilteredCoverResults}
        selectedCover={selectedCover}
        onSelectCover={setSelectedCover}
        onApply={applyCover}
        uploading={coverUploading}
      />
      
      {/* Subgroup Manager Dialog (nested subgroups inside an organizer) */}
      <SubgroupManagerDialog
        open={subgroupManagerOpen}
        onOpenChange={(v) => {
          setSubgroupManagerOpen(v);
          if (!v) setSubgroupManagerSection(null);
        }}
        gameId={gameId}
        section={subgroupManagerSection}
        api={API}
        onChanged={fetchData}
      />

      {/* Subgroup Sorter Dialog (3-panel drag-drop workspace).
          We intentionally do NOT pass `onChanged={fetchData}` — the sorter
          keeps its own optimistic state during rapid drags. Refetching on
          every drop hammered the API on 1700+ achievement games and caused
          partial failures that wiped DLCs/organizers. We refetch once when
          the dialog closes to reconcile the final state. */}
      <OrganizerSubgroupSorterDialog
        open={subgroupSorterOpen}
        onOpenChange={(v) => {
          setSubgroupSorterOpen(v);
          if (!v) {
            setSubgroupSorterSection(null);
            fetchData();
          }
        }}
        gameId={gameId}
        section={subgroupSorterSection}
        api={API}
      />

      {/* Homecoming Chatlog importer — shown when the master game has
          `chatlogImportEnabled = true`. Replaces the standard sync flow. */}
      <ChatlogImportDialog
        open={chatlogImportOpen}
        onOpenChange={setChatlogImportOpen}
        gameId={gameId}
        api={API}
        onImported={fetchData}
        characters={(game?.notes || []).filter((n) => n.noteType === "character")}
      />

      {/* Organizer Dialog */}
      <SectionDialog
        open={sectionDialogOpen || editingSectionId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSectionDialogOpen(false);
            setEditingSectionId(null);
            setNewSectionName("");
            setNewSectionDescription("");
          }
        }}
        isEditing={!!editingSectionId}
        sectionName={newSectionName}
        onSectionNameChange={setNewSectionName}
        sectionDescription={newSectionDescription}
        onSectionDescriptionChange={setNewSectionDescription}
        onSave={() => {
          if (editingSectionId) {
            handleUpdateSection(editingSectionId, newSectionName, newSectionDescription);
          } else {
            handleCreateSection();
          }
        }}
      />

      {/* Challenge Dialog */}
      <ChallengeDialog
        open={challengeDialogOpen || editingChallenge !== null}
        onOpenChange={(open) => {
          if (!open) {
            setChallengeDialogOpen(false);
            setEditingChallenge(null);
            setNewChallenge({
              name: "",
              description: "",
              imageUrl: "",
              timerSeconds: null,
              dueDate: null,
              prerequisiteAchievementIds: []
            });
          }
        }}
        challenge={editingChallenge || newChallenge}
        onChallengeChange={(updated) => {
          if (editingChallenge) {
            setEditingChallenge(updated);
          } else {
            setNewChallenge(updated);
          }
        }}
        isEditing={!!editingChallenge}
        achievements={achievements}
        onSave={() => {
          if (editingChallenge) {
            handleUpdateChallenge();
          } else {
            handleCreateChallenge();
          }
        }}
      />

      {/* Bulk Move Dialog */}
      <BulkMoveDialog
        open={bulkMoveDialogOpen}
        onOpenChange={setBulkMoveDialogOpen}
        selectedCount={selectedAchievements.length}
        sections={sections}
        dlcs={dlcs}
        onMove={handleBulkMove}
      />
      
      {/* Xbox Title Search/Link Dialog */}
      <XboxTitleSearchDialog
        open={xboxTitleSearchOpen}
        onOpenChange={setXboxTitleSearchOpen}
        searchQuery={xboxTitleSearch}
        onSearchQueryChange={setXboxTitleSearch}
        onSearch={handleSearchXboxTitles}
        searching={xboxTitleSearching}
        results={xboxTitleResults}
        onSelect={(game) => handleLinkXboxTitle(game.titleId)}
        linking={xboxTitleLinking}
      />

      {/* PlayStation Title Search/Link Dialog */}
      <PsnTitleSearchDialog
        open={psnTitleSearchOpen}
        onOpenChange={setPsnTitleSearchOpen}
        searchQuery={psnTitleSearch}
        onSearchQueryChange={setPsnTitleSearch}
        onSearch={handleSearchPsnTitles}
        searching={psnTitleSearching}
        results={psnTitleResults}
        onSelect={(game) => handleLinkPsnTitle(game.npCommunicationId)}
        linking={psnTitleLinking}
      />

      {/* Manual Bulk Sync Dialog */}
      <ManualSyncDialog
        open={manualSyncDialogOpen}
        onOpenChange={setManualSyncDialogOpen}
        achievements={manualSyncAchievements}
        selections={manualSyncSelections}
        onSelectionsChange={setManualSyncSelections}
        onSelectAll={handleSelectAllManualSync}
        onDeselectAll={handleDeselectAllManualSync}
        onToggleAchievement={handleToggleManualSyncAchievement}
        onSave={handleSaveManualSync}
        saving={savingManualSync}
      />

      {/* Score Mismatch Dialog */}
      <ScoreMismatchDialog
        open={scoreMismatchDialogOpen}
        onOpenChange={setScoreMismatchDialogOpen}
        scoreDifference={scoreMismatchData?.scoreDifference || 0}
        countDifference={scoreMismatchData?.countDifference || 0}
        platformLabel={scoreMismatchData?.platformLabel || "Xbox"}
        scoreUnit={scoreMismatchData?.scoreUnit || "GS"}
        missingAchievements={scoreMismatchData?.missingAchievements || []}
        selections={manualUnlockSelections}
        onSelectionsChange={setManualUnlockSelections}
        onSelectAll={() => {
          const allSelected = {};
          (scoreMismatchData?.missingAchievements || []).forEach(ach => {
            allSelected[ach.id] = { selected: true };
          });
          setManualUnlockSelections(allSelected);
        }}
        onDeselectAll={() => setManualUnlockSelections({})}
        onSave={handleSaveManualUnlocks}
        saving={savingManualUnlocks}
      />
      
      {/* Submit Fact Dialog */}
      <SubmitFactDialog
        open={submitFactDialogOpen}
        onOpenChange={setSubmitFactDialogOpen}
        achievementId={submitFactAchievement?.id}
        achievementName={submitFactAchievement?.name || ""}
        gameName={game?.name || ""}
        onSubmit={handleSubmitFact}
        isSubmitting={submittingFact}
      />

      {/* Share to Community Dialog */}
      <ShareToCommunityDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        game={game}
        groupedAchievements={groupedAchievements}
        challenges={challenges}
        onShare={handleShareToCommunity}
        shareSubmitting={shareSubmitting}
      />

      {/* Browse Community Content Dialog */}
      <BrowseCommunityDialog
        open={communityDialogOpen}
        onOpenChange={setCommunityDialogOpen}
        game={game}
        communityOrganizers={communityOrganizers}
        communityChallenges={communityChallenges}
        communityGamePlanners={communityGamePlanners}
        communityLoading={communityLoading}
        adoptingId={adoptingId}
        onAdoptOrganizer={handleAdoptOrganizer}
        onAdoptChallenge={handleAdoptChallenge}
        onAdoptGamePlanner={handleAdoptGamePlanner}
        communityChecklists={communityChecklists}
        communityGrids={communityGrids}
        communityGridTemplates={communityGridTemplates}
        onCopyChecklist={handleCopyChecklist}
        onAdoptGrid={handleAdoptGrid}
        onAdoptGridTemplate={handleAdoptGridTemplate}
        communityMaps={communityMaps}
        onAdoptMap={handleAdoptMap}
        onLikeMap={handleLikeMap}
      />

      {/* Exophase Verify Dialog */}
      <ExophaseVerifyDialog
        open={exophaseVerifyOpen}
        onOpenChange={setExophaseVerifyOpen}
        game={game}
        onVerified={() => fetchData()}
      />

      {/* Section-level Map Editor Modal */}
      <CharacterEditor
        open={characterEditorOpen}
        onOpenChange={(v) => { if (!v) { setCharacterEditorOpen(false); setEditingCharacter(null); } }}
        initial={editingCharacter}
        onSave={handleSaveCharacter}
        onCancel={() => { setCharacterEditorOpen(false); setEditingCharacter(null); }}
        masterGameId={game?.masterGameId}
        availableNotes={(game?.notes || []).filter((n) => (n.noteType || "note") === "note")}
      />

      {sectionMapEditorOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onMouseDown={(e) => { if (e.target === e.currentTarget) { setSectionMapEditorOpen(false); setEditingSectionMap(null); setActiveSectionIdForMap(null); } }}
          data-testid="section-map-editor-modal"
        >
          <div
            className="bg-[#0A0A0F] border border-white/10 rounded-xl p-4 w-full max-w-2xl max-h-[85vh] overflow-y-auto mx-4"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 className="text-white font-medium text-sm mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-amber-400" />
              {editingSectionMap ? "Edit Map" : "Add Map to Section"}
            </h3>
            <MapEditor
              initialData={editingSectionMap}
              API={`${API}`}
              gameId={gameId}
              onSave={(mapData) => {
                if (editingSectionMap) {
                  handleEditSectionMap(mapData);
                } else {
                  handleAddSectionMap(mapData);
                }
              }}
              onCancel={() => { setSectionMapEditorOpen(false); setEditingSectionMap(null); setActiveSectionIdForMap(null); }}
            />
          </div>
        </div>
      )}

      {/* Submit Map to Community Dialog */}
      {submitMapDialogOpen && mapToSubmit && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onMouseDown={(e) => { if (e.target === e.currentTarget) { setSubmitMapDialogOpen(false); setMapToSubmit(null); } }}
          data-testid="submit-map-community-modal"
        >
          <div
            className="bg-[#0A0A0F] border border-white/10 rounded-xl p-5 w-full max-w-md mx-4"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 className="text-white font-medium text-base mb-4 flex items-center gap-2">
              <Share2 className="w-4 h-4 text-cyan-400" />
              Submit Map to Community
            </h3>

            {/* Preview */}
            <div className="mb-4 rounded-lg overflow-hidden border border-white/10">
              <img
                src={mapToSubmit.mapImageUrl}
                alt="Map preview"
                className="w-full h-32 object-cover"
              />
              <div className="p-2 bg-white/5">
                <p className="text-white/80 text-sm font-medium">{mapToSubmit.title || "Untitled Map"}</p>
                <p className="text-white/40 text-xs">{(mapToSubmit.pins || []).length} pins</p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-3">
              <label className="text-white/50 text-xs block mb-1">Description (optional)</label>
              <textarea
                value={submitMapDescription}
                onChange={(e) => setSubmitMapDescription(e.target.value)}
                placeholder="Describe this map..."
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none h-16 focus:outline-none focus:border-amber-500/50"
                data-testid="submit-map-description"
              />
            </div>

            {/* Tags */}
            <div className="mb-3">
              <label className="text-white/50 text-xs block mb-1">Tags</label>
              <Input
                placeholder="Type a tag and press Enter"
                className="bg-white/5 border-white/10 text-white text-sm h-8"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.target.value.trim()) {
                    e.preventDefault();
                    const tag = e.target.value.trim();
                    if (!submitMapTags.includes(tag)) {
                      setSubmitMapTags([...submitMapTags, tag]);
                    }
                    e.target.value = "";
                  }
                }}
                data-testid="submit-map-tag-input"
              />
              {submitMapTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {submitMapTags.map(tag => (
                    <span
                      key={tag}
                      className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full cursor-pointer hover:bg-red-500/20 hover:text-red-300 transition-colors"
                      onClick={() => setSubmitMapTags(submitMapTags.filter(t => t !== tag))}
                    >
                      {tag} &times;
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Show submitter toggle */}
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={submitMapShowSubmitter}
                onChange={(e) => setSubmitMapShowSubmitter(e.target.checked)}
                className="rounded"
                data-testid="submit-map-show-submitter"
              />
              <span className="text-white/60 text-xs">Show my name as contributor</span>
            </label>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSubmitMapDialogOpen(false); setMapToSubmit(null); }}
                className="text-white/60 text-xs"
                data-testid="submit-map-cancel"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmitMapToCommunity}
                disabled={submittingMap}
                className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs"
                data-testid="submit-map-confirm"
              >
                {submittingMap ? "Submitting..." : "Submit for Review"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

